import {api} from '../../api.js';
import {LITIGATION_CREATOR_UNIT_ID, canEditLitigationCaseForAccess, canDeleteLitigationCaseForAccess} from '../../../common/litigationCollaborationAccess.js';
import {mergeLitigationCaseResponse, remainingLitigationPatch} from '../../../common/litigationCaseSync.js';
import type {AuthSnapshot} from '../../auth.js';
import {apiUrl} from '../../config.js';
import {fileIconUrl} from '../../core/fileTypeIcon.js';
import {isLegalFeatureEnabled} from '../../core/moduleAccess.js';
import {escapeHtml, renderLayout} from '../../layout.js';
import {navigate} from '../../router.js';
import {openLitigationWorkbookCreation} from './litigationWorkbookCreation.js';
import {
  alignLitigationCourtNumberRows,
  alignLitigationVenueRows,
  currentStageName,
  deadlineTone,
  DISPUTE_TYPE_LABELS,
  INSTANCE_LABELS,
  LITIGATION_COURT_NUMBER_LAYERS,
  litigationCourtNumberLayersForDispute,
  litigationVenueLayerUsesCourtDirectory,
  litigationVenueLayersForDispute,
  litigationInstanceFromCourtRows,
  LITIGATION_VENUE_LAYERS,
  litigationCourtNumberSummary,
  litigationVenueSummary,
  isLitigationPartyLayer,
  litigationPartyLayersForCase,
  partyLayerLabel,
  partyLayerRows,
  writePartyLayerName,
  MATERIAL_CATEGORY_LABELS,
  nextDeadlineDate,
  ourPartyName,
  PARTY_ROLE_LABELS,
  POSITION_LABELS,
  primaryOpponentName,
  sortedLitigationProgress,
  normalizeLitigationCourtNumberRows,
  normalizeLitigationVenueRows,
  resolveLitigationVenueRows,
  type LitigationCase,
  type LitigationCourtNumberLayer,
  type LitigationCourtNumberRow,
  type LitigationVenueLayer,
  type LitigationVenueRow,
  type LitigationDeadlineTone,
  type LitigationLedgerAccess,
  type LitigationLedgerStats,
  type LitigationLinkedContract,
  type LitigationMaterialCategory,
  type LitigationParty,
  type LitigationPartyLayer,
  type LitigationPartyRole,
} from '../../../common/litigationLedger.js';
import {CLOSE_MODE_LABELS, ENFORCEMENT_STATUS_LABELS} from '../../../common/litigationLedgerRules.js';
import {deriveLitigationNumberingKind, formatLitigationAwardedAmountCell, formatLitigationClaimCell, formatLitigationInvolvedAmountCell, formatLitigationResultCell, LITIGATION_INTERNAL_NO_PART_LABELS, LITIGATION_NUMBERING_KIND_LABELS, LITIGATION_ORGANIZATION_LABEL_KIND_LABELS, litigationCloseModeChoices, litigationCloseModeOptions, litigationFieldFillGuide, litigationFieldRuleKindInfo, litigationAwardedAmountLabels, litigationInvolvedAmountLabels, litigationJudgmentResultLabels, litigationPlanFieldLabel, litigationPlanFieldValue, litigationShownInstance, litigationTrialStageOptions, normalizeLitigationInternalNoRule, parseLitigationMoney, parseLitigationOrganizationLabelKind, previewLitigationInternalNo, resolveLitigationCourtNumberRows, resolveLitigationFieldRule, prepareLitigationInternalNo, type LitigationFieldRuleOverrides, type LitigationNumberingKind, type LitigationOrganizationLabelKind, type LitigationPlanCollaborationUnit, type LitigationPlanTeamEditor} from '../../../common/litigationLedgerPlan.js';
import {LITIGATION_DEFAULT_SHEETS, type LitigationSheetKind} from '../../../common/litigationLedgerSheets.js';
import {LITIGATION_EDIT_PATCH_KEYS, litigationCaseShowsInternalNo, litigationFieldChoices, litigationFieldEditPatch, litigationEditableCellValue} from '../../../common/litigationFieldEditing.js';

type LedgerCase = LitigationCase & {currentStage?: string};
type LinkableContract = LitigationLinkedContract;
type DetailTab = 'overview' | 'progress' | 'procedure' | 'deadlines' | 'materials' | 'contracts';
type EligibleMember = {userId: string; displayName: string};
const DEADLINE_KIND_LABELS: Record<string, string> = {
  custom: '自定义',
  evidence: '举证',
  hearing: '开庭',
  appeal: '上诉',
  enforcement: '执行',
};

/** 案件总表列默认宽度（像素）。 */
const LL_CASE_COL_WIDTH_DEFAULT: Record<string, number> = {
  insert: 32,
  select: 32,
  index: 48,
  remark: 72,
  internalNo: 108,
  title: 176,
  caseNature: 88,
  disputeType: 72,
  opponent: 112,
  venue: 360,
  amount: 88,
  involvedAmount: 240,
  resultSummary: 280,
  awardedAmount: 420,
  currentStage: 128,
  latestProgress: 128,
  nextDeadline: 88,
  courtNo: 360,
  relatedCourtNo: 220,
  filingDate: 96,
  handler: 80,
  counsel: 80,
  organizationUnit: 160,
};

/** 列表中点击后内联手填、失焦保存的字段。 */
const LL_LIST_INPLACE_TEXT_FIELDS = new Set([
  'caseName',
  'courtNo',
  'venue',
  'handler',
  'counsel',
  'claimSummary',
  'resultSummary',
  'filingDate',
  'amount',
]);

type ListColumnField = {id: string; label: string; sourceField?: string};

/**
 * 列表列在 Excel/案件对象里读写的字段（映射 sourceField）。
 * @param field 方案列
 */
function resolveListColumnStorageField(field: ListColumnField): string {
  const raw = field.sourceField ?? field.id;
  const label = field.label.replace(/\s+/g, '').replace(/／/g, '/');
  const plaintiffLabels = new Set(['原告', '申请人', '原告/执行申请人', '原告/申请人', '执行申请人', '原告/上诉人/申请人']);
  const thirdLabels = new Set(['第三人', '有独立请求权第三人', '无独立请求权第三人']);
  if (label === '原告/上诉人/申请人') return 'plaintiffParty';
  if (label === '被告/被上诉人/被申请人') return 'opponent';
  if (plaintiffLabels.has(label) && field.id !== 'ourParty' && (field.sourceField === 'ourParty' || field.sourceField === '')) return 'plaintiffParty';
  if (thirdLabels.has(label) && field.id !== 'opponent' && (field.sourceField === 'opponent' || field.sourceField === '')) return 'thirdParty';
  if ((label === '诉讼与执行关联案号' || label === '关联案号') && (field.sourceField === 'courtNo' || field.sourceField === '')) return 'relatedCourtNo';
  if (label === '案件性质') return 'caseNature';
  if (label === '所属单位') return 'organizationUnit';
  if (label === '申请立案日' || label === '申请立案日期') return 'filingDate';
  if (label === '诉讼请求/申请执行标的' || label === '申请执行标的') return 'claimSummary';
  if (raw === 'closedFlag' || label === '是否结案' || label === '是否已结案' || label === '结案状态' || label === '案涉金额') return 'involvedAmount';
  if (raw === 'resultSummary' || label === '判决/裁决结果' || label === '判决支持标的金额/执行到位金额' || label === '判决支持的金额/执行到位金额' || label === '判决支持标的金额' || label === '执行到位金额' || label === '结果摘要' || label === '裁判结果' || label === '裁决结果') return 'resultSummary';
  if (raw === 'awardedAmount' || raw === 'nonMonetaryResult' || label === '判决（裁决）支持的金额/执行到位的金额' || label === '判决（裁决）支持的金额' || label === '执行到位的金额' || label === '非货币判决结果/非货币执行结果' || label === '非货币判决结果' || label === '非货币执行结果' || label === '非货币结果' || label === '非金钱判决结果') return 'awardedAmount';
  if (raw.startsWith('template_')) return field.sourceField ?? '';
  return raw;
}

/**
 * 把仍标着「是否结案」的列显示为案涉金额。
 * @param field 方案列
 */
/**
 * 把仍标着旧表头的列显示为判决/裁决结果。
 * @param field 方案列
 */
const RESULT_LEGACY_LABELS = new Set(['判决支持标的金额/执行到位金额', '判决支持的金额/执行到位金额', '判决支持标的金额', '执行到位金额', '结果摘要', '裁判结果', '裁决结果']);
const AWARDED_LEGACY_LABELS = new Set(['非货币判决结果', '非货币执行结果', '非货币判决结果/非货币执行结果', '非货币结果', '非金钱判决结果']);
const INVOLVED_LEGACY_LABELS = new Set(['是否结案', '是否已结案', '结案状态']);

function presentResultColumn(field: ListColumnField): ListColumnField {
  if (resolveListColumnStorageField(field) !== 'resultSummary') return field;
  const label = field.label.trim();
  if (label && !RESULT_LEGACY_LABELS.has(label)) return {...field, sourceField: 'resultSummary'};
  return {...field, sourceField: 'resultSummary', label: '判决/裁决结果'};
}

/**
 * 把仍标着「非货币判决结果」的列显示为判决（裁决）支持的金额。
 * @param field 方案列
 */
function presentAwardedAmountColumn(field: ListColumnField): ListColumnField {
  if (resolveListColumnStorageField(field) !== 'awardedAmount') return field;
  const label = field.label.trim();
  if (label && !AWARDED_LEGACY_LABELS.has(label)) return {...field, sourceField: 'awardedAmount'};
  return {...field, sourceField: 'awardedAmount', label: '判决（裁决）支持的金额/执行到位的金额'};
}

function presentInvolvedAmountColumn(field: ListColumnField): ListColumnField {
  if (resolveListColumnStorageField(field) !== 'involvedAmount' && field.id !== 'closedFlag') return field;
  const label = field.label.trim();
  const nextLabel = !label || INVOLVED_LEGACY_LABELS.has(label) ? '案涉金额' : label;
  return {
    ...field,
    id: field.id === 'closedFlag' ? 'involvedAmount' : field.id,
    sourceField: 'involvedAmount',
    label: nextLabel,
  };
}

/**
 * 从案涉金额编辑区读出本诉金额和反诉金额。格式不对时返回 null。
 * @param host 表单或金额容器
 */
function readInvolvedAmountPatch(host: ParentNode): {principalAmount: number; counterclaimAmount: number} | null {
  const layers = host instanceof Element && host.classList.contains('ll-amount-layers')
    ? host
    : host.querySelector<HTMLElement>('.ll-amount-layers');
  if (!layers) return {principalAmount: 0, counterclaimAmount: 0};
  const principal = parseLitigationMoney(layers.querySelector<HTMLInputElement>('[data-amount-part="principal"]')?.value || '');
  const counterInput = layers.querySelector<HTMLInputElement>('[data-amount-part="counter"]');
  const counter = counterInput ? parseLitigationMoney(counterInput.value) : 0;
  if (principal === null || counter === null) return null;
  return {
    principalAmount: principal,
    counterclaimAmount: layers.querySelector('.ll-amount-kind.is-wide') ? 0 : counter,
  };
}

/**
 * 列表列编辑时使用的标准字段（填写规则、校验）。表头「案件名称」与「案由」须分开。
 * @param field 方案列
 */
function resolveListColumnEditField(field: ListColumnField): string {
  const label = field.label.trim();
  if (label === '案件名称' || label === '纠纷名称') return 'caseName';
  if (label === '案由') return 'title';
  if (label === '案件性质') return 'caseNature';
  if (label === '所属单位') return 'organizationUnit';
  const storage = resolveListColumnStorageField(field);
  if (storage) return storage;
  return field.id.startsWith('template_') ? '' : field.id;
}
type LitigationListPayload = {
  cases: LedgerCase[];
  access: LitigationLedgerAccess;
  stats: LitigationLedgerStats;
  eligibleMembers: EligibleMember[];
  activePlan: {id: string; name: string; listFieldIds: string[]; listFields: Array<{id: string; label: string; sourceField?: string}>; internalNoRule?: ReturnType<typeof normalizeLitigationInternalNoRule>; fieldRuleOverrides?: LitigationFieldRuleOverrides; sheetFieldLabels?: Partial<Record<LitigationSheetKind, string[]>>; sheetColumns?: Partial<Record<LitigationSheetKind, Array<{id: string; label: string; sourceField: string}>>>};
  canManagePlans: boolean;
  identityNotice?: string;
  exportTitle?: {row: number; title: string; canSetRow: boolean; headerRow: number};
  /** 台账绑定的方案副本，协作设置从这里读单位和人员。 */
  mapping?: {
    id: string;
    planScope?: 'personal' | 'enterprise';
    createdBy?: string;
    creatorOrganizationName?: string;
    creatorOrganizationShortName?: string;
    creatorOrganizationCode?: string;
    organizationLabelKind?: string;
    collaborationUnits?: LitigationPlanCollaborationUnit[];
    teamEditors?: LitigationPlanTeamEditor[];
  };
};

const icon = (name: string) => `<img class="la-icon" src="/assets/icons/archive/${name}.svg" alt="" aria-hidden="true">`;

type LitigationWorkbookCard = {
  id: string;
  name: string;
  scope: 'personal' | 'team';
  organizationId: string;
  folderId: string;
  isAdmin: boolean;
  editorCount: number;
  createdByName?: string;
  updatedAt: string;
};

/**
 * 解析诉讼台账路径上的工作区 ID。
 */
function litigationWorkbookIdFromPath(): string {
  const match = window.location.pathname.match(/\/tax-assistant\/legal-litigation\/([^/]+)/);
  const value = match?.[1] || '';
  return value === 'settings' ? '' : value;
}

/**
 * 按案件 ID 去重，避免 Excel 重复行导致列表出现两个「+」与双高亮。
 * @param list 原始案件列表
 */
function dedupeLitigationCases(list: LedgerCase[]): LedgerCase[] {
  const seen = new Set<string>();
  const unique: LedgerCase[] = [];
  for (const item of list) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    unique.push(item);
  }
  return unique;
}

/**
 * 当前列表中某案件 ID 首次出现的行号。
 * @param items 可见行
 * @param caseId 案件 ID
 */
function firstCaseRowIndex(items: LedgerCase[], caseId: string): number {
  return items.findIndex((item) => item.id === caseId);
}

/** 列表内联编辑可暂存、保存、撤销的字段。 */
const LIST_EDIT_PATCH_KEYS = [
  'title',
  'courtNo',
  'venue',
  'handler',
  'counsel',
  'claimSummary',
  'resultSummary',
  'filingDate',
  'amount',
  'principalAmount',
  'counterclaimAmount',
] as const;

type LitigationUndoEntry =
  | {kind: 'localPatch'; caseId: string; before: LedgerCase; after: LedgerCase}
  | {kind: 'create'; caseId: string; snapshot: LedgerCase; insertAfterCaseId?: string}
  | {kind: 'delete'; caseId: string; snapshot: LedgerCase; insertAfterCaseId?: string};

/**
 * 撤销新建行之后再恢复时，新行会换一个编号。把还堆在栈里的后续步骤改到新编号上。
 * @param entry 历史步骤
 * @param fromId 原来的案件编号
 * @param toId 恢复后的案件编号
 */
function remapLitigationHistoryId(entry: LitigationUndoEntry, fromId: string, toId: string): void {
  if (entry.caseId === fromId) entry.caseId = toId;
  if (entry.kind === 'create' && entry.insertAfterCaseId === fromId) entry.insertAfterCaseId = toId;
  if (entry.kind === 'localPatch') {
    if (entry.before.id === fromId) entry.before = {...entry.before, id: toId};
    if (entry.after.id === fromId) entry.after = {...entry.after, id: toId};
  }
}

/**
 * 深拷贝案件，供撤销栈使用。
 * @param item 案件
 */
function cloneLedgerCase(item: LedgerCase): LedgerCase {
  return structuredClone(item);
}

/**
 * 将列表补丁应用到内存中的案件（保存前预览）。
 * @param item 案件
 * @param patch 补丁
 */
function applyListPatchLocal(item: LedgerCase, patch: Record<string, unknown>): void {
  Object.assign(item, Object.fromEntries(Object.entries(patch).filter(([key]) => LITIGATION_EDIT_PATCH_KEYS.includes(key as typeof LITIGATION_EDIT_PATCH_KEYS[number]))));
  if (patch.title !== undefined) item.title = String(patch.title);
  if (patch.courtNo !== undefined) item.courtNo = String(patch.courtNo);
  if (patch.courtNumbers !== undefined) {
    item.courtNumbers = normalizeLitigationCourtNumberRows(patch.courtNumbers);
    item.courtNo = litigationCourtNumberSummary(item.courtNumbers);
  }
  if (patch.venues !== undefined) {
    item.venues = normalizeLitigationVenueRows(patch.venues);
    item.venue = litigationVenueSummary(item.venues);
  }
  if (patch.venue !== undefined) item.venue = String(patch.venue);
  if (patch.handler !== undefined) item.handler = String(patch.handler);
  if (patch.counsel !== undefined) item.counsel = String(patch.counsel);
  if (patch.claimSummary !== undefined) item.claimSummary = String(patch.claimSummary);
  if (patch.counterclaimSummary !== undefined) item.counterclaimSummary = String(patch.counterclaimSummary);
  if (patch.resultSummary !== undefined) item.resultSummary = String(patch.resultSummary);
  if (patch.counterclaimResult !== undefined) item.counterclaimResult = String(patch.counterclaimResult);
  if (patch.awardedAmount !== undefined) item.awardedAmount = Math.max(0, Number(patch.awardedAmount) || 0);
  if (patch.counterclaimAwardedAmount !== undefined) item.counterclaimAwardedAmount = Math.max(0, Number(patch.counterclaimAwardedAmount) || 0);
  if (patch.nonMonetaryResult !== undefined) item.nonMonetaryResult = String(patch.nonMonetaryResult);
  if (patch.filingDate !== undefined) item.filingDate = String(patch.filingDate);
  if (patch.amount !== undefined) item.amount = Math.max(0, Number(patch.amount) || 0);
  if (patch.principalAmount !== undefined) item.principalAmount = Math.max(0, Number(patch.principalAmount) || 0);
  if (patch.counterclaimAmount !== undefined) item.counterclaimAmount = Math.max(0, Number(patch.counterclaimAmount) || 0);
  if (patch.stageName !== undefined) {
    const stageName = String(patch.stageName);
    item.currentStage = stageName;
    /**
     * 选了其他阶段就不再视为已结案，避免下拉立刻弹回「已结案」。
     */
    item.closed = stageName.trim() === '已结案';
    if (!item.closed) item.litigationClosedAt = '';
    if (stageName.trim()) {
      item.unfilledFields = (item.unfilledFields || []).filter((field) => field !== 'currentStage');
    }
  }
  if (patch.litigationClosedAt !== undefined) item.closedAt = String(patch.litigationClosedAt);
  if (patch.closed === false) item.closedAt = '';
}

/**
 * 相对空间已存快照，汇总未保存的列表补丁。
 * @param server 已保存快照
 * @param current 当前内存案件
 */
function buildListPatchFromDiff(server: LedgerCase, current: LedgerCase): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of LITIGATION_EDIT_PATCH_KEYS) {
    if (JSON.stringify(server[key]) !== JSON.stringify(current[key])) patch[key] = current[key];
  }
  for (const field of server.unfilledFields || []) {
    if (current.unfilledFields?.includes(field)) continue;
    const key = field === 'closedFlag' ? 'closed' : field;
    if (field === 'currentStage') patch.stageName = current.currentStage || currentStageName(current);
    else if (key in current) patch[key] = current[key as keyof LedgerCase];
  }
  const serverStage = server.currentStage || currentStageName(server);
  const currentStage = current.currentStage || currentStageName(current);
  if (serverStage !== currentStage) patch.stageName = currentStage;
  return patch;
}

/**
 * 渲染诉讼台账管理页。
 * @param root 根节点
 * @param auth 登录快照
 */
export async function renderLitigationLedgerPage(root: HTMLElement, auth: AuthSnapshot): Promise<void> {
  if (!isLegalFeatureEnabled('legal-litigation-ledger-v1')) {
    renderLayout(root, auth, `<section class="la-page"><div class="la-empty"><b>诉讼台账已关闭</b><span>管理员已停用该功能。</span></div></section>`);
    return;
  }
  const workbookId = litigationWorkbookIdFromPath();
  if (!workbookId) {
    await renderWorkbookList(root, auth);
    return;
  }
  renderLayout(root, auth, pageShell());
  bindLitigationRailCollapse(root);
  // Reuse the shell's live controls and listeners in the ledger's tool rail.
  const railFooter = root.querySelector('.ll-rail-footer');
  for (const id of ['notificationButton', 'helpButton', 'plansLogoutButton']) {
    const control = root.querySelector(`#${id}`);
    if (control) railFooter?.append(control);
  }
  await mountLitigationPage(root, auth, workbookId);
}

/**
 * 台账列表与新建向导。
 * @param root 根节点
 * @param auth 登录快照
 */
async function renderWorkbookList(root: HTMLElement, auth: AuthSnapshot): Promise<void> {
  renderLayout(root, auth, `<div class="legacy-module-page invoice-recognition-page ll-ledger-page">
    <section class="invoice-page-head">
      <div class="invoice-page-actions">
        <button type="button" class="primary-button" id="llNewBook">新建台账</button>
        <button type="button" class="invoice-delete-button" id="llBookDelete" disabled>删除所选 0</button>
      </div>
      <div class="invoice-page-tools">
        <div class="invoice-search-group">
          <input class="form-input invoice-page-search" id="llBookSearch" type="search" placeholder="搜索台账名称">
          <button type="button" class="secondary-button" id="llBookSearchButton">搜索</button>
        </div>
      </div>
    </section>
    <section class="invoice-list-panel">
      <div class="table-scroll">
        <table class="invoice-grid-table ll-grid-table">
          <thead><tr>
            <th class="col-select"><input type="checkbox" id="llBookSelectAll" aria-label="全选"></th>
            <th>台账名称</th>
            <th>创建人</th>
            <th>范围</th>
            <th>可编辑人数</th>
            <th>更新时间</th>
            <th class="col-remark">操作</th>
          </tr></thead>
          <tbody id="llBookList"><tr><td colspan="7" class="legacy-empty-cell">正在加载…</td></tr></tbody>
        </table>
      </div>
    </section>
    <div id="llDialogHost"></div>
  </div>`);
  const host = root.querySelector<HTMLElement>('#llBookList')!;
  const deleteButton = root.querySelector<HTMLButtonElement>('#llBookDelete')!;
  const checked = new Set<string>();
  let books: LitigationWorkbookCard[] = [];
  let renamingId = '';
  const syncDelete = () => {
    deleteButton.disabled = !checked.size;
    deleteButton.textContent = `删除所选 ${checked.size}`;
  };
  /**
   * 台账名称格。创建人看到铅笔图标，点开后在原位改名。
   * @param book 列表中的台账
   */
  const bookNameCell = (book: LitigationWorkbookCard): string => {
    if (renamingId === book.id) {
      return `<form class="ll-book-rename-form" data-rename-form="${escapeHtml(book.id)}"><input class="form-input" name="name" maxlength="80" required value="${escapeHtml(book.name)}" aria-label="台账名称"><button type="submit" class="secondary-button">保存</button><button type="button" class="secondary-button" data-rename-cancel>取消</button></form>`;
    }
    const rename = book.isAdmin
      ? `<button type="button" class="ll-book-rename" data-rename="${escapeHtml(book.id)}" title="修改台账名称" aria-label="修改台账名称"><img src="/assets/icons/edit.svg" alt="" aria-hidden="true"></button>`
      : '';
    return `<span class="ll-book-name"><button type="button" class="invoice-view-link" data-open="${escapeHtml(book.id)}">${escapeHtml(book.name)}</button>${rename}</span>`;
  };
  const paint = (items: LitigationWorkbookCard[]) => {
    const query = (root.querySelector<HTMLInputElement>('#llBookSearch')?.value || '').trim().toLocaleLowerCase();
    const visible = items.filter((book) => !query || book.name.toLocaleLowerCase().includes(query));
    if (!visible.length) {
      host.innerHTML = `<tr><td colspan="7" class="legacy-empty-cell">${items.length ? '没有匹配的台账' : '暂无诉讼台账，请新建'}</td></tr>`;
      return;
    }
    host.innerHTML = visible.map((book) => `<tr data-book-row="${escapeHtml(book.id)}">
      <td class="col-select"><input type="checkbox" data-book-check="${escapeHtml(book.id)}" ${checked.has(book.id) ? 'checked' : ''}></td>
      <td>${bookNameCell(book)}</td>
      <td>${escapeHtml(book.createdByName || '—')}</td>
      <td>${book.scope === 'team' ? '团队' : '个人'}</td>
      <td>${book.editorCount}</td>
      <td>${escapeHtml(book.updatedAt.slice(0, 16).replace('T', ' '))}</td>
      <td class="col-remark"><button type="button" class="invoice-view-link" data-open="${escapeHtml(book.id)}">打开</button></td>
    </tr>`).join('');
  };
  try {
    const payload = await api.get<{workbooks: LitigationWorkbookCard[]}>(apiUrl('/api/legal-litigation/workbooks'));
    books = payload.workbooks;
    paint(books);
  } catch (error) {
    host.innerHTML = `<tr><td colspan="6" class="legacy-empty-cell">${escapeHtml(error instanceof Error ? error.message : '请稍后重试')}</td></tr>`;
  }
  /**
   * 打开列表中的台账。协作人员当前若停在别的空间，先切到台账所在空间，避免读到另一套默认表头。
   * @param bookId 台账 ID
   */
  const openListedWorkbook = async (bookId: string): Promise<void> => {
    const book = books.find((item) => item.id === bookId);
    const organizationId = book?.organizationId || '';
    if (organizationId && organizationId !== auth.organization.id) {
      await api.post(apiUrl(`/api/organizations/enterprise-spaces/${encodeURIComponent(organizationId)}/select`), {});
      window.location.assign(`/tax-assistant/legal-litigation/${encodeURIComponent(bookId)}`);
      return;
    }
    navigate(`/tax-assistant/legal-litigation/${encodeURIComponent(bookId)}`);
  };
  host.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const renameId = target.closest<HTMLElement>('[data-rename]')?.getAttribute('data-rename');
    if (renameId) {
      renamingId = renameId;
      paint(books);
      const input = host.querySelector<HTMLInputElement>('.ll-book-rename-form input');
      input?.focus();
      input?.select();
      return;
    }
    if (target.closest('[data-rename-cancel]')) {
      renamingId = '';
      paint(books);
      return;
    }
    const openId = target.closest<HTMLElement>('[data-open]')?.getAttribute('data-open');
    if (openId) void openListedWorkbook(openId);
  });
  host.addEventListener('submit', (event) => {
    const form = (event.target as HTMLElement).closest<HTMLFormElement>('.ll-book-rename-form');
    if (!form) return;
    event.preventDefault();
    const bookId = form.getAttribute('data-rename-form') || '';
    const name = String(new FormData(form).get('name') || '').trim();
    if (!bookId || !name) return;
    const buttons = [...form.querySelectorAll<HTMLButtonElement>('button')];
    buttons.forEach((button) => { button.disabled = true; });
    void api.patch<{workbook: LitigationWorkbookCard}>(apiUrl(`/api/legal-litigation/workbooks/${encodeURIComponent(bookId)}`), {name})
      .then((payload) => {
        const index = books.findIndex((item) => item.id === bookId);
        if (index >= 0 && payload.workbook) books[index] = {...books[index], ...payload.workbook};
        renamingId = '';
        paint(books);
      })
      .catch((error) => {
        buttons.forEach((button) => { button.disabled = false; });
        window.alert(error instanceof Error ? error.message : '台账名称没有保存');
      });
  });
  host.addEventListener('change', (event) => {
    const input = event.target as HTMLInputElement;
    if (input.dataset.bookCheck) {
      if (input.checked) checked.add(input.dataset.bookCheck);
      else checked.delete(input.dataset.bookCheck);
      syncDelete();
    }
  });
  root.querySelector('#llBookSelectAll')?.addEventListener('change', (event) => {
    const on = (event.target as HTMLInputElement).checked;
    books.forEach((book) => {
      if (on) checked.add(book.id);
      else checked.delete(book.id);
    });
    paint(books);
    syncDelete();
  });
  const filterBooks = () => paint(books);
  root.querySelector('#llBookSearch')?.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key === 'Enter') {
      event.preventDefault();
      filterBooks();
    }
  });
  root.querySelector('#llBookSearchButton')?.addEventListener('click', filterBooks);
  root.querySelector('#llBookDelete')?.addEventListener('click', () => {
    if (!checked.size) return;
    window.alert('请打开台账后，在案件列表中删除案件。台账本身请在空间中管理。');
  });
  root.querySelector('#llNewBook')?.addEventListener('click', () => {
    void openLitigationWorkbookCreation(root, auth).catch((error) => {
      window.alert(error instanceof Error ? error.message : '无法加载方案');
    });
  });
}

/**
 * 同步诉讼台账左侧栏的收起状态与按钮文案。
 * @param root 页面根节点
 */
function bindLitigationRailCollapse(root: HTMLElement): void {
  const page = root.querySelector<HTMLElement>('.ll-workbook-page');
  const button = root.querySelector<HTMLButtonElement>('#llRailCollapse');
  if (!page || !button) return;
  /**
   * @param collapsed 是否隐藏左侧栏
   */
  const syncRailCollapsed = (collapsed: boolean): void => {
    page.classList.toggle('is-rail-collapsed', collapsed);
    localStorage.setItem('tax-assistant-litigation-rail-collapsed', collapsed ? '1' : '0');
    button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    const label = collapsed ? '显示侧栏' : '隐藏侧栏';
    button.title = label;
    button.setAttribute('aria-label', label);
  };
  button.addEventListener('click', () => syncRailCollapsed(!page.classList.contains('is-rail-collapsed')));
}

function pageShell(): string {
  const railCollapsed = localStorage.getItem('tax-assistant-litigation-rail-collapsed') === '1';
  const railLabel = railCollapsed ? '显示侧栏' : '隐藏侧栏';
  return `<div class="legacy-module-page invoice-recognition-page ll-ledger-page ll-workbook-page${railCollapsed ? ' is-rail-collapsed' : ''}" aria-label="诉讼台账管理">
    <div class="ll-rail-wrap">
    <aside class="ll-tool-rail" id="llToolRail" aria-label="台账操作">
      <a class="ll-rail-back" href="/tax-assistant/tasks" data-route><img src="/assets/icons/archive/arrow-left.svg" alt="">工作台</a>
      <h1>诉讼台账管理</h1>
    <section class="invoice-page-head">
      <div class="invoice-page-actions">
        <div class="ll-toolbar-menu" id="llFileMenuRoot">
          <button type="button" class="primary-button" id="llFileMenu" aria-haspopup="menu" aria-expanded="false" aria-controls="llFileMenuPanel">台账文件</button>
          <div class="ll-toolbar-menu-panel" id="llFileMenuPanel" role="menu" hidden>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llCreate">新建台账</button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llSaveWorkbook" disabled>保存台账</button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llLoadWorkbook">载入</button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llImport" hidden>导入</button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llExport">导出</button>
          </div>
        </div>
        <input id="llImportFile" type="file" accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden>
        <div class="ll-toolbar-menu" id="llEditMenuRoot">
          <button type="button" class="secondary-button invoice-batch-action-button" id="llEditMenu" aria-haspopup="menu" aria-expanded="false" aria-controls="llEditMenuPanel">编辑</button>
          <div class="ll-toolbar-menu-panel" id="llEditMenuPanel" role="menu" hidden>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llUndoEdit" disabled>撤销<span class="ll-menu-shortcut">Ctrl+Z</span></button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llRedoEdit" disabled>恢复<span class="ll-menu-shortcut">Ctrl+Y</span></button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llExportTitle">台账标题</button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llInternalNoRule">内部案号规则</button>
          </div>
        </div>
        <div class="ll-toolbar-menu is-hidden" id="llCollaborationRoot">
          <button type="button" class="secondary-button invoice-batch-action-button" id="llCollaboration" aria-haspopup="menu" aria-expanded="false" aria-controls="llCollaborationPanel">协作设置</button>
          <div class="ll-toolbar-menu-panel" id="llCollaborationPanel" role="menu" hidden>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llCollabAdmin">台账管理员归属设定</button>
            <button type="button" class="ll-toolbar-menu-item" role="menuitem" id="llCollabTeam">协作单位及用户设置</button>
          </div>
        </div>
        <button type="button" class="invoice-delete-button" id="llBatchDelete" disabled>删除所选 0</button>
      </div>
      <div class="invoice-page-tools">
        <button type="button" class="secondary-button" id="llDeadlines">期限队列</button>
        <button type="button" class="secondary-button" id="llColumnPicker">字段选择</button>
      </div>
    </section>
      <div class="ll-rail-footer"></div>
    </aside>
    <button class="sidebar-collapse ll-rail-collapse" id="llRailCollapse" type="button" aria-controls="llToolRail" aria-expanded="${railCollapsed ? 'false' : 'true'}" title="${railLabel}" aria-label="${railLabel}"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
    </div>
    <header class="ll-workspace-heading">
      <div class="ll-heading-title"><div class="ll-ledger-title is-empty" id="llLedgerTitle">未设置台账标题</div><span class="ll-save-state" id="llSaveState" role="status">正在加载</span></div>
      <div class="ll-heading-tools">
        <button type="button" class="secondary-button" id="llColumnFilter" aria-pressed="false">筛选</button>
        <div class="invoice-search-group">
          <input class="form-input invoice-page-search" id="llSearch" type="search" placeholder="搜索案号、案由、对方">
          <button type="button" class="secondary-button" id="llSearchButton">搜索</button>
        </div>
      </div>
    </header>
    <div class="invoice-page-notice" id="llNotice" hidden></div>
    <section class="invoice-list-panel">
      <div class="table-scroll" id="llTableScroll">
        <table class="invoice-grid-table ll-grid-table ll-case-table">
          <colgroup id="llColGroup"></colgroup>
          <thead><tr id="llHeadRow"></tr></thead>
          <tbody id="llRows"><tr><td colspan="8" class="legacy-empty-cell">正在加载诉讼台账…</td></tr></tbody>
        </table>
      </div>
    </section>
    <div class="ll-sheet-bar">
      <nav class="ll-sheet-tabs" aria-label="工作表">
        ${LITIGATION_DEFAULT_SHEETS.map((sheet) => `<button type="button" class="${sheet.kind === 'cases' ? 'is-active' : ''}" data-sheet="${sheet.kind}">${escapeHtml(sheet.name)}</button>`).join('')}
      </nav>
      <div class="ll-sheet-hscroll" id="llSheetHScroll" aria-hidden="true"><div id="llSheetHScrollInner"></div></div>
    </div>
    <aside class="ll-detail-drawer" id="llDetail" hidden>
      <button type="button" class="ll-drawer-close" data-action="dismiss-detail" aria-label="关闭">×</button>
      <div class="la-empty">选择案件查看详情</div>
    </aside>
    <div id="llDialogHost"></div>
  </div>`;
}

function formatDate(value: string): string {
  return value ? value.slice(0, 10) : '—';
}

/**
 * 进展里展示的长日期，月日不补零。无法识别时保持原文。
 * @param value 年-月-日
 */
function formatProgressLongDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return value.trim();
  return `${Number(match[1])}年${Number(match[2])}月${Number(match[3])}日`;
}

function stageOptions(cases: LedgerCase[]): string[] {
  const names = new Set<string>(['已结案']);
  for (const item of cases) item.nodes.forEach((node) => names.add(node.name));
  return [...names];
}

/**
 * 当前阶段下拉。诉讼和仲裁使用各自的固定阶段，其他类型沿用程序节点。
 * @param item 案件
 * @param cases 当前列表，供其他案件收集节点名
 */
function stageOptionsForCase(item: LedgerCase, cases: LedgerCase[]): string[] {
  const trial = litigationTrialStageOptions(item);
  return trial.length ? [...trial] : stageOptions(cases);
}

/**
 * 结案方式下拉。已保存但不属于当前案件类型的值仍保留，避免打开表单时被清空。
 * @param item 案件
 */
function closeModeSelectOptions(item: LedgerCase): Array<{id: string; label: string}> {
  const choices = litigationCloseModeChoices(item);
  if (!item.closeMode || choices.some((choice) => choice.id === item.closeMode)) return choices;
  const label = CLOSE_MODE_LABELS[item.closeMode];
  return label ? [{id: item.closeMode, label}, ...choices] : choices;
}

/** 列筛选里代表空格子的选项。 */
const COLUMN_FILTER_BLANK = '（空白）';

/**
 * 把格子里的显示文字收成筛选用的一项。空内容和占位符「—」都算空白。
 * @param value 格子里能看见的文字
 */
function columnFilterKey(value: string): string {
  const text = value.replace(/\r\n/g, '\n').trim();
  return text && text !== '—' ? text : COLUMN_FILTER_BLANK;
}

/**
 * 下拉框里应对上的阶段名。诉讼案件结案后显示「已结案」。
 * @param item 案件
 * @param stage 当前展示的阶段
 * @param options 本行选项
 */
function stageSelectValue(item: LedgerCase, stage: string, options: string[]): string {
  if (options.includes(stage) && stage !== '已结案' && stage !== '结案' && stage !== '诉讼结案') return stage;
  if (options.includes('已结案') && (item.closed || stage === '已结案' || stage === '结案' || stage === '诉讼结案')) return '已结案';
  if (stage === '上诉/再审' && options.includes('上诉')) return '上诉';
  return stage;
}

/**
 * 当前阶段下拉显示出「已结案」时，结案三列才可填写。
 * @param item 案件
 * @param stage 列表里算出的阶段名
 */
function litigationDisplayedStageIsClosed(item: LedgerCase, stage: string): boolean {
  const options = litigationTrialStageOptions(item);
  if (options.includes('已结案')) {
    return Boolean(item.closed) || stage === '已结案' || stage === '结案' || stage === '诉讼结案';
  }
  return stage === '已结案';
}

async function mountLitigationPage(root: HTMLElement, auth: AuthSnapshot, workbookId: string): Promise<void> {
  const page = root.querySelector<HTMLElement>('.ll-ledger-page')!;
  const rows = page.querySelector<HTMLElement>('#llRows')!;
  const detail = page.querySelector<HTMLElement>('#llDetail')!;
  const search = page.querySelector<HTMLInputElement>('#llSearch')!;
  /** 表头筛选标记是否展开。与是否已经勾选过某列无关。 */
  let columnFilterArmed = false;
  /** 按列字段 id 记住勾中的显示值。没有该列表示这一列不筛选。 */
  const columnFilters = new Map<string, Set<string>>();
  let columnFilterMenu: HTMLElement | null = null;
  const batchDelete = page.querySelector<HTMLButtonElement>('#llBatchDelete');
  const base = `/api/legal-litigation/workbooks/${encodeURIComponent(workbookId)}`;
  let cases: LedgerCase[] = [];
  let selected = new URLSearchParams(window.location.search).get('case') || '';
  /** 仅当用户点「查看」时为非空，控制右侧抽屉。 */
  let detailCaseId = '';
  let tab: DetailTab = 'overview';
  let listSheet: LitigationSheetKind = 'cases';
  let saveTimer = 0;
  let dirty = false;
  let overviewSaving = false;
  let workbookSaving = false;
  let overviewEditVersion = 0;
  let contracts: LinkableContract[] = [];
  let access: LitigationLedgerAccess = {
    organizationId: auth.organization.id,
    createdBy: '',
    adminUserIds: [],
    participants: [],
    isAdmin: false,
    currentUserId: auth.user.id,
    canCreate: false,
  };
  /** 当前台账方案上的协作设定。打开「协作设置」时用它填对话框。 */
  let planSettings: NonNullable<LitigationListPayload['mapping']> | null = null;
  let eligibleMembers: EligibleMember[] = [];
  let listFields: Array<{id: string; label: string; sourceField?: string}> = [];
  let sheetColumns: LitigationListPayload['activePlan']['sheetColumns'];
  let internalNoRule = normalizeLitigationInternalNoRule(undefined);
  const checked = new Set<string>();
  const hiddenFieldIds = new Set<string>();
  const columnStorageKey = `ll-list-hidden:${auth.organization.id}:${workbookId}`;
  const columnWidthStorageKey = `ll-list-col-widths:${auth.organization.id}:${workbookId}`;
  try {
    const saved = JSON.parse(localStorage.getItem(columnStorageKey) || '[]');
    if (Array.isArray(saved)) saved.filter((id): id is string => typeof id === 'string').forEach((id) => hiddenFieldIds.add(id));
  } catch {
    hiddenFieldIds.clear();
  }
  const columnWidths: Record<string, number> = {};
  try {
    const savedWidths = JSON.parse(localStorage.getItem(columnWidthStorageKey) || '{}') as unknown;
    if (savedWidths && typeof savedWidths === 'object') {
      for (const [key, value] of Object.entries(savedWidths as Record<string, unknown>)) {
        if (typeof value === 'number' && value >= 48 && value <= 640) columnWidths[key] = value;
      }
    }
  } catch {
    /* 忽略损坏的列宽缓存 */
  }
  let noticeTimer = 0;
  /** 当前用户的导出标题。不在台账文件里，导出时才写入。 */
  let exportTitle: {row: number; title: string; canSetRow: boolean; headerRow: number} = {
    row: 1,
    title: '',
    canSetRow: false,
    headerRow: 2,
  };
  /** 表格横向滚动与左下角工作表条对齐。 */
  let syncSheetHScroll = () => {};

  /**
   * 在工作表名称原来的位置显示当前用户设定的台账标题。
   */
  const paintLedgerTitle = () => {
    const node = page.querySelector<HTMLElement>('#llLedgerTitle');
    if (!node) return;
    const title = exportTitle.title.trim();
    node.textContent = title || '未设置台账标题';
    node.classList.toggle('is-empty', !title);
    node.title = title;
  };
  /** 空间中已持久化的案件快照（按 ID）。 */
  const serverCasesById = new Map<string, LedgerCase>();
  /** 待保存到空间的列表补丁（按案件 ID 合并）。 */
  const pendingPatches = new Map<string, Record<string, unknown>>();
  const draftStorageKey = `ll-case-drafts:${auth.user.id}:${workbookId}`;
  type CaseDraft = {caseId: string; updatedAt: string; patch: Record<string, unknown>};
  let unresolvedDrafts: CaseDraft[] = [];
  const writeCaseDrafts = () => {
    const drafts: CaseDraft[] = [...unresolvedDrafts.filter((draft) => !pendingPatches.has(draft.caseId)),
      ...[...pendingPatches].map(([caseId, patch]) => ({caseId, updatedAt: serverCasesById.get(caseId)?.updatedAt || '', patch}))];
    try {
      if (drafts.length) localStorage.setItem(draftStorageKey, JSON.stringify(drafts));
      else localStorage.removeItem(draftStorageKey);
    } catch { /* 存储不可用时仍保留内存草稿和离页警告。 */ }
  };
  const caseRequestTails = new Map<string, Promise<unknown>>();
  const patchCase = async (caseId: string, patch: Record<string, unknown>): Promise<{case: LedgerCase}> => {
    const previous = caseRequestTails.get(caseId) || Promise.resolve();
    const request = previous.catch(() => undefined).then(async () => {
      const submitted = structuredClone({...patch, ...pendingPatches.get(caseId)});
      const baseline = serverCasesById.get(caseId);
      const result = await api.patch<{case: LedgerCase}>(apiUrl(`${base}/cases/${encodeURIComponent(caseId)}`), {
        ...submitted, ...(baseline ? {_expectedUpdatedAt: baseline.updatedAt} : {}),
      });
      const current = cases.find((entry) => entry.id === caseId);
      if (!current || result.case.id !== caseId) throw new Error('案件已删除或服务器返回编号不一致，已停止更新');
      const remaining = remainingLitigationPatch(pendingPatches.get(caseId) || {}, submitted);
      if (Object.keys(remaining).length) pendingPatches.set(caseId, remaining);
      else pendingPatches.delete(caseId);
      const merged = mergeLitigationCaseResponse(current, result.case, submitted, remaining);
      applyListPatchLocal(merged, remaining);
      serverCasesById.set(caseId, cloneLedgerCase(result.case));
      cases = cases.map((entry) => entry.id === caseId ? merged : entry);
      writeCaseDrafts();
      return {case: merged};
    });
    caseRequestTails.set(caseId, request);
    try { return await request; }
    finally { if (caseRequestTails.get(caseId) === request) caseRequestTails.delete(caseId); }
  };
  /** 当前用户可撤销的编辑栈（仅本人未结案行的列表操作）。 */
  const undoStack: LitigationUndoEntry[] = [];
  /** 刚刚撤销、还可以恢复的步骤。新的编辑会清掉这条恢复链。 */
  const redoStack: LitigationUndoEntry[] = [];
  const MAX_UNDO_DEPTH = 50;

  /**
   * 刷新「已保存到空间」快照（加载或保存成功后调用）。
   */
  const refreshServerCaseSnapshot = () => {
    serverCasesById.clear();
    for (const item of cases) serverCasesById.set(item.id, cloneLedgerCase(item));
    pendingPatches.clear();
    const keptDeletes = undoStack.filter((entry) => entry.kind === 'delete');
    undoStack.length = 0;
    undoStack.push(...keptDeletes);
    redoStack.length = 0;
    syncWorkbookEditControls();
  };

  /**
   * 更新保存/撤销按钮状态。
   */
  const syncWorkbookEditControls = () => {
    writeCaseDrafts();
    const saveBtn = page.querySelector<HTMLButtonElement>('#llSaveWorkbook');
    const undoBtn = document.getElementById('llUndoEdit') as HTMLButtonElement | null;
    const redoBtn = document.getElementById('llRedoEdit') as HTMLButtonElement | null;
    const fileMenu = page.querySelector<HTMLButtonElement>('#llFileMenu');
    const hasUnsaved = pendingPatches.size > 0 || dirty;
    const state = page.querySelector<HTMLElement>('#llSaveState');
    if (state) {
      state.textContent = overviewSaving || workbookSaving ? '正在保存' : hasUnsaved ? '未保存' : '已保存';
      state.classList.toggle('is-dirty', hasUnsaved);
    }
    if (saveBtn) {
      saveBtn.disabled = !hasUnsaved || overviewSaving || workbookSaving;
      saveBtn.classList.toggle('is-dirty', hasUnsaved);
      saveBtn.title = hasUnsaved ? '将修改写入空间台账文件' : '';
    }
    if (fileMenu) fileMenu.classList.toggle('is-dirty', hasUnsaved);
    if (undoBtn) {
      undoBtn.disabled = undoStack.length === 0;
      undoBtn.title = undoStack.length ? '撤销上一步您在列表中的可编辑操作' : '';
      if (undoBtn.firstChild) undoBtn.firstChild.textContent = undoStack.at(-1)?.kind === 'delete' ? '撤销删除' : '撤销';
    }
    if (redoBtn) {
      redoBtn.disabled = redoStack.length === 0;
      redoBtn.title = redoStack.length ? '恢复刚刚撤销的那一步' : '';
    }
    const importBtn = document.getElementById('llImport') as HTMLButtonElement | null;
    if (importBtn) importBtn.hidden = !access.isAdmin;
  };

  /**
   * 当前用户是否可在列表中编辑该案。
   * @param item 案件
   */
  const canEditCaseInList = (item: LedgerCase): boolean => (
    canEditLitigationCaseForAccess(access, item.ownerUserId)
  );

  /**
   * 是否可在列表中勾选并删除该案（本人负责或台账管理员）。
   * @param item 案件
   */
  const canDeleteCaseInList = (item: LedgerCase): boolean => (
    canDeleteLitigationCaseForAccess(access, item.ownerUserId)
  );

  /**
   * 压入一条可撤销记录。新的编辑会丢掉尚未恢复的步骤。
   * @param entry 撤销条目
   */
  const pushUndoEntry = (entry: LitigationUndoEntry) => {
    redoStack.length = 0;
    undoStack.push(entry);
    if (undoStack.length > MAX_UNDO_DEPTH) undoStack.shift();
    syncWorkbookEditControls();
  };

  /**
   * 根据内存与空间快照，重算某案的待保存补丁。
   * @param caseId 案件 ID
   */
  const recomputePendingForCase = (caseId: string) => {
    const item = cases.find((entry) => entry.id === caseId);
    const server = serverCasesById.get(caseId);
    if (!item || !server) {
      pendingPatches.delete(caseId);
      return;
    }
    const patch = buildListPatchFromDiff(server, item);
    if (Object.keys(patch).length) pendingPatches.set(caseId, patch);
    else pendingPatches.delete(caseId);
  };

  /**
   * 列表编辑：先改内存并标记待保存，点「保存台账」后写入空间。
   * @param caseId 案件 ID
   * @param patch 补丁
   */
  const queueLocalListPatch = (caseId: string, patch: Record<string, unknown>, rerender = true) => {
    if (!Object.keys(patch).length) return;
    const item = cases.find((entry) => entry.id === caseId);
    if (!item || !canEditCaseInList(item)) return;
    const before = cloneLedgerCase(item);
    applyListPatchLocal(item, patch);
    if (item.disputeType === 'litigation' && item.instance !== 'enforcement' && !item.unfilledFields?.includes('disputeType') && (patch.courtNumbers !== undefined || patch.disputeType !== undefined)) {
      const derivedInstance = litigationInstanceFromCourtRows(resolveLitigationCourtNumberRows(item)) || 'first';
      item.instance = derivedInstance;
      item.unfilledFields = (item.unfilledFields || []).filter((field) => field !== 'instance');
      patch.instance = derivedInstance;
    }
    if (patch.disputeType !== undefined || patch.instance !== undefined) {
      const layerContext = {disputeType: item.disputeType, instance: item.instance};
      if (item.courtNumbers) {
        const rows = alignLitigationCourtNumberRows(normalizeLitigationCourtNumberRows(item.courtNumbers), layerContext);
        item.courtNumbers = rows;
        item.courtNo = litigationCourtNumberSummary(rows);
        patch.courtNumbers = rows;
        patch.courtNo = item.courtNo;
      }
      if (item.venues) {
        const rows = alignLitigationVenueRows(normalizeLitigationVenueRows(item.venues), layerContext);
        item.venues = rows;
        item.venue = litigationVenueSummary(rows);
        patch.venues = rows;
        patch.venue = item.venue;
      }
    }
    item.internalNo = prepareLitigationInternalNo(internalNoRule, item.internalNo, {
      filingDate: item.filingDate,
      disputeType: item.disputeType,
      instance: item.instance,
      enforcementStatus: item.enforcementStatus,
      orgCode: internalNoRule.orgCode,
      unfilledFields: item.unfilledFields,
    }, cases.filter((entry) => entry.id !== item.id).map((entry) => entry.internalNo));
    const merged = {...pendingPatches.get(caseId), ...patch};
    pendingPatches.set(caseId, merged);
    pushUndoEntry({kind: 'localPatch', caseId, before, after: cloneLedgerCase(item)});
    syncWorkbookEditControls();
    if (rerender) renderList(false);
  };

  /**
   * 先把还没写入空间的列表修改提交掉。
   * 插入、删除会整表重写并重新加载，不先提交的话，其他行上已选的案件类型和内部案号会被冲掉。
   * @param exceptIds 这些案件即将删除，不必再提交
   */
  const persistPendingPatches = async (exceptIds?: Set<string>): Promise<boolean> => {
    const entries = [...pendingPatches].filter(([caseId]) => !exceptIds?.has(caseId));
    if (!entries.length) return true;
    if (overviewSaving || workbookSaving) {
      notice('正在保存，请稍后再试', true);
      return false;
    }
    workbookSaving = true;
    syncWorkbookEditControls();
    try {
      for (const [caseId, patch] of entries) {
        const result = await patchCase(caseId, patch);
        replaceCase(result.case);
      }
      return true;
    } catch (error) {
      notice(error instanceof Error ? error.message : '保存失败', true);
      return false;
    } finally {
      workbookSaving = false;
      syncWorkbookEditControls();
    }
  };

  /**
   * 将待保存列表修改写入空间（Excel 台账文件）。
   */
  const saveWorkbookToSpace = async () => {
    if (overviewSaving || workbookSaving) return;
    window.clearTimeout(saveTimer);
    if (dirty) await saveOverview();
    if (dirty || !pendingPatches.size) return;
    workbookSaving = true;
    const saveBtn = page.querySelector<HTMLButtonElement>('#llSaveWorkbook');
    if (saveBtn) saveBtn.disabled = true;
    try {
      for (const [caseId, patch] of [...pendingPatches]) {
        const result = await patchCase(caseId, patch);
        replaceCase(result.case);
      }
      undoStack.length = 0;
      redoStack.length = 0;
      syncWorkbookEditControls();
      renderList(false);
      notice('台账已保存到空间');
    } catch (error) {
      notice(error instanceof Error ? error.message : '保存失败', true);
    } finally {
      workbookSaving = false;
      syncWorkbookEditControls();
    }
  };

  /**
   * 撤销上一步列表内可编辑操作（仅本人案件）。
   */
  const undoLastListEdit = async () => {
    const entry = undoStack.pop();
    if (!entry) return;
    try {
      if (entry.kind === 'localPatch') {
        replaceCase(cloneLedgerCase(entry.before));
        recomputePendingForCase(entry.caseId);
        redoStack.push(entry);
        renderList(false);
      } else if (entry.kind === 'delete') {
        const result = await api.post<{case: LedgerCase}>(apiUrl(`${base}/cases`), {
          quickInsert: true,
          insertAfterCaseId: entry.insertAfterCaseId,
          restoreCase: entry.snapshot,
          numberingKind: 'ss',
        });
        placeCaseAfter(result.case, entry.insertAfterCaseId);
        serverCasesById.set(result.case.id, cloneLedgerCase(result.case));
        entry.caseId = result.case.id;
        entry.snapshot = cloneLedgerCase(result.case);
        redoStack.push(entry);
        renderList(false);
      } else {
        const kept = await persistPendingPatches(new Set([entry.caseId]));
        if (!kept) {
          undoStack.push(entry);
          return;
        }
        await api.delete(apiUrl(`${base}/cases/${encodeURIComponent(entry.caseId)}`));
        cases = cases.filter((item) => item.id !== entry.caseId);
        pendingPatches.delete(entry.caseId);
        serverCasesById.delete(entry.caseId);
        checked.delete(entry.caseId);
        if (selected === entry.caseId) selected = '';
        redoStack.push(entry);
        renderList(false);
      }
      syncWorkbookEditControls();
    } catch (error) {
      undoStack.push(entry);
      notice(error instanceof Error ? error.message : '撤销失败', true);
      syncWorkbookEditControls();
    }
  };

  /**
   * 恢复刚刚撤销的那一步。
   */
  const redoLastListEdit = async () => {
    const entry = redoStack.pop();
    if (!entry) return;
    try {
      if (entry.kind === 'localPatch') {
        if (!cases.some((item) => item.id === entry.caseId)) {
          redoStack.push(entry);
          notice('这一步依赖的案件已经不在列表里', true);
          return;
        }
        replaceCase(cloneLedgerCase(entry.after));
        recomputePendingForCase(entry.caseId);
        undoStack.push(entry);
        renderList(false);
      } else if (entry.kind === 'delete') {
        await api.delete(apiUrl(`${base}/cases/${encodeURIComponent(entry.caseId)}`));
        cases = cases.filter((item) => item.id !== entry.caseId);
        pendingPatches.delete(entry.caseId);
        serverCasesById.delete(entry.caseId);
        checked.delete(entry.caseId);
        if (selected === entry.caseId) selected = '';
        undoStack.push(entry);
        renderList(false);
      } else {
        const result = await api.post<{case: LedgerCase}>(apiUrl(`${base}/cases`), {
          quickInsert: true,
          insertAfterCaseId: entry.insertAfterCaseId,
          numberingKind: 'ss',
        });
        placeCaseAfter(result.case, entry.insertAfterCaseId);
        serverCasesById.set(result.case.id, cloneLedgerCase(result.case));
        if (entry.caseId !== result.case.id) {
          for (const pending of redoStack) remapLitigationHistoryId(pending, entry.caseId, result.case.id);
        }
        entry.caseId = result.case.id;
        entry.snapshot = cloneLedgerCase(result.case);
        selected = result.case.id;
        detailCaseId = '';
        undoStack.push(entry);
        renderList(false);
      }
      syncWorkbookEditControls();
    } catch (error) {
      redoStack.push(entry);
      notice(error instanceof Error ? error.message : '恢复失败', true);
      syncWorkbookEditControls();
    }
  };

  /**
   * 当前表格可见列（案号与案由始终显示；序号固定在勾选列后，不参与字段选择）。
   */
  const visibleListFields = () => listFields.filter((field) => field.id !== 'seqNo' && (field.id === 'internalNo' || field.id === 'title' || field.id === 'caseName' || resolveListColumnEditField(field) === 'caseName' || !hiddenFieldIds.has(field.id)));
  const detailSheetColumns = () => sheetColumns?.[listSheet] ?? [];

  /**
   * 当前工作表列数（案件总表含插入列、勾选列与序号列）。
   */
  const sheetColumnCount = () => {
    if (listSheet === 'cases') return visibleListFields().length + 3;
    return detailSheetColumns().length;
  };

  /**
   * 表格空行占位。
   * @param message 提示文案
   */
  const emptyRow = (message: string) => `<tr><td colspan="${sheetColumnCount()}" class="legacy-empty-cell">${message}${!cases.length && listSheet === 'cases' && access.canCreate ? ` <button type="button" class="ll-row-insert-btn" data-action="add-case" aria-label="添加案件">${ledgerPlusIcon()}</button>` : ''}</td></tr>`;

  /**
   * 打开或关闭案件详情抽屉。
   * @param open 是否打开
   */
  const setDrawerOpen = (open: boolean) => {
    detail.hidden = !open;
    detail.classList.toggle('is-open', open);
    page.classList.toggle('show-detail', open);
    if (!open) detail.setAttribute('aria-hidden', 'true');
    else detail.removeAttribute('aria-hidden');
  };

  /**
   * 关闭案件详情抽屉，不再自动打开当前选中行。
   */
  const dismissDetail = () => {
    detailCaseId = '';
    setDrawerOpen(false);
    renderList(false);
  };

  // Capture before table actions: hide without rebuilding their clicked target.
  // Portal menus/dialogs belong to the drawer interaction, not the outside page.
  const dismissDetailOnOutsideClick = (event: MouseEvent) => {
    if (!page.isConnected) {
      document.removeEventListener('click', dismissDetailOnOutsideClick, true);
      return;
    }
    // Existing drawer adapters forward clicks to table editors programmatically.
    // Those internal actions must not be mistaken for a user's outside click.
    if (!event.isTrusted || detail.hidden || !detail.classList.contains('is-open')) return;
    const hit = event.target instanceof Element ? event.target : null;
    if (!hit || detail.contains(hit) || event.composedPath().includes(detail)) return;
    if (hit.closest('dialog, [role="dialog"], [role="listbox"], .ll-cell-choice-menu, .ll-layer-menu, [data-litigation-cause-suggestions], [data-litigation-court-suggestions]')) return;
    // A non-focusable outside target does not reliably commit change-on-blur
    // textareas before display:none. Let the existing editor save first.
    const active = document.activeElement;
    if (active instanceof HTMLElement && detail.contains(active)) active.blur();
    const progressCase = cases.find(entry => entry.id === detailCaseId);
    detail.querySelectorAll<HTMLTextAreaElement>('textarea[data-progress-edit="content"]').forEach(area => {
      const row = area.closest<HTMLElement>('[data-progress-id]');
      const saved = progressCase?.progressEntries.find(entry => entry.id === row?.dataset.progressId);
      if (row && (saved || area.value.trim()) && saved?.content !== area.value.trim()) {
        area.dispatchEvent(new Event('change', {bubbles: true}));
      }
    });
    // Snapshot valid edits synchronously before any outside action can open another case.
    if (dirty) void saveOverview();
    detailCaseId = '';
    setDrawerOpen(false);
  };
  document.addEventListener('click', dismissDetailOnOutsideClick, true);

  /**
   * 新建空案件并在列表中编辑，不打开右侧抽屉。
   * @param insertAfterCaseId 参照行 ID；省略则追加在本人区段末尾
   */
  const createInlineCase = async (insertAfterCaseId?: string, focusTitle = true): Promise<string | undefined> => {
    if (!access.canCreate) return;
    const kept = await persistPendingPatches();
    if (!kept) return;
    const afterId = insertAfterCaseId && cases.some((entry) => entry.id === insertAfterCaseId)
      ? insertAfterCaseId
      : undefined;
    const result = await api.post<{case: LedgerCase}>(apiUrl(`${base}/cases`), {
      quickInsert: true,
      insertAfterCaseId: afterId,
      numberingKind: 'ss',
    });
    placeCaseAfter(result.case, afterId);
    serverCasesById.set(result.case.id, cloneLedgerCase(result.case));
    pushUndoEntry({kind: 'create', caseId: result.case.id, snapshot: cloneLedgerCase(result.case), insertAfterCaseId: afterId});
    selected = result.case.id;
    detailCaseId = '';
    rememberCase(selected);
    setDrawerOpen(false);
    syncWorkbookEditControls();
    renderList(false);
    if (focusTitle) {
      window.requestAnimationFrame(() => {
        const row = rows.querySelector<HTMLTableRowElement>(`tr[data-id="${CSS.escape(result.case.id)}"]`);
        const titleCell = row?.querySelector<HTMLElement>('[data-inplace-field="title"]');
        titleCell?.click();
      });
    }
    return result.case.id;
  };

  /**
   * 同步批量删除按钮。未勾选案件时不可点。
   */
  const syncDelete = () => {
    if (!batchDelete) return;
    batchDelete.disabled = !checked.size;
    batchDelete.textContent = `删除所选 ${checked.size}`;
  };

  /**
   * 在表格上方显示一行说明。
   * @param message 文案
   * @param error 是否为失败
   */
  const showNotice = (message: string, error = false) => {
    const box = page.querySelector<HTMLElement>('#llNotice')!;
    clearTimeout(noticeTimer);
    box.textContent = message;
    box.classList.toggle('is-error', error);
    box.hidden = false;
    noticeTimer = window.setTimeout(() => { box.hidden = true; }, 6000);
  };
  /**
   * 出错时提示。保存、删除、导出等完成提示不显示。
   * @param message 文案
   * @param error 是否为失败
   */
  const notice = (message: string, error = false) => {
    if (!error) return;
    showNotice(message, true);
  };

  /**
   * 这一列在列表里能看见的整段文字。上下两行的格子合成一段，不拆开筛。
   * @param item 案件
   * @param field 列表列
   */
  const columnFilterText = (item: LedgerCase, field: ListColumnField): string => {
    const storageField = resolveListColumnStorageField(field) || resolveListColumnEditField(field);
    const editField = resolveListColumnEditField(field) || storageField;
    if (storageField === 'internalNo' || editField === 'internalNo') return litigationCaseShowsInternalNo(item) ? item.internalNo || '' : '';
    if (storageField === 'courtNo' || editField === 'courtNo') {
      return resolveLitigationCourtNumberRows(item).flatMap((row) => {
        const value = row.value.trim();
        if (!value) return [];
        const label = LITIGATION_COURT_NUMBER_LAYERS.find((layer) => layer.id === row.layer)?.label || '';
        return [`${label} ${value}`.trim()];
      }).join('\n');
    }
    if (storageField === 'plaintiffParty' || editField === 'plaintiffParty' || storageField === 'opponent' || editField === 'opponent') {
      const role = storageField === 'opponent' || editField === 'opponent' ? 'opponent' : 'plaintiff';
      return partyLayerRows(item.parties, role, partyLayersForListItem(item)).map((row) => `${partyLayerLabel(role, row.layer)} ${row.name}`.trim()).join('\n');
    }
    if (storageField === 'venue' || editField === 'venue') {
      return resolveLitigationVenueRows(item).flatMap((row) => {
        const value = row.value.trim();
        if (!value) return [];
        const label = LITIGATION_VENUE_LAYERS.find((layer) => layer.id === row.layer)?.label || '';
        return [`${label} ${value}`.trim()];
      }).join('\n');
    }
    if (storageField === 'claimSummary' || editField === 'claimSummary') return formatLitigationClaimCell(item);
    if (storageField === 'resultSummary' || editField === 'resultSummary') return formatLitigationResultCell(item);
    if (storageField === 'involvedAmount' || editField === 'involvedAmount') return formatLitigationInvolvedAmountCell(item);
    if (storageField === 'awardedAmount' || editField === 'awardedAmount') return formatLitigationAwardedAmountCell(item);
    if (storageField === 'instance' || editField === 'instance') {
      const shown = litigationShownInstance(item);
      return shown ? INSTANCE_LABELS[shown] : '';
    }
    if (storageField === 'relatedCourtNo' || editField === 'relatedCourtNo') return item.unfilledFields?.includes('relatedCourtNo') ? '' : String(item.relatedCourtNo || '');
    if (storageField === 'currentStage' || editField === 'currentStage') {
      if (item.unfilledFields?.includes('currentStage') && !String(item.currentStage || '').trim()) return '';
      const stage = item.currentStage || currentStageName(item);
      return stageSelectValue(item, stage, stageOptionsForCase(item, cases));
    }
    if (storageField === 'nextDeadline' || editField === 'nextDeadline') return item.closed ? '已结案' : formatDate(nextDeadlineDate(item));
    if (storageField === 'latestProgress' || editField === 'latestProgress') {
      const entries = sortedLitigationProgress(item.progressEntries).filter((entry) => entry.content.trim());
      const latest = entries[entries.length - 1];
      if (!latest) return '';
      const date = formatProgressLongDate(latest.date || latest.createdAt.slice(0, 10));
      const content = latest.content.trim();
      return date ? `${date}\u3000${content}` : content;
    }
    if (editField && item.unfilledFields?.includes(editField)) return '';
    return litigationEditableCellValue(item, storageField || editField);
  };

  /**
   * 格子里分行的分类和具体内容。没有内部分类的列返回空。
   * @param item 案件
   * @param field 列表列
   */
  const columnLayerParts = (item: LedgerCase, field: ListColumnField): Array<{category: string; value: string}> | null => {
    const storageField = resolveListColumnStorageField(field) || resolveListColumnEditField(field);
    const editField = resolveListColumnEditField(field) || storageField;
    const id = storageField || editField;
    if (id === 'courtNo') {
      return resolveLitigationCourtNumberRows(item).flatMap((row) => {
        const value = row.value.trim();
        if (!value) return [];
        return [{category: LITIGATION_COURT_NUMBER_LAYERS.find((layer) => layer.id === row.layer)?.label || '案号', value}];
      });
    }
    if (id === 'venue') {
      return resolveLitigationVenueRows(item).flatMap((row) => {
        const value = row.value.trim();
        if (!value) return [];
        return [{category: LITIGATION_VENUE_LAYERS.find((layer) => layer.id === row.layer)?.label || '管辖', value}];
      });
    }
    if (id === 'plaintiffParty' || id === 'opponent') {
      const role = id === 'opponent' ? 'opponent' : 'plaintiff';
      return partyLayerRows(item.parties, role, partyLayersForListItem(item)).flatMap((row) => {
        const value = row.name.trim();
        if (!value) return [];
        return [{category: partyLayerLabel(role, row.layer), value}];
      });
    }
    if (id === 'involvedAmount') {
      const labels = litigationInvolvedAmountLabels(item);
      const parts: Array<{category: string; value: string}> = [];
      if (item.principalAmount && item.principalAmount > 0) parts.push({category: labels.primary, value: String(item.principalAmount)});
      if (!labels.single && labels.counter && item.counterclaimAmount && item.counterclaimAmount > 0) {
        parts.push({category: labels.counter, value: String(item.counterclaimAmount)});
      }
      return parts;
    }
    if (id === 'awardedAmount') {
      const labels = litigationAwardedAmountLabels(item, activeFieldRuleOverrides?.awardedAmount?.rowLabels);
      const parts: Array<{category: string; value: string}> = [];
      if (item.awardedAmount && item.awardedAmount > 0) parts.push({category: labels.primary, value: String(item.awardedAmount)});
      if (!labels.single && labels.counter && item.counterclaimAwardedAmount && item.counterclaimAwardedAmount > 0) {
        parts.push({category: labels.counter, value: String(item.counterclaimAwardedAmount)});
      }
      return parts;
    }
    if (id === 'claimSummary') {
      const kind = deriveLitigationNumberingKind(item);
      const parts: Array<{category: string; value: string}> = [];
      const claim = String(item.claimSummary || '').trim();
      if (claim) parts.push({category: kind === 'zx' ? '申请执行标的' : '原告请求', value: claim});
      const counter = String(item.counterclaimSummary || '').trim();
      if (kind !== 'zx' && counter) parts.push({category: '反请求', value: counter});
      return parts;
    }
    if (id === 'resultSummary') {
      const labels = litigationJudgmentResultLabels(item);
      const parts: Array<{category: string; value: string}> = [];
      const primary = String(item.resultSummary || '').trim();
      if (primary) parts.push({category: labels.primary, value: primary});
      const counter = String(item.counterclaimResult || '').trim();
      if (!labels.single && labels.counter && counter) parts.push({category: labels.counter, value: counter});
      return parts;
    }
    return null;
  };

  /**
   * 筛选按钮上的小标记，以及表头标记是否处于展开状态。
   */
  const syncColumnFilterButton = () => {
    const button = page.querySelector<HTMLButtonElement>('#llColumnFilter');
    if (!button) return;
    button.classList.toggle('is-armed', columnFilterArmed);
    button.classList.toggle('is-active', columnFilters.size > 0);
    button.setAttribute('aria-pressed', columnFilterArmed ? 'true' : 'false');
  };

  /**
   * 关掉表头上的列筛选菜单。
   */
  const closeColumnFilterMenu = () => {
    columnFilterMenu?.remove();
    columnFilterMenu = null;
    page.querySelectorAll<HTMLButtonElement>('.ll-col-filter.is-open').forEach((button) => {
      button.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
    });
  };

  const filtered = () => {
    const query = search.value.trim().toLocaleLowerCase();
    return cases.filter((item) => {
      const hay = `${item.internalNo} ${item.courtNo} ${item.title} ${item.venue} ${item.handler} ${primaryOpponentName(item.parties)}`.toLocaleLowerCase();
      if (query && !hay.includes(query)) return false;
      for (const [fieldId, allowed] of columnFilters) {
        const field = listFields.find((entry) => entry.id === fieldId);
        if (!field) continue;
        const parts = columnLayerParts(item, field);
        if (parts) {
          const tokens = parts.length
            ? parts.flatMap((part) => [columnFilterKey(`${part.category}\n${part.value}`), columnFilterKey(`${part.category} ${part.value}`.trim())])
            : [COLUMN_FILTER_BLANK];
          if (!tokens.some((token) => allowed.has(token))) return false;
          continue;
        }
        if (!allowed.has(columnFilterKey(columnFilterText(item, field)))) return false;
      }
      return true;
    });
  };

  const rememberCase = (id: string) => {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('case', id);
    else url.searchParams.delete('case');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  const replaceCase = (item: LedgerCase) => {
    cases = dedupeLitigationCases([...cases.filter((entry) => entry.id !== item.id), item]);
    cases.sort((left, right) => (
      left.participantOrder - right.participantOrder
      || left.rowOrder - right.rowOrder
      || left.createdAt.localeCompare(right.createdAt)
    ));
  };

  /**
   * 把新案件放到参照行的下一行，并重排该填写人后面的序号。
   * @param created 新案件
   * @param afterId 参照行；省略则追加到末尾
   */
  const placeCaseAfter = (created: LedgerCase, afterId?: string) => {
    if (!afterId) {
      replaceCase(created);
      return;
    }
    const owned = cases.filter((entry) => entry.ownerUserId === created.ownerUserId && entry.id !== created.id);
    const afterIndex = owned.findIndex((entry) => entry.id === afterId);
    const sequence = afterIndex < 0
      ? [...owned, created]
      : [...owned.slice(0, afterIndex + 1), created, ...owned.slice(afterIndex + 1)];
    sequence.forEach((entry, index) => {
      entry.rowOrder = index + 1;
    });
    const others = cases.filter((entry) => entry.ownerUserId !== created.ownerUserId);
    cases = dedupeLitigationCases([...others, ...sequence]);
    cases.sort((left, right) => (
      left.participantOrder - right.participantOrder
      || left.rowOrder - right.rowOrder
      || left.createdAt.localeCompare(right.createdAt)
    ));
  };

  const collectOverview = (): Record<string, unknown> | null => {
    const form = detail.querySelector<HTMLFormElement>('#llOverviewForm');
    if (!form) return null;
    const data = new FormData(form);
    const parties: LitigationParty[] = [...form.querySelectorAll<HTMLElement>('[data-party]')].map((row) => ({
      id: row.dataset.party || '',
      role: (row.querySelector<HTMLSelectElement>('[data-party-role]')?.value || 'our') as LitigationPartyRole,
      name: row.querySelector<HTMLInputElement>('[data-party-name]')?.value.trim() || '',
      contact: row.querySelector<HTMLInputElement>('[data-party-contact]')?.value.trim() || '',
    }));
    const courtNumbers = readCourtNumberRows(form);
    const venues = readVenueRows(form);
    const amounts = readInvolvedAmountPatch(form);
    const overviewItem = cases.find((entry) => entry.id === form.dataset.caseId);
    const closureOpen = overviewItem
      ? litigationDisplayedStageIsClosed(overviewItem, overviewItem.currentStage || currentStageName(overviewItem))
      : false;
    const awards = closureOpen ? readAwardedAmountPatch(form) : null;
    if (!amounts || (closureOpen && !awards)) {
      notice('金额必须为非负数字，单位为元', true);
      return null;
    }
    const patch: Record<string, unknown> = {
      internalNo: String(data.get('internalNo') || ''),
      courtNo: litigationCourtNumberSummary(courtNumbers),
      courtNumbers,
      title: String(data.get('title') || ''),
      disputeType: String(data.get('disputeType') || 'litigation'),
      instance: String(data.get('instance') || 'first'),
      position: String(data.get('position') || 'plaintiff'),
      ourEntity: String(data.get('ourEntity') || ''),
      venue: litigationVenueSummary(venues),
      venues,
      filingDate: String(data.get('filingDate') || ''),
      claimSummary: form.querySelector<HTMLTextAreaElement>('[data-claim-part="claim"]')?.value.trim() || '',
      ...(form.querySelector('.ll-claim-kind.is-wide') ? {} : {counterclaimSummary: form.querySelector<HTMLTextAreaElement>('[data-claim-part="counter"]')?.value.trim() || ''}),
      ...amounts,
      amount: Number(data.get('amount') || 0),
      currency: String(data.get('currency') || 'CNY'),
      preservation: form.querySelector<HTMLInputElement>('[name="preservation"]')?.checked === true,
      handler: String(data.get('handler') || ''),
      counsel: String(data.get('counsel') || ''),
      counselOrg: String(data.get('counselOrg') || ''),
      ...(closureOpen ? {
        resultSummary: form.querySelector<HTMLTextAreaElement>('.ll-result-layers [data-result-part="primary"]')?.value.trim() || '',
        ...(form.querySelector('.ll-result-layers.is-single') ? {} : {counterclaimResult: form.querySelector<HTMLTextAreaElement>('.ll-result-layers [data-result-part="counter"]')?.value.trim() || ''}),
      } : {}),
      ...(closureOpen && awards ? (form.querySelector('.ll-award-layers.is-single') ? {awardedAmount: awards.awardedAmount} : awards) : {}),
      effectiveDate: String(data.get('effectiveDate') || ''),
      ...(closureOpen ? {
        closeMode: String(data.get('closeMode') || ''),
        litigationClosedAt: String(data.get('litigationClosedAt') || ''),
      } : {}),
      enforcementStatus: String(data.get('enforcementStatus') || 'none'),
      enforcementClosedAt: String(data.get('enforcementClosedAt') || ''),
      parties,
    };
    const item = cases.find((entry) => entry.id === form.dataset.caseId);
    for (const field of item?.unfilledFields || []) {
      const name = field === 'closedFlag' ? 'closedFlag' : field;
      if (data.has(name) && !String(data.get(name) || '').trim()) delete patch[field === 'closedFlag' ? 'closed' : field];
    }
    return patch;
  };

  const saveOverview = async () => {
    if (overviewSaving) return;
    const form = detail.querySelector<HTMLFormElement>('#llOverviewForm');
    const item = cases.find((entry) => entry.id === form?.dataset.caseId);
    if (!item || !form) return;
    const typedTitle = String(new FormData(form).get('title') || '').trim();
    const causeCheck = typedTitle ? readLitigationCauseSelection(form) : {ok: true as const, title: ''};
    if (causeCheck.ok === false) {
      dirty = true;
      notice(causeCheck.message, true);
      return;
    }
    const patch = collectOverview();
    if (!patch) return;
    patch.title = causeCheck.title;
    const savingVersion = overviewEditVersion;
    overviewSaving = true;
    syncWorkbookEditControls();
    try {
      const result = await patchCase(item.id, patch);
      replaceCase(result.case);
      dirty = overviewEditVersion !== savingVersion;
      notice('已自动保存');
      renderList(false);
    } catch (error) {
      dirty = true;
      notice(error instanceof Error ? error.message : '保存失败', true);
    } finally {
      overviewSaving = false;
      syncWorkbookEditControls();
      if (dirty && overviewEditVersion !== savingVersion) saveTimer = window.setTimeout(() => { void saveOverview(); }, 600);
    }
  };

  const scheduleSave = () => {
    dirty = true;
    overviewEditVersion += 1;
    syncWorkbookEditControls();
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => { void saveOverview(); }, 600);
  };

  const onLeave = (event: BeforeUnloadEvent) => {
    if (!dirty && !pendingPatches.size && !overviewSaving && !workbookSaving) return;
    event.preventDefault();
    event.returnValue = '';
  };
  window.addEventListener('beforeunload', onLeave);

  const renderDetail = (item?: LedgerCase) => {
    if (!item) {
      setDrawerOpen(false);
      detail.innerHTML = `<button type="button" class="ll-drawer-close" data-action="dismiss-detail" aria-label="关闭">×</button><div class="la-empty">选择案件查看详情</div>`;
      return;
    }
    setDrawerOpen(true);
    const tabs: Array<[DetailTab, string]> = [['overview', '概况'], ['progress', '进展'], ['procedure', '程序'], ['deadlines', '期限'], ['materials', '材料'], ['contracts', '关联合同']];
    const tone = deadlineTone(nextDeadlineDate(item), item.closed);
    const editable = canEditCaseInList(item);
    detail.innerHTML = `<button type="button" class="ll-drawer-close" data-action="dismiss-detail" aria-label="关闭">×</button>
      <div class="la-detail-bar"><button type="button" class="la-mobile-back" data-action="dismiss-detail">${icon('arrow-left')}关闭</button>
      <div class="la-project-heading"><div class="la-title-block"><h2>${escapeHtml(item.title || '未命名案件')}</h2>${litigationCaseShowsInternalNo(item) ? `<span class="ll-case-no">${escapeHtml(item.internalNo)}</span>` : ''}${access.isAdmin ? `<span class="ll-owner-badge">${escapeHtml(item.ownerName)}</span>` : ''}</div></div>
      <nav class="ll-tabs">${tabs.map(([id, label]) => `<button type="button" class="${tab === id ? 'is-active' : ''}" data-tab="${id}">${label}</button>`).join('')}</nav>
    </div>
    <div class="la-detail-body">${tab === 'overview' ? overviewForm(item, editable, internalNoRule) : tab === 'progress' ? progressView(item, editable) : tab === 'procedure' ? procedureView(item, editable) : tab === 'deadlines' ? deadlineView(item, tone) : tab === 'materials' ? materialsView(item, editable) : contractsView(item, contracts, editable)}</div>`;
    detail.scrollTop = 0;
    const overview = detail.querySelector<HTMLFormElement>('#llOverviewForm');
    if (overview) overview.dataset.caseId = item.id;
    if (tab === 'overview') fitClaimTextareas(detail);
    if (tab === 'overview' && editable) {
      bindLitigationCausePicker(detail);
      bindLitigationCourtPicker(detail, notice);
    }
  };

  /**
   * 案件总表列键顺序（与 colgroup、表头一致）。
   * @param fields 可见数据列
   */
  const caseTableColumnKeys = (fields: Array<{id: string}>) => ['insert', 'select', 'index', ...fields.map((field) => field.id)];

  /**
   * 读取列宽（像素）。
   * @param key 列键
   */
  const caseColWidth = (key: string) => {
    if (key === 'insert') return LL_CASE_COL_WIDTH_DEFAULT.insert;
    const field = listFields.find((item) => item.id === key);
    const labelWidth = field ? [...field.label].reduce((width, char) => width + (/[^\x00-\xff]/.test(char) ? 12 : 7), 28) : 0;
    return columnWidths[key] ?? Math.max(labelWidth, LL_CASE_COL_WIDTH_DEFAULT[field?.sourceField || key] ?? 76);
  };

  /** 持久化用户拖拽后的列宽。 */
  const persistColumnWidths = () => {
    localStorage.setItem(columnWidthStorageKey, JSON.stringify(columnWidths));
  };

  /**
   * 同步 colgroup，供 table-layout:fixed 与拖拽改宽使用。
   * @param fields 可见数据列
   */
  /**
   * 表格总宽等于各列之和，避免百分比宽度把表头和表体拆成两条边。
   * @param table 案件总表
   */
  const syncCaseTableWidth = (table: HTMLElement) => {
    const total = [...table.querySelectorAll<HTMLElement>('colgroup col')].reduce((sum, col) => sum + (Number.parseFloat(col.style.width) || 0), 0);
    if (!total) return;
    table.style.width = `${total}px`;
    table.style.minWidth = `${total}px`;
  };

  const updateCaseTableColgroup = (fields: Array<{id: string}>) => {
    const colgroup = page.querySelector<HTMLElement>('#llColGroup');
    if (!colgroup) return;
    if (listSheet !== 'cases') {
      colgroup.innerHTML = '';
      return;
    }
    colgroup.innerHTML = caseTableColumnKeys(fields).map((key) => (
      `<col data-col-key="${escapeHtml(key)}" style="width:${caseColWidth(key)}px">`
    )).join('');
    const table = colgroup.closest('table');
    if (table) syncCaseTableWidth(table);
  };

  /**
   * 带拖拽手柄的表头单元格。
   * @param label 标题文案
   * @param colKey 列键
   * @param extraClass 附加 class
   */
  /**
   * 案件总表表头。筛选标记只加在数据列上，避开拖列宽的右缘。
   * @param label 表头文字
   * @param colKey 列宽键
   * @param extraClass 附加 class
   * @param filterFieldId 可筛选时的字段 id；空字符串表示这一列不筛选
   */
  const renderCaseTableHeaderCell = (label: string, colKey: string, extraClass = '', filterFieldId = '') => {
    const filtering = Boolean(filterFieldId) && columnFilters.has(filterFieldId);
    const opened = columnFilterMenu?.dataset.field === filterFieldId;
    const filter = columnFilterArmed && filterFieldId
      ? `<button type="button" class="ll-col-filter${filtering ? ' is-active' : ''}${opened ? ' is-open' : ''}" data-col-filter="${escapeHtml(filterFieldId)}" aria-label="筛选${escapeHtml(label)}" aria-expanded="${opened ? 'true' : 'false'}" aria-haspopup="dialog"><svg viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" d="M1.5 2.5h13l-5 6.1V13l-3-1.5V8.6z"/></svg></button>`
      : '';
    return `<th scope="col" class="ll-col-header ${extraClass}" title="${escapeHtml(label)}" data-col-key="${escapeHtml(colKey)}" style="width:${caseColWidth(colKey)}px"><span class="ll-col-label">${label ? escapeHtml(label) : ''}</span>${filter}<span class="ll-col-resizer" data-col-key="${escapeHtml(colKey)}" role="separator" aria-orientation="vertical" aria-label="调整列宽"></span></th>`;
  };

  let columnResizeBound = false;

  /** 绑定表头列宽拖拽（仅绑定一次）。 */
  const bindCaseTableColumnResize = () => {
    if (columnResizeBound) return;
    const table = page.querySelector<HTMLElement>('.ll-case-table');
    if (!table) return;
    columnResizeBound = true;
    let activeKey = '';
    let startX = 0;
    let startWidth = 0;
    const colForKey = (key: string) => table.querySelector<HTMLElement>(`colgroup col[data-col-key="${CSS.escape(key)}"]`);
    const onMove = (event: MouseEvent) => {
      if (!activeKey) return;
      const next = Math.min(640, Math.max(48, startWidth + event.clientX - startX));
      columnWidths[activeKey] = next;
      const col = colForKey(activeKey);
      if (col) col.style.width = `${next}px`;
      const header = table.querySelector<HTMLElement>(`thead th[data-col-key="${CSS.escape(activeKey)}"]`);
      if (header) header.style.width = `${next}px`;
      syncCaseTableWidth(table);
    };
    const onUp = () => {
      if (!activeKey) return;
      persistColumnWidths();
      activeKey = '';
      document.body.classList.remove('ll-col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    table.addEventListener('mousedown', (event) => {
      const handle = (event.target as HTMLElement).closest<HTMLElement>('.ll-col-resizer');
      if (!handle?.dataset.colKey || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      activeKey = handle.dataset.colKey;
      startX = event.clientX;
      startWidth = caseColWidth(activeKey);
      document.body.classList.add('ll-col-resizing');
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });
  };

  const renderTableHead = () => {
    const head = page.querySelector<HTMLElement>('#llHeadRow')!;
    if (listSheet === 'cases') {
      const fields = visibleListFields();
      updateCaseTableColgroup(fields);
      head.innerHTML = [
        `<th scope="col" class="col-insert" data-col-key="insert" style="width:${caseColWidth('insert')}px" aria-label="插入行">${access.canCreate ? `<button type="button" class="ll-row-insert-btn" data-action="add-case" aria-label="添加案件">${ledgerPlusIcon()}</button>` : ''}</th>`,
        `<th scope="col" class="col-select ll-col-header" data-col-key="select" aria-label="选择"><input type="checkbox" id="llSelectAll" aria-label="全选"><span class="ll-col-resizer" data-col-key="select" role="separator" aria-orientation="vertical" aria-label="调整列宽"></span></th>`,
        renderCaseTableHeaderCell(listFields.find((field) => field.id === 'seqNo')?.label || '序号', 'index', 'col-index'),
        ...fields.map((field) => renderCaseTableHeaderCell(field.label, field.id, '', field.id)),
      ].join('');
      bindCaseTableColumnResize();
      return;
    }
    const columns = detailSheetColumns();
    const widths = columns.map((field) => detailSheetColWidth(field.sourceField));
    const colgroup = page.querySelector<HTMLElement>('#llColGroup');
    const table = head.closest('table');
    if (colgroup && table) {
      colgroup.innerHTML = columns.map((field, index) => `<col data-col-key="${escapeHtml(field.sourceField || field.id)}" style="width:${widths[index]}px">`).join('');
      const total = widths.reduce((sum, width) => sum + width, 0);
      table.style.width = `${total}px`;
      table.style.minWidth = `${total}px`;
    }
    head.innerHTML = columns.map((field) => `<th>${escapeHtml(field.label)}</th>`).join('');
  };

  /**
   * 明细表单元格文案。
   * @param item 所属案件
   * @param fieldId 列 ID
   * @param party 当事人行
   * @param node 程序节点
   * @param progress 进展记录
   */
  const sheetCell = (
    item: LedgerCase,
    fieldId: string,
    party?: LedgerCase['parties'][number],
    node?: LedgerCase['nodes'][number],
    progress?: NonNullable<LedgerCase['progressEntries']>[number],
  ) => {
    if (fieldId === 'internalNo') return litigationCaseShowsInternalNo(item) ? escapeHtml(item.internalNo || '') : '';
    if (party) {
      if (fieldId === 'partyRole') return escapeHtml(PARTY_ROLE_LABELS[party.role] || party.role);
      if (fieldId === 'partyName') return escapeHtml(party.name || '—');
      if (fieldId === 'partyContact') return escapeHtml(party.contact || '—');
      if (fieldId === 'partyPrimary') {
        const first = item.parties.find((entry) => entry.role === party.role);
        return first?.id === party.id ? '是' : '否';
      }
    }
    if (node) {
      if (fieldId === 'nodeName') return escapeHtml(node.name || '—');
      if (fieldId === 'plannedDate') return escapeHtml(formatDate(node.plannedDate));
      if (fieldId === 'completedDate') return escapeHtml(formatDate(node.completedDate));
      if (fieldId === 'keyDeadline') return node.keyDeadline ? '是' : '否';
      if (fieldId === 'deadlineKind') return escapeHtml(DEADLINE_KIND_LABELS[node.deadlineKind] || node.deadlineKind || '—');
      if (fieldId === 'nodeNote') return escapeHtml(node.note || '—');
    }
    if (progress) {
      if (fieldId === 'progressDate') return escapeHtml(formatDate(progress.date));
      if (fieldId === 'progressContent') return escapeHtml(progress.content || '—');
      if (fieldId === 'progressAuthor') return escapeHtml(item.ownerName || '—');
    }
    return '—';
  };

  /**
   * 是否可在该行下方插入新案件（仅本人未结案行）。
   * @param item 参照行
   */
  const canInsertCaseAfter = (item: LedgerCase): boolean => (
    listSheet === 'cases' && access.canCreate && item.ownerUserId === access.currentUserId
  );

  let insertMenu: HTMLElement | null = null;
  let insertMenuCaseId = '';
  /** 下一次 Excel 导入要插到哪一行下面。台账文件里的导入会先清掉它。 */
  let importBelowCaseId = '';
  /** 折角菜单复制下来的案件，按表格从上到下排列。 */
  let copiedRowIds: string[] = [];

  /**
   * 记住当前勾选的行，供之后在另一行的角标菜单里插入。
   */
  const copySelectedRows = () => {
    const selected = cases.filter((item) => checked.has(item.id));
    if (!selected.length) {
      notice('请先勾选要复制的行', true);
      return;
    }
    copiedRowIds = selected.map((item) => item.id);
    showNotice(`已复制 ${copiedRowIds.length} 行。点另一行的角标，选择「插入复制行」。`);
  };

  /**
   * 把已复制的案件插到参照行下面。每一行都是新案件，内部案号重新取。
   * @param afterId 参照行
   */
  const insertCopiedRows = async (afterId: string) => {
    if (!copiedRowIds.length) {
      notice('还没有复制的行', true);
      return;
    }
    if (!access.canCreate) return;
    const kept = await persistPendingPatches();
    if (!kept) return;
    let anchor = afterId;
    const sourceIds = [...copiedRowIds];
    for (const sourceId of sourceIds) {
      const result = await api.post<{case: LedgerCase}>(apiUrl(`${base}/cases`), {
        quickInsert: true,
        insertAfterCaseId: anchor,
        copyFromCaseId: sourceId,
        numberingKind: 'ss',
      });
      placeCaseAfter(result.case, anchor);
      serverCasesById.set(result.case.id, cloneLedgerCase(result.case));
      pushUndoEntry({kind: 'create', caseId: result.case.id, snapshot: cloneLedgerCase(result.case), insertAfterCaseId: anchor});
      anchor = result.case.id;
    }
    selected = anchor;
    detailCaseId = '';
    rememberCase(selected);
    setDrawerOpen(false);
    syncWorkbookEditControls();
    renderList(false);
    showNotice(`已在本行下面插入 ${sourceIds.length} 行`);
  };

  /**
   * 收起插入一行菜单，并去掉角标的展开状态。
   */
  const closeInsertMenu = () => {
    insertMenu?.remove();
    insertMenu = null;
    insertMenuCaseId = '';
    page.querySelectorAll<HTMLButtonElement>('.ll-insert-corner.is-open').forEach((button) => {
      button.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
    });
  };

  /**
   * 删除角标所在的这一行。仅本人或台账管理员可以删。
   * @param caseId 案件
   */
  const deleteCaseRow = async (caseId: string) => {
    const item = cases.find((entry) => entry.id === caseId);
    if (!item || !canDeleteCaseInList(item)) {
      notice('没有可删除的本单位案件，或请联系台账管理员', true);
      return;
    }
    if (!window.confirm('确定删除本行吗？案件登记及关联记录将移除，空间中的材料文件会保留。')) return;
    const snapshot = cloneLedgerCase(item);
    const index = cases.findIndex((entry) => entry.id === item.id);
    const insertAfterCaseId = index > 0 ? cases[index - 1].id : '';
    const kept = await persistPendingPatches(new Set([item.id]));
    if (!kept) return;
    await api.delete(apiUrl(`${base}/cases/${encodeURIComponent(item.id)}`));
    pendingPatches.delete(item.id);
    checked.delete(item.id);
    serverCasesById.delete(item.id);
    cases = cases.filter((entry) => entry.id !== item.id);
    if (selected === item.id) selected = '';
    pushUndoEntry({kind: 'delete', caseId: snapshot.id, snapshot, insertAfterCaseId});
    renderList(false);
  };

  /**
   * 在参照行下面连续插入多行空案件。
   * @param afterId 参照行
   * @param count 行数
   */
  const insertManyBelow = async (afterId: string, count: number) => {
    let anchor = afterId;
    for (let index = 0; index < count; index += 1) {
      const created = await createInlineCase(anchor, index === count - 1);
      if (!created) return;
      anchor = created;
    }
  };

  /**
   * 询问要在本行下面增加几行。
   * @param caseId 参照行
   */
  const openInsertManyDialog = (caseId: string) => {
    const host = page.querySelector<HTMLElement>('#llDialogHost')!;
    host.innerHTML = `<dialog class="la-dialog" open aria-label="增加多行"><header><h2>增加多行</h2><button class="la-dialog-close" type="button" data-close>×</button></header>
      <div class="la-dialog-body"><label class="ll-export-title-field">在本行下面增加<input class="form-input" id="llInsertManyCount" type="number" min="1" max="20" step="1" value="2">行</label>
      <p class="ll-hint">一次最多 20 行。新行加在这一行下面。</p></div>
      <footer><button type="button" class="la-secondary" data-close>取消</button><button type="button" class="la-primary" id="llInsertManyConfirm">确定</button></footer></dialog>`;
    const dialog = host.querySelector('dialog');
    host.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => dialog?.close()));
    host.querySelector<HTMLInputElement>('#llInsertManyCount')?.focus();
    host.querySelector('#llInsertManyConfirm')?.addEventListener('click', () => {
      const count = Math.floor(Number(host.querySelector<HTMLInputElement>('#llInsertManyCount')?.value));
      if (!Number.isFinite(count) || count < 1 || count > 20) {
        notice('请填写 1 到 20 之间的行数', true);
        return;
      }
      dialog?.close();
      void insertManyBelow(caseId, count).catch((error) => notice(error instanceof Error ? error.message : '新增失败', true));
    });
  };

  /**
   * 选择一份 Excel，把其中的新案件插到这一行下面。
   * @param caseId 参照行
   */
  const beginImportBelow = (caseId: string) => {
    if ((dirty || pendingPatches.size) && !window.confirm('当前台账有未保存的修改。导入 Excel 后这些修改不会保留。仍要导入吗？')) return;
    importBelowCaseId = caseId;
    page.querySelector<HTMLInputElement>('#llImportFile')?.click();
  };

  /**
   * 在插入列角标旁打开行操作。再点同一个角标则收起。
   * @param corner 行前格子右下角的角标
   */
  const openInsertMenu = (corner: HTMLButtonElement) => {
    const caseId = corner.dataset.insertCorner || '';
    if (!caseId) return;
    if (insertMenu && insertMenuCaseId === caseId) {
      closeInsertMenu();
      return;
    }
    closeInsertMenu();
    const menu = document.createElement('div');
    menu.className = 'll-insert-menu';
    menu.setAttribute('role', 'menu');
    const paste = copiedRowIds.length
      ? '<button type="button" role="menuitem" data-row-action="paste">插入复制行</button>'
      : '';
    menu.innerHTML = `<button type="button" role="menuitem" data-row-action="insert">插入一行</button>
      ${paste}
      <button type="button" role="menuitem" data-row-action="delete">删除本行</button>
      <button type="button" role="menuitem" data-row-action="many">增加多行</button>
      <button type="button" role="menuitem" data-row-action="import">在本行下导入台账</button>
      <button type="button" role="menuitem" data-row-action="copy">复制所选行</button>`;
    document.body.appendChild(menu);
    const rect = corner.getBoundingClientRect();
    const width = Math.max(menu.offsetWidth, 176);
    const menuHeight = menu.offsetHeight;
    const top = rect.bottom + 4 + menuHeight > window.innerHeight - 8
      ? Math.max(8, rect.top - menuHeight - 4)
      : rect.bottom + 4;
    menu.style.left = `${Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8))}px`;
    menu.style.top = `${top}px`;
    menu.addEventListener('click', (event) => {
      const action = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-row-action]')?.dataset.rowAction;
      if (!action) return;
      closeInsertMenu();
      if (action === 'insert') {
        void createInlineCase(caseId).catch((error) => notice(error instanceof Error ? error.message : '新增失败', true));
        return;
      }
      if (action === 'copy') {
        copySelectedRows();
        return;
      }
      if (action === 'paste') {
        void insertCopiedRows(caseId).catch((error) => notice(error instanceof Error ? error.message : '插入复制行失败', true));
        return;
      }
      if (action === 'delete') {
        void deleteCaseRow(caseId).catch((error) => notice(error instanceof Error ? error.message : '删除失败', true));
        return;
      }
      if (action === 'many') {
        openInsertManyDialog(caseId);
        return;
      }
      beginImportBelow(caseId);
    });
    insertMenu = menu;
    insertMenuCaseId = caseId;
    corner.classList.add('is-open');
    corner.setAttribute('aria-expanded', 'true');
  };

  /**
   * 行前插入列留空。角标放在复选框格子的右下角。
   */
  const renderInsertCell = (): string => '<td class="col-insert"></td>';

  /**
   * 复选框格。可插入的本人行在右下角放角标。
   * @param item 案件行
   * @param deletable 是否可勾选删除
   */
  const renderSelectCell = (item: LedgerCase, deletable: boolean): string => {
    const checkbox = deletable
      ? `<input type="checkbox" data-check="${escapeHtml(item.id)}" ${checked.has(item.id) ? 'checked' : ''} aria-label="选择案件 ${escapeHtml(item.internalNo || item.title)}">`
      : '';
    const corner = canInsertCaseAfter(item)
      ? `<button type="button" class="ll-insert-corner" data-insert-corner="${escapeHtml(item.id)}" aria-label="插入一行" aria-haspopup="menu" aria-expanded="false"></button>`
      : '';
    return `<td class="col-select"><div class="ll-select-cell">${checkbox}${corner}</div></td>`;
  };

  const renderListCell = (item: LedgerCase, column: ListColumnField, stage: string, tone: LitigationDeadlineTone, editable: boolean) => {
    const storageField = resolveListColumnStorageField(column) || resolveListColumnEditField(column);
    const editField = resolveListColumnEditField(column) || storageField;
    const fieldRule = resolveLitigationFieldRule(editField, activeFieldRuleOverrides);
    const readOnly = litigationFieldRuleKindInfo(fieldRule.ruleKind)?.readOnly;
    /**
     * 当前阶段不是「已结案」时，结案方式、结案日、判决/裁决结果和裁判支持金额不提供选择或填写。
     */
    const closureField = storageField === 'awardedAmount' || editField === 'awardedAmount'
      || storageField === 'closeMode' || editField === 'closeMode'
      || storageField === 'closedAt' || editField === 'closedAt'
      || storageField === 'resultSummary' || editField === 'resultSummary';
    if (closureField && !litigationDisplayedStageIsClosed(item, stage)) return '';
    if (storageField === 'internalNo' || editField === 'internalNo') {
      return litigationCaseShowsInternalNo(item) ? escapeHtml(item.internalNo || '') : '';
    }
    if (storageField === 'courtNo' || editField === 'courtNo') {
      return courtNumberFieldsMarkup(item, editable && !readOnly);
    }
    if (storageField === 'claimSummary' || editField === 'claimSummary') {
      return claimFieldsMarkup(item, editable && !readOnly);
    }
    if (storageField === 'resultSummary' || editField === 'resultSummary') {
      return resultFieldsMarkup(item, editable && !readOnly);
    }
    if (storageField === 'involvedAmount' || editField === 'involvedAmount') {
      return involvedAmountFieldsMarkup(item, editable && !readOnly);
    }
    if (storageField === 'awardedAmount' || editField === 'awardedAmount') {
      return awardedAmountFieldsMarkup(item, editable && !readOnly);
    }
    if (storageField === 'instance' || editField === 'instance') {
      const shown = litigationShownInstance(item);
      return escapeHtml(shown ? INSTANCE_LABELS[shown] : '');
    }
    if (storageField === 'relatedCourtNo' || editField === 'relatedCourtNo') {
      const value = item.unfilledFields?.includes('relatedCourtNo') ? '' : String(item.relatedCourtNo || '');
      if (!editable || readOnly) return escapeHtml(value);
      const hint = relatedCourtNoHint(item);
      return `<span class="ll-cell-inplace" data-inplace-case="${escapeHtml(item.id)}" data-inplace-field="relatedCourtNo" data-placeholder="${escapeHtml(hint)}" tabindex="0">${value ? escapeHtml(value) : ''}</span>`;
    }
    if (storageField === 'venue' || editField === 'venue') {
      return venueFieldsMarkup(item, editable && !readOnly);
    }
    if (storageField === 'plaintiffParty' || editField === 'plaintiffParty' || storageField === 'opponent' || editField === 'opponent') {
      const role = storageField === 'opponent' || editField === 'opponent' ? 'opponent' : 'plaintiff';
      return partySideFieldsMarkup(item, role, editable && !readOnly);
    }
    const supported = ['caseName', 'title', 'caseNature', 'courtNo', 'venue', 'handler', 'counsel', 'counselOrg', 'claimSummary', 'resultSummary', 'nonMonetaryResult', 'filingDate', 'amount', 'disputeType', 'instance', 'position', 'ourParty', 'plaintiffParty', 'opponent', 'thirdParty', 'closedFlag', 'closedAt', 'closeMode', 'enforcementStatus', 'currentStage'];
    if (supported.includes(storageField) || supported.includes(editField)) {
      const stageStillBlank = editField === 'currentStage' && item.unfilledFields?.includes('currentStage') && !String(item.currentStage || '').trim();
      const value = stageStillBlank || (editField !== 'currentStage' && item.unfilledFields?.includes(editField))
        ? ''
        : editField === 'currentStage' ? stage : litigationEditableCellValue(item, storageField);
      const stageChoices = editField === 'currentStage' ? stageOptionsForCase(item, cases) : [];
      const closeChoices = editField === 'closeMode' ? [...litigationCloseModeOptions(item)] : [];
      const shownStage = editField === 'currentStage' ? stageSelectValue(item, value, stageChoices) : value;
      if (!editable || readOnly) return escapeHtml(shownStage || '');
      if (fieldRule.ruleKind === 'date' || (fieldRule.ruleKind === 'manual' && ledgerDateField(editField))) {
        return dateCellMarkup(editField, item.id, value);
      }
      const choiceOptions = stageChoices.length
        ? stageChoices
        : closeChoices.length
          ? closeChoices
          : fieldRule.ruleKind === 'choice'
            ? litigationFieldChoices(editField, activeFieldRuleOverrides)
            : [];
      const needsInlineSelect = choiceOptions.length > 0
        || (fieldRule.ruleKind === 'choice' && editField !== 'currentStage');
      if (needsInlineSelect && choiceOptions.length) {
        const options = choiceOptions.map((option) => (
          `<option value="${escapeHtml(option)}" ${option === shownStage ? 'selected' : ''}>${escapeHtml(option)}</option>`
        )).join('');
        return choiceCellMarkup(editField, item.id, column.label || litigationPlanFieldLabel(editField), shownStage, choiceOptions);
      }
      if (editField === 'title' && fieldRule.ruleKind === 'cause-library') {
        return litigationCauseCellMarkup(item.id, value);
      }
      if (editField === 'caseName') return caseNameCellMarkup(item, value, editable && !readOnly);
      const wrap = editField === 'title' || editField === 'claimSummary' || editField === 'resultSummary';
      return `<span class="ll-cell-inplace${wrap ? ' ll-cell-wrap' : ''}" data-inplace-case="${escapeHtml(item.id)}" data-inplace-field="${escapeHtml(editField)}" tabindex="0">${value ? escapeHtml(value) : ''}</span>`;
    }
    if (editField === 'currentStage') {
      if (!editable) return escapeHtml(stage || '');
      const names = stageOptionsForCase(item, cases);
      const shown = stageSelectValue(item, stage, names);
      return choiceCellMarkup('currentStage', item.id, '当前阶段', shown, names);
    }
    if (storageField === 'nextDeadline' || editField === 'nextDeadline') {
      return `<span class="ll-deadline ll-deadline-${tone}">${item.closed ? '已结案' : formatDate(nextDeadlineDate(item))}</span>`;
    }
    if (storageField === 'latestProgress' || editField === 'latestProgress') {
      return progressCellMarkup(item);
    }
    const rawValue = litigationPlanFieldValue(item, storageField);
    const textValue = rawValue === undefined || rawValue === null || rawValue === '' ? '' : String(rawValue);
    if (LL_LIST_INPLACE_TEXT_FIELDS.has(editField) && editable && !readOnly && !item.closed) {
      return `<span class="ll-cell-inplace" data-inplace-case="${escapeHtml(item.id)}" data-inplace-field="${escapeHtml(editField)}" tabindex="0">${textValue ? escapeHtml(textValue) : ''}</span>`;
    }
    return escapeHtml(textValue || '');
  };

  /**
   * 数据列 td 样式（省略号 / 案由换行）。
   * @param column 方案列
   */
  const listDataCellClass = (column: ListColumnField) => {
    const editField = resolveListColumnEditField(column);
    const wrap = editField === 'title' || editField === 'caseName' || editField === 'claimSummary' || editField === 'resultSummary';
    return `ll-data-cell${wrap ? ' ll-cell-wrap' : ''}`;
  };

  let choiceMenu: HTMLElement | null = null;

  /**
   * 关闭当前阶段等字段的选项弹层。
   */
  const closeChoiceMenu = () => {
    choiceMenu?.remove();
    choiceMenu = null;
    page.querySelectorAll<HTMLButtonElement>('.ll-cell-choice.is-open, .ll-layer-choice.is-open, .ll-cell-date.is-open, .ll-progress-date.is-open').forEach((button) => {
      button.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
    });
  };

  /**
   * 管辖层次的下拉。挂在表格外面，四边边框都完整。
   * @param button 层次按钮
   */
  const openLayerMenu = (button: HTMLButtonElement) => {
    closeChoiceMenu();
    let layers: Array<{id: string; label: string}> = [];
    try {
      const parsed = JSON.parse(button.dataset.layerOptions || '[]');
      layers = Array.isArray(parsed) ? parsed.filter((item) => item && typeof item.id === 'string') : [];
    } catch {
      layers = [];
    }
    const current = button.getAttribute('data-venue-kind') || '';
    const menu = document.createElement('div');
    menu.className = 'll-layer-menu';
    menu.setAttribute('role', 'listbox');
    menu.innerHTML = layers.map((layer) => {
      const selected = layer.id === current ? ' is-selected' : '';
      return `<button type="button" class="ll-cell-choice-option${selected}" role="option" data-value="${escapeHtml(layer.id)}">${escapeHtml(layer.label)}</button>`;
    }).join('');
    document.body.appendChild(menu);
    const rect = button.getBoundingClientRect();
    const longest = Math.max(...layers.map((layer) => layer.label.length), 4);
    let width = Math.max(rect.width, longest * 16 + 36);
    const leftLimit = Math.max(8, window.innerWidth - 8);
    menu.style.width = `${width}px`;
    if (menu.scrollWidth > menu.clientWidth) width = Math.min(menu.scrollWidth + 2, leftLimit - 8);
    menu.style.width = `${width}px`;
    const left = Math.min(Math.max(8, rect.left), Math.max(8, leftLimit - width));
    menu.style.width = `${width}px`;
    menu.style.left = `${left}px`;
    const menuHeight = menu.offsetHeight;
    const top = window.innerHeight - rect.bottom - 8 >= menuHeight ? rect.bottom + 2 : Math.max(8, rect.top - menuHeight - 2);
    menu.style.top = `${top}px`;
    button.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    choiceMenu = menu;
    menu.addEventListener('click', (event) => {
      const option = (event.target as HTMLElement).closest<HTMLButtonElement>('.ll-cell-choice-option');
      if (!option) return;
      button.setAttribute('data-venue-kind', option.dataset.value || '');
      button.textContent = option.textContent || '';
      const host = button.closest<HTMLElement>('.ll-venue-layers');
      closeChoiceMenu();
      if (!host) return;
      const venues = readVenueRows(host);
      if (host.closest('#llOverviewForm')) {
        scheduleSave();
        return;
      }
      const caseId = host.closest('tr')?.dataset.id || '';
      if (!caseId) return;
      const item = cases.find((entry) => entry.id === caseId);
      const before = item ? resolveLitigationVenueRows(item) : [];
      const unchanged = Boolean(item) && before.length === venues.length && before.every((row, index) => row.layer === venues[index]?.layer && row.value === venues[index]?.value);
      if (!unchanged) queueLocalListPatch(caseId, {venues, venue: litigationVenueSummary(venues)});
    });
  };

  /**
   * 在单元格外打开完整边框的选项弹层，文字水平居中。
   * @param button 单元格按钮
   */
  const openChoiceMenu = (button: HTMLButtonElement) => {
    closeChoiceMenu();
    let options: string[] = [];
    try {
      const parsed = JSON.parse(button.dataset.choiceOptions || '[]');
      options = Array.isArray(parsed) ? parsed.map((item) => String(item)) : [];
    } catch {
      options = [];
    }
    const current = button.dataset.choiceValue || '';
    const menu = document.createElement('div');
    menu.className = 'll-cell-choice-menu';
    menu.setAttribute('role', 'listbox');
    menu.innerHTML = ['', ...options].map((option) => {
      const label = option || '请选择';
      const selected = option === current ? ' is-selected' : '';
      return `<button type="button" class="ll-cell-choice-option${selected}" role="option" data-value="${escapeHtml(option)}">${escapeHtml(label)}</button>`;
    }).join('');
    document.body.appendChild(menu);
    const rect = button.getBoundingClientRect();
    const longest = Math.max(...['请选择', ...options].map((item) => item.length), 4);
    const width = Math.max(rect.width, longest * 16 + 48, 196);
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.min(Math.max(8, left), Math.max(8, window.innerWidth - width - 8));
    menu.style.width = `${width}px`;
    menu.style.left = `${left}px`;
    const menuHeight = Math.min(menu.scrollHeight, 360);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const top = spaceBelow >= Math.min(menuHeight, 220) ? rect.bottom + 2 : Math.max(8, rect.top - menuHeight - 2);
    menu.style.top = `${top}px`;
    menu.style.maxHeight = `${Math.max(120, Math.min(360, window.innerHeight - top - 8))}px`;
    button.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    choiceMenu = menu;
    menu.addEventListener('click', (event) => {
      const option = (event.target as HTMLElement).closest<HTMLButtonElement>('.ll-cell-choice-option');
      if (!option || !choiceMenu) return;
      const caseId = button.dataset.inplaceCase || '';
      const fieldId = button.dataset.inplaceField || '';
      const item = cases.find((entry) => entry.id === caseId);
      const value = option.dataset.value || '';
      closeChoiceMenu();
      if (!item || !fieldId) return;
      try {
        const patch = fieldId === 'currentStage'
          ? {stageName: value, closed: value.trim() === '已结案'}
          : litigationFieldEditPatch(item, fieldId, value, activeFieldRuleOverrides);
        queueLocalListPatch(caseId, patch);
      } catch (error) {
        notice(error instanceof Error ? error.message : '填写内容有误', true);
        renderList(false);
      }
    });
  };

  /**
   * 日期格弹出年、月、日，确认后写入。清空则把该日留空。
   * 进展记录弹窗传入 onApply，把选定日期交给那一行，不走台账单元格保存。
   * @param button 日期格
   * @param onApply 自定义写入。缺省时按台账字段保存
   */
  const openDateMenu = (button: HTMLButtonElement, onApply?: (value: string) => void) => {
    closeChoiceMenu();
    const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(button.dataset.dateValue || '');
    const today = new Date();
    let year = parsed ? Number(parsed[1]) : today.getFullYear();
    let month = parsed ? Number(parsed[2]) : today.getMonth() + 1;
    let day = parsed ? Number(parsed[3]) : today.getDate();
    const firstYear = Math.min(1990, year);
    const lastYear = Math.max(today.getFullYear() + 15, year);
    const yearOptions = Array.from({length: lastYear - firstYear + 1}, (_, index) => firstYear + index);
    const menu = document.createElement('div');
    const compact = button.classList.contains('ll-progress-date');
    menu.className = `ll-cell-choice-menu ll-cell-date-menu${compact ? ' is-compact' : ''}`;
    menu.setAttribute('role', 'dialog');
    const fieldId = button.dataset.inplaceField || '';
    const title = ({filingDate: '申请立案日', closedAt: '结案日', plannedDate: '计划日期', completedDate: '完成日期', progressDate: '进展日期'} as Record<string, string>)[fieldId] || button.getAttribute('aria-label') || '选择日期';
    menu.setAttribute('aria-label', title);
    menu.innerHTML = `<header class="ll-calendar-header"><h3>${escapeHtml(title)}</h3><button type="button" data-date-close aria-label="关闭日期选择器"><img src="/assets/icons/archive/x.svg" alt=""></button></header>
    <div class="ll-calendar-nav">
      <button type="button" data-date-prev aria-label="上一月"><img src="/assets/icons/archive/chevron-down.svg" alt=""></button>
      <select data-date-year aria-label="年">${yearOptions.map((item) => `<option value="${item}" ${item === year ? 'selected' : ''}>${item}年</option>`).join('')}</select>
      <select data-date-month aria-label="月">${Array.from({length: 12}, (_, index) => index + 1).map((item) => `<option value="${item}" ${item === month ? 'selected' : ''}>${item}月</option>`).join('')}</select>
      <button type="button" data-date-next aria-label="下一月"><img src="/assets/icons/archive/chevron-down.svg" alt=""></button>
    </div>
    <div class="ll-calendar-weekdays" aria-hidden="true">${['一', '二', '三', '四', '五', '六', '日'].map((name) => `<span>${name}</span>`).join('')}</div>
    <div class="ll-calendar-days" role="group" aria-label="日期"></div>
    <div class="ll-date-actions"><button type="button" data-date-clear>清空</button><button type="button" data-date-confirm>确认</button></div>`;
    document.body.appendChild(menu);
    const yearSelect = menu.querySelector<HTMLSelectElement>('[data-date-year]')!;
    const monthSelect = menu.querySelector<HTMLSelectElement>('[data-date-month]')!;
    const dayGrid = menu.querySelector<HTMLElement>('.ll-calendar-days')!;
    const fillDays = () => {
      year = Number(yearSelect.value);
      month = Number(monthSelect.value);
      const count = new Date(year, month, 0).getDate();
      day = Math.min(day, count);
      const offset = (new Date(year, month - 1, 1).getDay() + 6) % 7;
      const slots = Math.ceil((offset + count) / 7) * 7;
      dayGrid.innerHTML = Array.from({length: slots}, (_, index) => {
        const value = index - offset + 1;
        if (value < 1 || value > count) return '<span aria-hidden="true"></span>';
        return `<button type="button" data-date-day="${value}" class="${index % 7 > 4 ? 'is-weekend ' : ''}${value === day ? 'is-selected' : ''}" aria-label="${year}年${month}月${value}日" aria-pressed="${value === day}" tabindex="${value === day ? '0' : '-1'}">${value}</button>`;
      }).join('');
      menu.querySelector<HTMLButtonElement>('[data-date-prev]')!.disabled = year === firstYear && month === 1;
      menu.querySelector<HTMLButtonElement>('[data-date-next]')!.disabled = year === lastYear && month === 12;
    };
    fillDays();
    yearSelect.addEventListener('change', fillDays);
    monthSelect.addEventListener('change', fillDays);
    const moveMonth = (delta: number) => {
      const next = new Date(year, month - 1 + delta, 1);
      if (next.getFullYear() < firstYear || next.getFullYear() > lastYear) return;
      yearSelect.value = String(next.getFullYear());
      monthSelect.value = String(next.getMonth() + 1);
      fillDays();
    };
    menu.querySelector('[data-date-prev]')!.addEventListener('click', () => moveMonth(-1));
    menu.querySelector('[data-date-next]')!.addEventListener('click', () => moveMonth(1));
    dayGrid.addEventListener('click', (event) => {
      const hit = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-date-day]');
      if (!hit) return;
      day = Number(hit.dataset.dateDay);
      fillDays();
      dayGrid.querySelector<HTMLButtonElement>('.is-selected')?.focus();
    });
    dayGrid.addEventListener('keydown', (event) => {
      const delta = {ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7}[event.key];
      if (delta === undefined) return;
      event.preventDefault();
      const next = new Date(year, month - 1, day + delta);
      if (next.getFullYear() < firstYear || next.getFullYear() > lastYear) return;
      day = next.getDate();
      yearSelect.value = String(next.getFullYear());
      monthSelect.value = String(next.getMonth() + 1);
      fillDays();
      dayGrid.querySelector<HTMLButtonElement>('.is-selected')?.focus();
    });
    const dismiss = () => { closeChoiceMenu(); button.focus(); };
    menu.querySelector('[data-date-close]')!.addEventListener('click', dismiss);
    menu.addEventListener('keydown', (event) => { if (event.key === 'Escape') { event.preventDefault(); dismiss(); } });
    const rect = button.getBoundingClientRect();
    const width = Math.min(compact ? 300 : 340, window.innerWidth - 16);
    const anchorToField = button.classList.contains('ll-progress-date');
    let left = anchorToField ? rect.left : rect.left + rect.width / 2 - width / 2;
    left = Math.min(Math.max(8, left), Math.max(8, window.innerWidth - width - 8));
    const sheetTop = document.querySelector('.ll-sheet-bar')?.getBoundingClientRect().top;
    const limitBottom = Math.min(window.innerHeight, sheetTop && sheetTop > 80 ? sheetTop : window.innerHeight) - 8;
    const limitTop = 8;
    const menuHeight = menu.getBoundingClientRect().height;
    const spaceBelow = limitBottom - rect.bottom - 6;
    const spaceAbove = rect.top - limitTop - 6;
    const openBelow = spaceBelow >= menuHeight || spaceBelow >= spaceAbove;
    let top = openBelow ? rect.bottom + 6 : rect.top - menuHeight - 6;
    top = Math.max(limitTop, Math.min(top, Math.max(limitTop, limitBottom - Math.min(menuHeight, limitBottom - limitTop))));
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    menu.style.width = `${width}px`;
    menu.style.zIndex = '200';
    menu.style.maxHeight = `${Math.max(180, limitBottom - top)}px`;
    button.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    choiceMenu = menu;
    dayGrid.querySelector<HTMLButtonElement>('.is-selected')?.focus({preventScroll: true});
    const apply = (value: string) => {
      if (onApply) {
        closeChoiceMenu();
        onApply(value);
        return;
      }
      const caseId = button.dataset.inplaceCase || '';
      const fieldId = button.dataset.inplaceField || '';
      const item = cases.find((entry) => entry.id === caseId);
      closeChoiceMenu();
      if (!item || !fieldId) return;
      try {
        queueLocalListPatch(caseId, litigationFieldEditPatch(item, fieldId, value, activeFieldRuleOverrides));
      } catch (error) {
        notice(error instanceof Error ? error.message : '填写内容有误', true);
        renderList(false);
      }
    };
    menu.querySelector<HTMLButtonElement>('[data-date-confirm]')?.addEventListener('click', () => {
      const pickedYear = yearSelect.value;
      const pickedMonth = monthSelect.value.padStart(2, '0');
      const pickedDay = String(day).padStart(2, '0');
      apply(`${pickedYear}-${pickedMonth}-${pickedDay}`);
    });
    menu.querySelector<HTMLButtonElement>('[data-date-clear]')?.addEventListener('click', () => apply(''));
  };

  const renderList = (refreshDetail = true) => {
    closeChoiceMenu();
    closeInsertMenu();
    const items = filtered();
    const fields = visibleListFields();
    if (selected && !items.some((item) => item.id === selected)) selected = '';
    rememberCase(selected);
    if (listSheet !== 'cases') {
      const fieldIds = detailSheetColumns().map((field) => field.sourceField);
      const backgroundSheet = listSheet === 'parties' && (fieldIds.includes('caseBackground') || (!fieldIds.includes('partyName') && !fieldIds.includes('partyRole')));
      const emptyHint = listSheet === 'parties'
        ? '暂无案件背景'
        : listSheet === 'procedure'
          ? '暂无程序节点'
          : '暂无进展记录';
      const detailRows: string[] = [];
      for (const item of items) {
        if (listSheet === 'parties' && backgroundSheet) {
          const editable = canEditCaseInList(item);
          detailRows.push(`<tr data-id="${escapeHtml(item.id)}">${fieldIds.map((id) => `<td class="${backgroundSheetCellClass(id)}">${backgroundSheetCell(item, id, editable)}</td>`).join('')}</tr>`);
        } else if (listSheet === 'parties') {
          for (const party of item.parties || []) {
            detailRows.push(`<tr data-id="${escapeHtml(item.id)}">${fieldIds.map((id) => `<td>${sheetCell(item, id, party)}</td>`).join('')}</tr>`);
          }
        } else if (listSheet === 'procedure') {
          for (const node of item.nodes || []) {
            detailRows.push(`<tr data-id="${escapeHtml(item.id)}">${fieldIds.map((id) => `<td>${sheetCell(item, id, undefined, node)}</td>`).join('')}</tr>`);
          }
        } else {
          for (const progress of item.progressEntries || []) {
            detailRows.push(`<tr data-id="${escapeHtml(item.id)}">${fieldIds.map((id) => `<td>${sheetCell(item, id, undefined, undefined, progress)}</td>`).join('')}</tr>`);
          }
        }
      }
      rows.innerHTML = detailRows.length ? detailRows.join('') : emptyRow(cases.length ? emptyHint : '暂无诉讼案件，请在案件总表点击“＋”添加');
      if (backgroundSheet) fitBackgroundSheetTextareas(rows);
      const visibleDetailIds = new Set(items.map((item) => item.id));
      for (const id of [...checked]) if (!visibleDetailIds.has(id)) checked.delete(id);
      syncDelete();
      syncSheetHScroll();
      if (refreshDetail && detailCaseId) renderDetail(cases.find((item) => item.id === detailCaseId));
      return;
    }
    renderTableHead();
    if (!items.length) {
      rows.innerHTML = emptyRow(cases.length
        ? '没有匹配的案件'
        : '暂无诉讼案件，点击“＋”添加');
    } else {
      rows.innerHTML = items.map((item, index) => {
        const tone = deadlineTone(nextDeadlineDate(item), item.closed);
        const stage = item.currentStage || currentStageName(item);
        const editable = canEditCaseInList(item);
        const anchorIndex = selected ? firstCaseRowIndex(items, selected) : -1;
        const isActiveRow = Boolean(selected) && item.id === selected && index === anchorIndex;
        const deletable = canDeleteCaseInList(item);
        return `<tr class="${isActiveRow ? 'is-selected ' : ''}" data-id="${escapeHtml(item.id)}">
        ${renderInsertCell()}
        ${renderSelectCell(item, deletable)}
        <td class="col-index">${index + 1}</td>
        ${fields.map((field) => {
          const storageField = resolveListColumnStorageField(field) || resolveListColumnEditField(field);
          return `<td class="${listDataCellClass(field)}" data-label="${escapeHtml(field.label)}">${storageField ? renderListCell(item, field, stage, tone, editable) : ''}</td>`;
        }).join('')}
      </tr>`;
      }).join('');
    }
    const visibleIds = new Set(items.map((item) => item.id));
    for (const id of [...checked]) if (!visibleIds.has(id)) checked.delete(id);
    syncDelete();
    const selectAll = page.querySelector<HTMLInputElement>('#llSelectAll');
    if (selectAll) {
      const selectable = items.filter((item) => canDeleteCaseInList(item));
      selectAll.checked = Boolean(selectable.length) && selectable.every((item) => checked.has(item.id));
    }
    if (refreshDetail && detailCaseId) {
      const detailItem = cases.find((item) => item.id === detailCaseId);
      if (detailItem) renderDetail(detailItem);
      else {
        detailCaseId = '';
        setDrawerOpen(false);
      }
    } else if (!detailCaseId) {
      setDrawerOpen(false);
    }
    if (listSheet === 'cases') {
      bindLitigationCausePicker(rows);
      bindLitigationCourtPicker(rows, notice);
      fitClaimTextareas(rows);
    }
    syncSheetHScroll();
  };

  /**
   * 打开字段选择弹窗，控制列表可见列。
   */
  const openColumnPicker = () => {
    page.querySelector('#llColumnModal')?.remove();
    const mandatory = new Set(['internalNo', 'title']);
    const bodyRows = listFields.filter((field) => field.id !== 'seqNo').map((field) => {
      const locked = mandatory.has(field.id);
      const checkedCol = locked || !hiddenFieldIds.has(field.id);
      return `<tr${locked ? ' class="is-mandatory"' : ''}><td>${escapeHtml(field.label)}</td><td><input type="checkbox" data-column-toggle="${escapeHtml(field.id)}" ${checkedCol ? 'checked' : ''} ${locked ? 'disabled' : ''} aria-label="${escapeHtml(field.label)}"></td></tr>`;
    }).join('');
    root.insertAdjacentHTML('beforeend', `<div class="modal-overlay show" id="llColumnModal"><div class="modal invoice-column-modal"><div class="modal-header"><h3>字段选择</h3><button type="button" class="modal-close" data-close-columns>×</button></div><div class="modal-body invoice-column-modal-body"><aside class="invoice-column-nav"><button type="button" class="invoice-column-nav-item is-active" disabled>列表显示项目</button></aside><div class="invoice-column-panel"><div class="invoice-column-table-wrap"><table class="invoice-column-table"><thead><tr><th>列表项</th><th>是否显示</th></tr></thead><tbody>${bodyRows}</tbody></table></div></div></div><div class="modal-footer"><button type="button" class="secondary-button" data-close-columns>取消</button><button type="button" class="primary-button" id="llConfirmColumns">确定</button></div></div></div>`);
    const modal = root.querySelector<HTMLElement>('#llColumnModal');
    if (!modal) return;
    const close = () => modal.remove();
    modal.querySelectorAll('[data-close-columns]').forEach((button) => button.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.querySelector('#llConfirmColumns')?.addEventListener('click', () => {
      hiddenFieldIds.clear();
      listFields.forEach((field) => {
        if (mandatory.has(field.id)) return;
        const input = modal.querySelector<HTMLInputElement>(`[data-column-toggle="${field.id}"]`);
        if (input && !input.checked) hiddenFieldIds.add(field.id);
      });
      localStorage.setItem(columnStorageKey, JSON.stringify([...hiddenFieldIds]));
      renderTableHead();
      renderList(false);
      close();
    });
  };

  const load = async () => {
    try {
      let drafts: CaseDraft[] = [];
      try {
        const saved: unknown = JSON.parse(localStorage.getItem(draftStorageKey) || '[]');
        if (Array.isArray(saved)) drafts = saved.filter((draft) => draft && typeof draft.caseId === 'string' && typeof draft.updatedAt === 'string' && draft.patch && typeof draft.patch === 'object' && !Array.isArray(draft.patch));
      } catch { /* 损坏草稿不影响读取服务器台账。 */ }
      const [casePayload, contractPayload] = await Promise.all([
        api.get<LitigationListPayload>(apiUrl(`${base}/cases`)),
        api.get<{contracts: LinkableContract[]}>(apiUrl(`${base}/contracts`)).catch(() => ({contracts: [] as LinkableContract[]})),
      ]);
      cases = dedupeLitigationCases(casePayload.cases);
      access = casePayload.access;
      eligibleMembers = casePayload.eligibleMembers;
      const serverFields = casePayload.activePlan?.listFields ?? [];
      if (!serverFields.length) throw new Error('后台没有返回台账字段，已停止显示内置默认列');
      listFields = serverFields
        .map(presentInvolvedAmountColumn)
        .map(presentResultColumn)
        .map(presentAwardedAmountColumn);
      if (casePayload.identityNotice) notice(casePayload.identityNotice, true);
      internalNoRule = normalizeLitigationInternalNoRule(casePayload.activePlan?.internalNoRule);
      activeFieldRuleOverrides = casePayload.activePlan?.fieldRuleOverrides;
      activePlanSheetLabels = casePayload.activePlan?.sheetFieldLabels;
      sheetColumns = casePayload.activePlan?.sheetColumns;
      exportTitle = casePayload.exportTitle ?? {
        row: 1,
        title: '',
        canSetRow: casePayload.access.createdBy === casePayload.access.currentUserId,
        headerRow: 2,
      };
      paintLedgerTitle();
      renderTableHead();
      page.querySelector('.ll-case-table')?.removeAttribute('hidden');
      contracts = contractPayload.contracts;
      refreshServerCaseSnapshot();
      unresolvedDrafts = [];
      for (const draft of drafts) {
        const item = cases.find((entry) => entry.id === draft.caseId && canEditCaseInList(entry));
        /**
         * 服务器案件为准。草稿记下的更新时间已经对不上时，丢掉这份草稿，不盖到案件上。
         */
        if (!item || item.updatedAt !== draft.updatedAt) continue;
        applyListPatchLocal(item, draft.patch);
        pendingPatches.set(item.id, draft.patch);
      }
      syncWorkbookEditControls();
      page.querySelector('#llCollaborationRoot')?.classList.toggle('is-hidden', !access.isAdmin);
      const mapping = casePayload.mapping;
      if (mapping?.id || casePayload.activePlan?.id) {
        planSettings = {
          id: mapping?.id || casePayload.activePlan?.id || '',
          planScope: mapping?.planScope,
          createdBy: mapping?.createdBy,
          creatorOrganizationName: mapping?.creatorOrganizationName || '',
          creatorOrganizationShortName: mapping?.creatorOrganizationShortName || '',
          creatorOrganizationCode: mapping?.creatorOrganizationCode || '',
          organizationLabelKind: parseLitigationOrganizationLabelKind(String(mapping?.organizationLabelKind || 'name')),
          collaborationUnits: mapping?.collaborationUnits || [],
          teamEditors: mapping?.teamEditors || [],
        };
      }
      renderList();
    } catch (error) {
      page.querySelector('.ll-case-table')?.removeAttribute('hidden');
      const head = page.querySelector<HTMLElement>('#llHeadRow');
      if (head) head.innerHTML = '';
      rows.innerHTML = `<tr><td colspan="12" class="legacy-empty-cell">${escapeHtml(error instanceof Error ? error.message : '请稍后重试')} <button type="button" class="invoice-view-link" data-action="retry">重新加载</button></td></tr>`;
    }
  };

  page.addEventListener('input', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('#llSearch')) {
      renderList();
      return;
    }
    if (target.closest('#llOverviewForm')) {
      const courtInput = target.closest<HTMLInputElement>('[data-venue-court]');
      if (courtInput && courtInput.value.trim() !== (courtInput.dataset.venueSaved || '')) return;
      scheduleSave();
    }
  });
  page.addEventListener('focusout', (event) => {
    const target = event.target as HTMLElement;
    const inplace = target.closest<HTMLElement>('.ll-cell-inplace.is-editing');
    if (!inplace?.dataset.inplaceField || !inplace.dataset.inplaceCase) return;
    inplace.classList.remove('is-editing');
    inplace.removeAttribute('contenteditable');
    inplace.closest('td')?.classList.remove('is-excel-editing');
    const fieldId = inplace.dataset.inplaceField;
    const caseId = inplace.dataset.inplaceCase;
    let raw = (inplace.textContent || '').trim();
    if (!raw || raw === '—') raw = '';
    const item = cases.find((entry) => entry.id === caseId);
    if (!item) return;
    if (fieldId === 'amount') {
      const num = Math.max(0, Number(raw.replace(/,/g, '')) || 0);
      const previous = item.amount || 0;
      if (num === previous) {
        inplace.textContent = num ? String(num) : '';
        return;
      }
      queueLocalListPatch(caseId, {amount: num});
      return;
    }
    const previous = String(litigationEditableCellValue(item, fieldId) || '');
    if (raw === previous) {
      inplace.textContent = raw || '';
      return;
    }
    try {
      queueLocalListPatch(caseId, litigationFieldEditPatch(item, fieldId, raw, activeFieldRuleOverrides));
    } catch (error) {
      notice(error instanceof Error ? error.message : '填写内容有误', true);
      inplace.textContent = previous || '';
    }
  });
  page.addEventListener('input', (event) => {
    if (event.target instanceof HTMLTextAreaElement && (event.target.classList.contains('ll-claim-text') || event.target.classList.contains('ll-result-text'))) fitClaimTextarea(event.target);
  });
  page.addEventListener('change', (event) => {
    const courtHost = (event.target as HTMLElement).closest<HTMLElement>('.ll-court-layers');
    const courtControl = (event.target as HTMLElement).closest('[data-court-kind], [data-court-value]');
    if (courtHost && courtControl) {
      if (courtHost.closest('#llOverviewForm')) {
        scheduleSave();
        return;
      }
      const caseId = courtHost.closest('tr')?.dataset.id || '';
      if (caseId) queueLocalListPatch(caseId, {courtNumbers: readCourtNumberRows(courtHost)});
      return;
    }
    const claimHost = (event.target as HTMLElement).closest<HTMLElement>('.ll-claim-layers');
    const claimControl = (event.target as HTMLElement).closest('[data-claim-part]');
    if (claimHost && claimControl) {
      if (claimHost.closest('#llOverviewForm')) {
        scheduleSave();
        return;
      }
      const caseId = claimHost.dataset.claimCase || claimHost.closest('tr')?.dataset.id || '';
      const item = cases.find((entry) => entry.id === caseId);
      if (!item) return;
      const claim = claimHost.querySelector<HTMLTextAreaElement>('[data-claim-part="claim"]')?.value.trim().slice(0, 2000) || '';
      const counterInput = claimHost.querySelector<HTMLTextAreaElement>('[data-claim-part="counter"]');
      const counter = counterInput ? counterInput.value.trim().slice(0, 2000) : undefined;
      if (claim === String(item.claimSummary || '') && (counter === undefined || counter === String(item.counterclaimSummary || ''))) return;
      queueLocalListPatch(caseId, counter === undefined ? {claimSummary: claim} : {claimSummary: claim, counterclaimSummary: counter}, false);
      return;
    }
    const resultHost = (event.target as HTMLElement).closest<HTMLElement>('.ll-result-layers');
    const resultControl = (event.target as HTMLElement).closest('[data-result-part]');
    if (resultHost && resultControl) {
      if (resultHost.closest('#llOverviewForm')) {
        scheduleSave();
        return;
      }
      const caseId = resultHost.dataset.resultCase || resultHost.closest('tr')?.dataset.id || '';
      const item = cases.find((entry) => entry.id === caseId);
      if (!item) return;
      const primary = resultHost.querySelector<HTMLTextAreaElement>('[data-result-part="primary"]')?.value.trim().slice(0, 2000) || '';
      const counterInput = resultHost.querySelector<HTMLTextAreaElement>('[data-result-part="counter"]');
      const counter = counterInput ? counterInput.value.trim().slice(0, 2000) : undefined;
      if (primary === String(item.resultSummary || '') && (counter === undefined || counter === String(item.counterclaimResult || ''))) return;
      queueLocalListPatch(caseId, counter === undefined ? {resultSummary: primary} : {resultSummary: primary, counterclaimResult: counter}, false);
      return;
    }
    const amountHost = (event.target as HTMLElement).closest<HTMLElement>('.ll-amount-layers');
    const amountControl = (event.target as HTMLElement).closest('[data-amount-part]');
    if (amountHost && amountControl) {
      if (amountHost.closest('#llOverviewForm')) {
        scheduleSave();
        return;
      }
      const caseId = amountHost.dataset.amountCase || amountHost.closest('tr')?.dataset.id || '';
      const item = cases.find((entry) => entry.id === caseId);
      const amounts = readInvolvedAmountPatch(amountHost);
      if (!item || !amounts) {
        if (item && !amounts) notice('金额必须为非负数字，单位为元', true);
        return;
      }
      if ((item.principalAmount || 0) === amounts.principalAmount && (item.counterclaimAmount || 0) === amounts.counterclaimAmount) return;
      queueLocalListPatch(caseId, amounts, false);
      return;
    }
    const awardHost = (event.target as HTMLElement).closest<HTMLElement>('.ll-award-layers');
    const awardControl = (event.target as HTMLElement).closest('[data-award-part]');
    if (awardHost && awardControl) {
      if (awardHost.closest('#llOverviewForm')) {
        scheduleSave();
        return;
      }
      const caseId = awardHost.dataset.awardCase || awardHost.closest('tr')?.dataset.id || '';
      const item = cases.find((entry) => entry.id === caseId);
      const awards = readAwardedAmountPatch(awardHost);
      if (!item || !awards) {
        if (item && !awards) notice('金额必须为非负数字，单位为元', true);
        return;
      }
      if ((item.awardedAmount || 0) === awards.awardedAmount && (item.counterclaimAwardedAmount || 0) === awards.counterclaimAwardedAmount) return;
      queueLocalListPatch(caseId, awards, false);
      return;
    }
    const venueHost = (event.target as HTMLElement).closest<HTMLElement>('.ll-venue-layers');
    const venueControl = (event.target as HTMLElement).closest('[data-venue-kind], [data-venue-value]');
    if (venueHost && venueControl) {
      const venues = readVenueRows(venueHost);
      if (venueHost.closest('#llOverviewForm')) {
        scheduleSave();
        return;
      }
      const caseId = venueHost.closest('tr')?.dataset.id || '';
      if (caseId) queueLocalListPatch(caseId, {venues, venue: litigationVenueSummary(venues)});
      return;
    }
    const partyHost = (event.target as HTMLElement).closest<HTMLElement>('.ll-party-layers');
    const partyControl = (event.target as HTMLElement).closest('[data-party-value]');
    if (partyHost && partyControl) {
      const caseId = partyHost.closest('tr')?.dataset.id || '';
      const item = cases.find((entry) => entry.id === caseId);
      if (!item || !caseId) return;
      const role = partyHost.dataset.partyRole === 'opponent' ? 'opponent' : 'plaintiff';
      const layers = partyLayersForListItem(item);
      let parties = item.parties;
      for (const row of partyHost.querySelectorAll<HTMLElement>('.ll-court-row')) {
        const rawLayer = row.querySelector<HTMLElement>('[data-party-layer]')?.getAttribute('data-party-layer') || layers[0];
        const layer = isLitigationPartyLayer(rawLayer) ? rawLayer : layers[0];
        const name = row.querySelector<HTMLInputElement>('[data-party-value]')?.value || '';
        parties = writePartyLayerName(parties, role, layer, name, `party_${role}_${layer}_${caseId}`, layers);
      }
      const before = partyLayerRows(item.parties, role, layers).map((row) => `${row.layer}:${row.name}`).join('|');
      const after = partyLayerRows(parties, role, layers).map((row) => `${row.layer}:${row.name}`).join('|');
      if (before !== after) queueLocalListPatch(caseId, {parties}, false);
      return;
    }
    const input = event.target as HTMLInputElement;
    if (input.id === 'llSelectAll') {
      filtered()
        .filter((item) => canDeleteCaseInList(item))
        .forEach((item) => input.checked ? checked.add(item.id) : checked.delete(item.id));
      renderList(false);
      return;
    }
    if (input.dataset.check) {
      if (input.checked) checked.add(input.dataset.check);
      else checked.delete(input.dataset.check);
      selected = input.dataset.check;
      rememberCase(selected);
      renderList(false);
      return;
    }
    const stageSelect = (event.target as HTMLElement).closest<HTMLSelectElement>('[data-stage]');
    if (stageSelect?.dataset.stage) {
      const stageName = stageSelect.value;
      queueLocalListPatch(stageSelect.dataset.stage, {stageName, closed: stageName.trim() === '已结案'});
      return;
    }
    const listFieldSelect = (event.target as HTMLElement).closest<HTMLSelectElement>('.ll-cell-inplace-select[data-inplace-field][data-inplace-case]');
    if (listFieldSelect?.dataset.inplaceCase && listFieldSelect.dataset.inplaceField) {
      const caseId = listFieldSelect.dataset.inplaceCase;
      const fieldId = listFieldSelect.dataset.inplaceField;
      const item = cases.find((entry) => entry.id === caseId);
      if (!item) return;
      try {
        const patch = fieldId === 'currentStage'
          ? {stageName: listFieldSelect.value}
          : litigationFieldEditPatch(item, fieldId, listFieldSelect.value, activeFieldRuleOverrides);
        queueLocalListPatch(caseId, patch);
      } catch (error) {
        notice(error instanceof Error ? error.message : '填写内容有误', true);
        renderList(false);
      }
      return;
    }
    const causeInput = event.target as HTMLInputElement;
    if (causeInput.name === 'title' && causeInput.closest('[data-litigation-cause-cell]')) {
      const cell = causeInput.closest<HTMLElement>('[data-litigation-cause-cell]');
      const caseId = cell?.dataset.inplaceCase || '';
      const item = cases.find((entry) => entry.id === caseId);
      if (!item || !caseId) return;
      const title = causeInput.value.trim();
      if (!title) {
        if (item.title) queueLocalListPatch(caseId, {title: ''});
        return;
      }
      const locked = cell?.querySelector<HTMLInputElement>('input[name="litigationCauseLocked"]')?.value.trim() || '';
      if (locked === title && title !== item.title) {
        try {
          queueLocalListPatch(caseId, litigationFieldEditPatch(item, 'title', title, activeFieldRuleOverrides));
        } catch (error) {
          notice(error instanceof Error ? error.message : '请选择标准案由', true);
        }
      }
      return;
    }
    const causeLocked = event.target as HTMLInputElement;
    if (causeLocked.name === 'litigationCauseLocked') {
      const cell = causeLocked.closest<HTMLElement>('[data-litigation-cause-cell]');
      if (cell?.dataset.inplaceCase) {
        const item = cases.find((entry) => entry.id === cell.dataset.inplaceCase);
        if (!item) return;
        const title = causeLocked.value.trim();
        if (!title) return;
        try {
          queueLocalListPatch(cell.dataset.inplaceCase, litigationFieldEditPatch(item, 'title', title, activeFieldRuleOverrides));
        } catch (error) {
          notice(error instanceof Error ? error.message : '请选择标准案由', true);
        }
      }
      return;
    }
    if ((event.target as HTMLElement).closest('#llOverviewForm')) scheduleSave();
  });
  document.addEventListener('mousedown', (event) => {
    const hit = event.target as HTMLElement;
    if (columnFilterMenu && !columnFilterMenu.contains(hit) && !hit.closest('[data-col-filter]')) closeColumnFilterMenu();
    if (!choiceMenu || choiceMenu.contains(hit) || hit.closest('.ll-cell-choice, .ll-layer-choice, .ll-cell-date, .ll-progress-date')) return;
    closeChoiceMenu();
  });
  page.querySelector('.table-scroll')?.addEventListener('scroll', () => {
    closeChoiceMenu();
    closeColumnFilterMenu();
  });
  window.addEventListener('resize', () => {
    closeChoiceMenu();
    closeColumnFilterMenu();
  });

  /**
   * 打开新建台账。当前台账还有未保存修改时先拦住。
   */
  const startNewWorkbook = () => {
    const canStart = () => {
      if (dirty || pendingPatches.size || overviewSaving || workbookSaving) {
        window.alert('请先点击“保存台账”，保存好现有台账后再新建台账。');
        return false;
      }
      return true;
    };
    void openLitigationWorkbookCreation(root, auth, canStart).catch((error) => notice(error instanceof Error ? error.message : '无法加载新建台账向导', true));
  };

  /**
   * 打开某一列的筛选菜单。选项来自当前已加载案件里这一列出现过的文字。
   * @param button 表头上的筛选标记
   * @param fieldId 列字段 id
   */
  const openColumnFilterMenu = (button: HTMLButtonElement, fieldId: string) => {
    closeColumnFilterMenu();
    closeChoiceMenu();
    const field = listFields.find((entry) => entry.id === fieldId);
    if (!field) return;
    const counts = new Map<string, number>();
    for (const item of cases) {
      const key = columnFilterKey(columnFilterText(item, field));
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const keys = [...counts.keys()].sort((left, right) => {
      if (left === COLUMN_FILTER_BLANK) return 1;
      if (right === COLUMN_FILTER_BLANK) return -1;
      return left.localeCompare(right, 'zh');
    });
    /**
     * 数量达到 2 时，在筛选项名称后标出条数。
     * @param label 选项名称
     * @param count 当前列表里该选项出现的条数
     */
    const filterCountText = (label: string, count: number) => count >= 2 ? `${label}（${count}）` : label;
    const filterCountOf = (list: string[]) => list.reduce((sum, key) => sum + (counts.get(key) || 0), 0);
    const filterTotal = filterCountOf(keys);
    const allowed = columnFilters.get(fieldId);
    const menu = document.createElement('div');
    menu.className = 'll-col-filter-menu';
    menu.dataset.field = fieldId;
    menu.setAttribute('role', 'dialog');
    menu.setAttribute('aria-label', `筛选${field.label}`);
    const fieldKey = resolveListColumnEditField(field) || resolveListColumnStorageField(field);
    const layered = ['courtNo', 'venue', 'plaintiffParty', 'opponent', 'claimSummary', 'resultSummary', 'involvedAmount', 'awardedAmount'].includes(fieldKey || '');
    const dateKeys = keys.filter((key) => /^\d{4}-\d{2}-\d{2}$/.test(key));
    const otherKeys = keys.filter((key) => !dateKeys.includes(key));
    const dateTree = !layered && dateKeys.length > 0 && (ledgerDateField(fieldKey) || otherKeys.every((key) => key === COLUMN_FILTER_BLANK));
    if (layered) {
      const groups = new Map<string, Map<string, number>>();
      let blankCount = 0;
      for (const item of cases) {
        const parts = columnLayerParts(item, field) || [];
        if (!parts.length) {
          blankCount += 1;
          continue;
        }
        const seen = new Set<string>();
        for (const part of parts) {
          const token = `${part.category}\n${part.value}`;
          if (seen.has(token)) continue;
          seen.add(token);
          const values = groups.get(part.category) || new Map<string, number>();
          values.set(part.value, (values.get(part.value) || 0) + 1);
          groups.set(part.category, values);
        }
      }
      const categories = [...groups.keys()].sort((left, right) => left.localeCompare(right, 'zh'));
      const tokens: string[] = [];
      const tokenCounts: number[] = [];
      const categoryChecked = (category: string) => {
        const values = groups.get(category);
        if (!values) return false;
        if (!allowed) return true;
        return [...values.keys()].some((value) => allowed.has(columnFilterKey(`${category}\n${value}`)) || allowed.has(columnFilterKey(`${category} ${value}`.trim())));
      };
      const layerHtml = categories.map((category, layerIndex) => {
        const values = groups.get(category)!;
        const count = [...values.values()].reduce((sum, item) => sum + item, 0);
        return `<label><input type="checkbox" data-layer-index="${layerIndex}" ${categoryChecked(category) ? 'checked' : ''}><span>${escapeHtml(filterCountText(category, count))}</span></label>`;
      }).join('');
      const valueHtml = categories.map((category, layerIndex) => {
        const values = [...groups.get(category)!.entries()].sort((left, right) => left[0].localeCompare(right[0], 'zh'));
        return values.map(([value, count]) => {
          const token = columnFilterKey(`${category}\n${value}`);
          const legacy = columnFilterKey(`${category} ${value}`.trim());
          const index = tokens.length;
          tokens.push(token);
          tokenCounts.push(count);
          const checked = !allowed || allowed.has(token) || allowed.has(legacy);
          return `<label data-layer-index="${layerIndex}"${categoryChecked(category) ? '' : ' hidden'}><input type="checkbox" data-filter-index="${index}" ${checked ? 'checked' : ''}><span title="${escapeHtml(value)}">${escapeHtml(filterCountText(value, count))}</span></label>`;
        }).join('');
      }).join('');
      const blankHtml = blankCount
        ? (() => {
          const index = tokens.length;
          tokens.push(COLUMN_FILTER_BLANK);
          tokenCounts.push(blankCount);
          const checked = !allowed || allowed.has(COLUMN_FILTER_BLANK);
          return `<label data-layer-blank><input type="checkbox" data-filter-index="${index}" ${checked ? 'checked' : ''}><span>${escapeHtml(filterCountText(COLUMN_FILTER_BLANK, blankCount))}</span></label>`;
        })()
        : '';
      menu.innerHTML = `<div class="ll-col-filter-title">${escapeHtml(field.label)}</div><div class="ll-col-filter-layers"><div class="ll-col-filter-title">分类</div>${layerHtml}</div><label class="ll-col-filter-all"><input type="checkbox" data-filter-all><span>${escapeHtml(filterCountText('全选', tokenCounts.reduce((sum, count) => sum + count, 0)))}</span></label><div class="ll-col-filter-list">${valueHtml}${blankHtml}</div>`;
      document.body.appendChild(menu);
      columnFilterMenu = menu;
      button.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      const boxes = () => [...menu.querySelectorAll<HTMLInputElement>('[data-filter-index]')].filter((box) => !box.closest('label')?.hidden);
      const selectAll = menu.querySelector<HTMLInputElement>('[data-filter-all]')!;
      const paintSelectAll = () => {
        const items = boxes();
        const checkedCount = items.filter((box) => box.checked).length;
        selectAll.checked = items.length > 0 && checkedCount === items.length;
        selectAll.indeterminate = checkedCount > 0 && checkedCount < items.length;
      };
      const applyChecks = () => {
        const picked = new Set([...menu.querySelectorAll<HTMLInputElement>('[data-filter-index]')].flatMap((box) => {
          if (!box.checked || box.closest('label')?.hidden) return [];
          const token = tokens[Number(box.dataset.filterIndex)];
          return token ? [token] : [];
        }));
        if (picked.size === tokens.length) columnFilters.delete(fieldId);
        else columnFilters.set(fieldId, picked);
        paintSelectAll();
        syncColumnFilterButton();
        renderList(false);
        page.querySelector<HTMLButtonElement>(`[data-col-filter="${CSS.escape(fieldId)}"]`)?.classList.add('is-open');
      };
      paintSelectAll();
      selectAll.addEventListener('change', () => {
        const on = selectAll.checked;
        menu.querySelectorAll<HTMLInputElement>('[data-layer-index]').forEach((box) => { box.checked = on; });
        menu.querySelectorAll<HTMLLabelElement>('[data-layer-index]').forEach((label) => { label.hidden = !on; });
        menu.querySelectorAll<HTMLInputElement>('[data-filter-index]').forEach((box) => { box.checked = on; });
        applyChecks();
      });
      menu.querySelector('.ll-col-filter-layers')?.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        if (!target.matches('[data-layer-index]')) return;
        const layerIndex = target.dataset.layerIndex || '';
        menu.querySelectorAll<HTMLLabelElement>(`.ll-col-filter-list [data-layer-index="${layerIndex}"]`).forEach((label) => {
          label.hidden = !target.checked;
          const box = label.querySelector<HTMLInputElement>('[data-filter-index]');
          if (!target.checked && box) box.checked = false;
        });
        applyChecks();
      });
      menu.querySelector('.ll-col-filter-list')?.addEventListener('change', (event) => {
        if (!(event.target as HTMLElement).matches('[data-filter-index]')) return;
        applyChecks();
      });
    } else if (dateTree) {
      const years = new Map<string, Map<string, string[]>>();
      for (const key of dateKeys) {
        const [year, month] = key.split('-');
        const months = years.get(year!) || new Map<string, string[]>();
        const days = months.get(month!) || [];
        days.push(key);
        months.set(month!, days);
        years.set(year!, months);
      }
      const yearHtml = [...years.keys()].sort((left, right) => right.localeCompare(left)).map((year) => {
        const months = years.get(year)!;
        const yearCount = filterCountOf([...months.values()].flat());
        const monthHtml = [...months.keys()].sort().map((month) => {
          const days = months.get(month)!.sort();
          return `<div class="ll-date-branch" data-date-branch="${year}-${month}">
            <div class="ll-date-row"><button type="button" class="ll-date-twist" aria-expanded="false" aria-label="展开${Number(month)}月">+</button><label><input type="checkbox" data-date-scope="${year}-${month}"><span>${filterCountText(`${Number(month)}月`, filterCountOf(days))}</span></label></div>
            <div class="ll-date-kids" hidden>${days.map((key) => {
              const checked = !allowed || allowed.has(key);
              return `<label class="ll-date-day"><input type="checkbox" data-filter-key="${escapeHtml(key)}" data-year="${year}" data-month="${year}-${month}" ${checked ? 'checked' : ''}><span>${filterCountText(`${Number(key.slice(8))}日`, counts.get(key) || 0)}</span></label>`;
            }).join('')}</div>
          </div>`;
        }).join('');
        return `<div class="ll-date-branch" data-date-branch="${year}">
          <div class="ll-date-row"><button type="button" class="ll-date-twist" aria-expanded="true" aria-label="收起${year}年">−</button><label><input type="checkbox" data-date-scope="${year}"><span>${filterCountText(`${year}年`, yearCount)}</span></label></div>
          <div class="ll-date-kids">${monthHtml}</div>
        </div>`;
      }).join('');
      const otherHtml = otherKeys.map((key) => {
        const checked = !allowed || allowed.has(key);
        return `<label class="ll-date-day"><input type="checkbox" data-filter-key="${escapeHtml(key)}" ${checked ? 'checked' : ''}><span>${escapeHtml(filterCountText(key, counts.get(key) || 0))}</span></label>`;
      }).join('');
      menu.innerHTML = `<div class="ll-col-filter-title">${escapeHtml(field.label)}</div><label class="ll-col-filter-all"><input type="checkbox" data-filter-all><span>${escapeHtml(filterCountText('全选', filterTotal))}</span></label><div class="ll-col-filter-list ll-date-tree">${yearHtml}${otherHtml}</div>`;
      document.body.appendChild(menu);
      columnFilterMenu = menu;
      button.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      const dayBoxes = () => [...menu.querySelectorAll<HTMLInputElement>('[data-filter-key]')];
      const selectAll = menu.querySelector<HTMLInputElement>('[data-filter-all]')!;
      const paintScope = (scope: string) => {
        const input = menu.querySelector<HTMLInputElement>(`[data-date-scope="${scope}"]`);
        if (!input) return;
        const days = dayBoxes().filter((box) => (box.dataset.filterKey || '').startsWith(scope.length === 4 ? `${scope}-` : scope));
        const checkedCount = days.filter((box) => box.checked).length;
        input.checked = days.length > 0 && checkedCount === days.length;
        input.indeterminate = checkedCount > 0 && checkedCount < days.length;
      };
      const paintScopes = () => {
        menu.querySelectorAll<HTMLInputElement>('[data-date-scope]').forEach((input) => paintScope(input.dataset.dateScope || ''));
      };
      const paintSelectAll = () => {
        const items = dayBoxes();
        const checkedCount = items.filter((box) => box.checked).length;
        selectAll.checked = items.length > 0 && checkedCount === items.length;
        selectAll.indeterminate = checkedCount > 0 && checkedCount < items.length;
        paintScopes();
      };
      const applyChecks = () => {
        const picked = new Set(dayBoxes().flatMap((box) => box.checked && box.dataset.filterKey ? [box.dataset.filterKey] : []));
        if (picked.size === keys.length) columnFilters.delete(fieldId);
        else columnFilters.set(fieldId, picked);
        paintSelectAll();
        syncColumnFilterButton();
        renderList(false);
        page.querySelector<HTMLButtonElement>(`[data-col-filter="${CSS.escape(fieldId)}"]`)?.classList.add('is-open');
      };
      paintSelectAll();
      selectAll.addEventListener('change', () => {
        const on = selectAll.checked;
        dayBoxes().forEach((box) => { box.checked = on; });
        applyChecks();
      });
      menu.addEventListener('click', (event) => {
        const twist = (event.target as HTMLElement).closest<HTMLButtonElement>('.ll-date-twist');
        if (!twist) return;
        event.preventDefault();
        const kids = twist.closest('.ll-date-branch')?.querySelector<HTMLElement>(':scope > .ll-date-kids');
        if (!kids) return;
        const open = kids.hasAttribute('hidden');
        kids.toggleAttribute('hidden', !open);
        twist.textContent = open ? '−' : '+';
        twist.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      menu.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        if (target.dataset.dateScope) {
          const scope = target.dataset.dateScope;
          const prefix = scope.length === 4 ? `${scope}-` : scope;
          dayBoxes().forEach((box) => {
            if ((box.dataset.filterKey || '').startsWith(prefix)) box.checked = target.checked;
          });
          applyChecks();
          return;
        }
        if (target.dataset.filterKey) applyChecks();
      });
    } else {
      const searchBox = keys.length > 8
        ? '<input class="ll-col-filter-search" type="search" placeholder="搜索" aria-label="搜索筛选项">'
        : '';
      menu.innerHTML = `<div class="ll-col-filter-title">${escapeHtml(field.label)}</div>${searchBox}<label class="ll-col-filter-all"><input type="checkbox" data-filter-all><span>${escapeHtml(filterCountText('全选', filterTotal))}</span></label><div class="ll-col-filter-list">${keys.map((key, index) => {
        const checked = !allowed || allowed.has(key);
        return `<label><input type="checkbox" data-filter-index="${index}" ${checked ? 'checked' : ''}><span title="${escapeHtml(key)}">${escapeHtml(filterCountText(key, counts.get(key) || 0))}</span></label>`;
      }).join('')}</div>`;
      document.body.appendChild(menu);
      columnFilterMenu = menu;
      button.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      const boxes = () => [...menu.querySelectorAll<HTMLInputElement>('[data-filter-index]')];
      const selectAll = menu.querySelector<HTMLInputElement>('[data-filter-all]')!;
      const paintSelectAll = () => {
        const items = boxes();
        const checkedCount = items.filter((box) => box.checked).length;
        selectAll.checked = checkedCount === items.length && items.length > 0;
        selectAll.indeterminate = checkedCount > 0 && checkedCount < items.length;
      };
      const applyChecks = () => {
        const picked = new Set(boxes().flatMap((box) => {
          if (!box.checked) return [];
          const key = keys[Number(box.dataset.filterIndex)];
          return key ? [key] : [];
        }));
        if (picked.size === keys.length) columnFilters.delete(fieldId);
        else columnFilters.set(fieldId, picked);
        paintSelectAll();
        syncColumnFilterButton();
        renderList(false);
        page.querySelector<HTMLButtonElement>(`[data-col-filter="${CSS.escape(fieldId)}"]`)?.classList.add('is-open');
      };
      paintSelectAll();
      selectAll.addEventListener('change', () => {
        const on = selectAll.checked;
        boxes().forEach((box) => { box.checked = on; });
        applyChecks();
      });
      menu.querySelector('.ll-col-filter-list')?.addEventListener('change', (event) => {
        if (!(event.target as HTMLElement).matches('[data-filter-index]')) return;
        applyChecks();
      });
      menu.querySelector<HTMLInputElement>('.ll-col-filter-search')?.addEventListener('input', (event) => {
        const query = (event.target as HTMLInputElement).value.trim().toLocaleLowerCase();
        menu.querySelectorAll<HTMLLabelElement>('.ll-col-filter-list label').forEach((label) => {
          const text = label.querySelector('span')?.getAttribute('title') || label.querySelector('span')?.textContent || '';
          label.hidden = Boolean(query) && !text.toLocaleLowerCase().includes(query);
        });
      });
    }
    const rect = button.getBoundingClientRect();
    const width = 280;
    const left = Math.min(Math.max(8, rect.right - width), Math.max(8, window.innerWidth - width - 8));
    menu.style.width = `${width}px`;
    menu.style.left = `${left}px`;
    const menuHeight = menu.offsetHeight;
    const top = window.innerHeight - rect.bottom - 8 >= menuHeight ? rect.bottom + 4 : Math.max(8, rect.top - menuHeight - 4);
    menu.style.top = `${top}px`;
  };

  page.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement);
    const insertCorner = target.closest<HTMLButtonElement>('[data-insert-corner]');
    if (insertCorner) {
      event.preventDefault();
      event.stopPropagation();
      openInsertMenu(insertCorner);
      return;
    }
    const columnFilter = target.closest<HTMLButtonElement>('[data-col-filter]');
    if (columnFilter) {
      event.preventDefault();
      event.stopPropagation();
      const fieldId = columnFilter.dataset.colFilter || '';
      if (columnFilterMenu?.dataset.field === fieldId) closeColumnFilterMenu();
      else openColumnFilterMenu(columnFilter, fieldId);
      return;
    }
    const dateButton = target.closest<HTMLButtonElement>('.ll-cell-date');
    if (dateButton) {
      event.preventDefault();
      if (dateButton.classList.contains('is-open')) closeChoiceMenu();
      else openDateMenu(dateButton);
      return;
    }
    const choice = target.closest<HTMLButtonElement>('.ll-cell-choice');
    if (choice) {
      event.preventDefault();
      if (choice.classList.contains('is-open')) closeChoiceMenu();
      else openChoiceMenu(choice);
      return;
    }
    const layerChoice = target.closest<HTMLButtonElement>('.ll-layer-choice');
    if (layerChoice) {
      event.preventDefault();
      if (layerChoice.classList.contains('is-open')) closeChoiceMenu();
      else openLayerMenu(layerChoice);
      return;
    }
    const addCourt = target.closest<HTMLButtonElement>('[data-court-add]');
    if (addCourt) {
      event.preventDefault();
      const host = addCourt.closest<HTMLElement>('.ll-court-layers');
      if (!host) return;
      const rowEl = addCourt.closest<HTMLElement>('.ll-court-row');
      const rows = readCourtNumberRows(host);
      const used = new Set(rows.map((row) => row.layer));
      const caseIdForLayers = host.closest('tr')?.dataset.id || '';
      const caseItem = cases.find((entry) => entry.id === caseIdForLayers);
      const layerContext = caseItem || layerContextFromForm(host);
      const allowedLayers = litigationCourtNumberLayersForDispute(layerContext);
      const nextLayer = allowedLayers.find((layer) => !used.has(layer.id))?.id || allowedLayers[0]?.id || 'first';
      const index = rowEl ? [...host.querySelectorAll<HTMLElement>('.ll-court-row')].indexOf(rowEl) : rows.length - 1;
      const next = rows.slice();
      next.splice(Math.max(0, index) + 1, 0, {layer: nextLayer, value: ''});
      const caseId = host.closest('tr')?.dataset.id || '';
      if (caseId) queueLocalListPatch(caseId, {courtNumbers: next});
      else {
        rowEl?.insertAdjacentHTML('afterend', courtNumberRowMarkup(nextLayer, '', true, true, true, layerContext));
        if (host.closest('#llOverviewForm')) scheduleSave();
      }
      return;
    }
    const removeCourt = target.closest<HTMLButtonElement>('[data-court-remove]');
    if (removeCourt) {
      event.preventDefault();
      const host = removeCourt.closest<HTMLElement>('.ll-court-layers');
      const rowEl = removeCourt.closest<HTMLElement>('.ll-court-row');
      if (!host || !rowEl) return;
      const rows = readCourtNumberRows(host);
      const index = [...host.querySelectorAll<HTMLElement>('.ll-court-row')].indexOf(rowEl);
      if (index <= 0) return;
      const next = rows.filter((_, rowIndex) => rowIndex !== index);
      const caseId = host.closest('tr')?.dataset.id || '';
      if (caseId) queueLocalListPatch(caseId, {courtNumbers: next});
      else {
        rowEl.remove();
        if (host.closest('#llOverviewForm')) scheduleSave();
      }
      return;
    }
    const addClaim = target.closest<HTMLButtonElement>('[data-claim-add]');
    if (addClaim) {
      event.preventDefault();
      const host = addClaim.closest<HTMLElement>('.ll-claim-layers');
      const caseId = host?.dataset.claimCase || host?.closest('tr')?.dataset.id || '';
      if (!host || !caseId) return;
      openCounterclaimRows.add(caseId);
      if (host.closest('#llOverviewForm')) {
        if (!host.querySelector('[data-claim-part="counter"]')) host.insertAdjacentHTML('beforeend', claimRowMarkup('反请求', 'counter', '', true, false, false, true));
        addClaim.remove();
        scheduleSave();
        return;
      }
      renderList(false);
      return;
    }
    const removeClaim = target.closest<HTMLButtonElement>('[data-claim-remove]');
    if (removeClaim) {
      event.preventDefault();
      const host = removeClaim.closest<HTMLElement>('.ll-claim-layers');
      const caseId = host?.dataset.claimCase || host?.closest('tr')?.dataset.id || '';
      if (!caseId) return;
      openCounterclaimRows.delete(caseId);
      if (host?.closest('#llOverviewForm')) {
        removeClaim.closest('.ll-court-row')?.remove();
        const claimRow = host.querySelector('.ll-court-row');
        if (claimRow && !claimRow.querySelector('[data-claim-add]')) claimRow.insertAdjacentHTML('beforeend', `<button type="button" class="ll-court-add" data-claim-add title="添加反请求" aria-label="添加反请求">${ledgerPlusIcon()}</button>`);
        scheduleSave();
        return;
      }
      queueLocalListPatch(caseId, {counterclaimSummary: ''});
      return;
    }
    const addResult = target.closest<HTMLButtonElement>('[data-result-add]');
    if (addResult) {
      event.preventDefault();
      const host = addResult.closest<HTMLElement>('.ll-result-layers');
      const caseId = host?.dataset.resultCase || host?.closest('tr')?.dataset.id || '';
      if (!host || !caseId) return;
      openCounterResultRows.add(caseId);
      if (host.closest('#llOverviewForm')) {
        const labels = litigationJudgmentResultLabels(cases.find((entry) => entry.id === caseId) || {disputeType: 'litigation', instance: 'first', enforcementStatus: 'none'});
        if (!host.querySelector('[data-result-part="counter"]')) host.insertAdjacentHTML('beforeend', resultRowMarkup(labels.counter, 'counter', '', true, false, true, labels.removeTitle));
        addResult.remove();
        scheduleSave();
        return;
      }
      renderList(false);
      return;
    }
    const removeResult = target.closest<HTMLButtonElement>('[data-result-remove]');
    if (removeResult) {
      event.preventDefault();
      const host = removeResult.closest<HTMLElement>('.ll-result-layers');
      const caseId = host?.dataset.resultCase || host?.closest('tr')?.dataset.id || '';
      if (!caseId) return;
      openCounterResultRows.delete(caseId);
      if (host?.closest('#llOverviewForm')) {
        removeResult.closest('.ll-court-row')?.remove();
        const primaryRow = host.querySelector('.ll-court-row');
        const labels = litigationJudgmentResultLabels(cases.find((entry) => entry.id === caseId) || {disputeType: 'litigation', instance: 'first', enforcementStatus: 'none'});
        if (primaryRow && !primaryRow.querySelector('[data-result-add]')) primaryRow.insertAdjacentHTML('beforeend', `<button type="button" class="ll-court-add" data-result-add title="${escapeHtml(labels.addTitle)}" aria-label="${escapeHtml(labels.addTitle)}">${ledgerPlusIcon()}</button>`);
        scheduleSave();
        return;
      }
      queueLocalListPatch(caseId, {counterclaimResult: ''});
      return;
    }
    const addAmount = target.closest<HTMLButtonElement>('[data-amount-add]');
    if (addAmount) {
      event.preventDefault();
      const host = addAmount.closest<HTMLElement>('.ll-amount-layers');
      const caseId = host?.dataset.amountCase || host?.closest('tr')?.dataset.id || '';
      if (!host || !caseId) return;
      openCounterAmountRows.add(caseId);
      if (host.closest('#llOverviewForm')) {
        const item = cases.find((entry) => entry.id === caseId);
        const labels = litigationInvolvedAmountLabels(item || {disputeType: 'litigation', instance: 'first', enforcementStatus: 'none'});
        if (!host.querySelector('[data-amount-part="counter"]')) host.insertAdjacentHTML('beforeend', amountRowMarkup(labels.counter, 'counter', '', true, false, true));
        addAmount.remove();
        scheduleSave();
        return;
      }
      renderList(false);
      return;
    }
    const removeAmount = target.closest<HTMLButtonElement>('[data-amount-remove]');
    if (removeAmount) {
      event.preventDefault();
      const host = removeAmount.closest<HTMLElement>('.ll-amount-layers');
      const caseId = host?.dataset.amountCase || host?.closest('tr')?.dataset.id || '';
      if (!caseId) return;
      openCounterAmountRows.delete(caseId);
      if (host?.closest('#llOverviewForm')) {
        removeAmount.closest('.ll-court-row')?.remove();
        const item = cases.find((entry) => entry.id === caseId);
        const labels = litigationInvolvedAmountLabels(item || {disputeType: 'litigation', instance: 'first', enforcementStatus: 'none'});
        const primaryRow = host.querySelector('.ll-court-row');
        if (primaryRow && !primaryRow.querySelector('[data-amount-add]')) {
          primaryRow.insertAdjacentHTML('beforeend', `<button type="button" class="ll-court-add" data-amount-add title="${escapeHtml(labels.addTitle)}" aria-label="${escapeHtml(labels.addTitle)}">${ledgerPlusIcon()}</button>`);
        }
        scheduleSave();
        return;
      }
      queueLocalListPatch(caseId, {counterclaimAmount: 0});
      return;
    }
    const addAward = target.closest<HTMLButtonElement>('[data-award-add]');
    if (addAward) {
      event.preventDefault();
      const host = addAward.closest<HTMLElement>('.ll-award-layers');
      const caseId = host?.dataset.awardCase || host?.closest('tr')?.dataset.id || '';
      if (!host || !caseId) return;
      openCounterAwardRows.add(caseId);
      if (host.closest('#llOverviewForm')) {
        const item = cases.find((entry) => entry.id === caseId);
        const labels = litigationAwardedAmountLabels(item || {disputeType: 'litigation', instance: 'first', enforcementStatus: 'none'}, activeFieldRuleOverrides?.awardedAmount?.rowLabels);
        if (!host.querySelector('[data-award-part="counter"]')) host.insertAdjacentHTML('beforeend', awardRowMarkup(labels.counter, 'counter', '', true, false, true, labels.removeTitle));
        addAward.remove();
        scheduleSave();
        return;
      }
      renderList(false);
      return;
    }
    const removeAward = target.closest<HTMLButtonElement>('[data-award-remove]');
    if (removeAward) {
      event.preventDefault();
      const host = removeAward.closest<HTMLElement>('.ll-award-layers');
      const caseId = host?.dataset.awardCase || host?.closest('tr')?.dataset.id || '';
      if (!caseId) return;
      openCounterAwardRows.delete(caseId);
      if (host?.closest('#llOverviewForm')) {
        removeAward.closest('.ll-court-row')?.remove();
        const item = cases.find((entry) => entry.id === caseId);
        const labels = litigationAwardedAmountLabels(item || {disputeType: 'litigation', instance: 'first', enforcementStatus: 'none'}, activeFieldRuleOverrides?.awardedAmount?.rowLabels);
        const primaryRow = host.querySelector('.ll-court-row');
        if (primaryRow && !primaryRow.querySelector('[data-award-add]')) {
          primaryRow.insertAdjacentHTML('beforeend', `<button type="button" class="ll-court-add" data-award-add title="${escapeHtml(labels.addTitle)}" aria-label="${escapeHtml(labels.addTitle)}">${ledgerPlusIcon()}</button>`);
        }
        scheduleSave();
        return;
      }
      queueLocalListPatch(caseId, {counterclaimAwardedAmount: 0});
      return;
    }
    const addVenue = target.closest<HTMLButtonElement>('[data-venue-add]');
    if (addVenue) {
      event.preventDefault();
      const host = addVenue.closest<HTMLElement>('.ll-venue-layers');
      if (!host) return;
      const rowEl = addVenue.closest<HTMLElement>('.ll-court-row');
      const rows = readVenueRows(host);
      const used = new Set(rows.map((row) => row.layer));
      const caseIdForLayers = host.closest('tr')?.dataset.id || '';
      const caseItem = cases.find((entry) => entry.id === caseIdForLayers);
      const layerContext = caseItem || layerContextFromForm(host);
      const allowedLayers = litigationVenueLayersForDispute(layerContext);
      const nextLayer = allowedLayers.find((layer) => !used.has(layer.id))?.id || allowedLayers[0]?.id || 'first';
      const index = rowEl ? [...host.querySelectorAll<HTMLElement>('.ll-court-row')].indexOf(rowEl) : rows.length - 1;
      const next = rows.slice();
      next.splice(Math.max(0, index) + 1, 0, {layer: nextLayer, value: ''});
      const caseId = host.closest('tr')?.dataset.id || '';
      if (caseId) queueLocalListPatch(caseId, {venues: next, venue: litigationVenueSummary(next)});
      else {
        rowEl?.insertAdjacentHTML('afterend', venueRowMarkup(nextLayer, '', true, true, true, layerContext));
        bindLitigationCourtPicker(host, notice);
        if (host.closest('#llOverviewForm')) scheduleSave();
      }
      return;
    }
    const removeVenue = target.closest<HTMLButtonElement>('[data-venue-remove]');
    if (removeVenue) {
      event.preventDefault();
      const host = removeVenue.closest<HTMLElement>('.ll-venue-layers');
      const rowEl = removeVenue.closest<HTMLElement>('.ll-court-row');
      if (!host || !rowEl) return;
      const rows = readVenueRows(host);
      const index = [...host.querySelectorAll<HTMLElement>('.ll-court-row')].indexOf(rowEl);
      if (index <= 0) return;
      const next = rows.filter((_, rowIndex) => rowIndex !== index);
      const caseId = host.closest('tr')?.dataset.id || '';
      if (caseId) queueLocalListPatch(caseId, {venues: next, venue: litigationVenueSummary(next)});
      else {
        rowEl.remove();
        if (host.closest('#llOverviewForm')) scheduleSave();
      }
      return;
    }
    if (listSheet === 'cases') {
      const caseRow = target.closest<HTMLTableRowElement>('#llRows tr[data-id]');
      if (caseRow?.dataset.id && !target.closest('input, button, a, select, textarea, .ll-cell-inplace, .ll-cell-inplace-stage, .ll-cell-cause, [data-action], .ll-row-insert-btn')) {
        detailCaseId = '';
        selected = caseRow.dataset.id;
        rememberCase(selected);
        renderList(false);
        return;
      }
    }
    const inplace = target.closest<HTMLElement>('.ll-cell-inplace');
    if (inplace?.dataset.inplaceField && inplace.dataset.inplaceCase) {
      if (litigationFieldRuleKindInfo(resolveLitigationFieldRule(inplace.dataset.inplaceField, activeFieldRuleOverrides).ruleKind)?.readOnly) return;
      if (inplace.classList.contains('is-editing')) return;
      event.stopPropagation();
      inplace.closest('td')?.classList.add('is-excel-editing');
      inplace.classList.add('is-editing');
      inplace.setAttribute('contenteditable', 'true');
      if (inplace.textContent === '—' || inplace.textContent === '–') inplace.textContent = '';
      const range = document.createRange();
      range.selectNodeContents(inplace);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      inplace.focus();
      return;
    }
    const action = target.closest<HTMLElement>('[data-action]')?.dataset.action;
    const backgroundId = target.closest<HTMLElement>('[data-background]')?.dataset.background;
    if (backgroundId) {
      event.preventDefault();
      event.stopPropagation();
      openBackgroundDialog(backgroundId);
      return;
    }
    const progressViewId = target.closest<HTMLElement>('[data-progress-view]')?.dataset.progressView;
    if (progressViewId) {
      event.preventDefault();
      event.stopPropagation();
      openProgressDialog(progressViewId, true);
      return;
    }
    const progressId = target.closest<HTMLElement>('[data-progress]')?.dataset.progress;
    if (progressId) {
      selected = progressId;
      rememberCase(selected);
      renderList(false);
      openProgressDialog(progressId);
      return;
    }
    if (action === 'open-detail') {
      const caseId = target.closest<HTMLElement>('[data-case-id]')?.dataset.caseId || '';
      if (!caseId) return;
      selected = caseId;
      detailCaseId = caseId;
      rememberCase(caseId);
      renderList(false);
      renderDetail(cases.find((item) => item.id === caseId));
      return;
    }
    const tabId = target.closest<HTMLElement>('[data-tab]')?.dataset.tab as DetailTab | undefined;
    if (tabId) {
      tab = tabId;
      renderDetail(cases.find((item) => item.id === selected));
      return;
    }
    const sheetKind = target.closest<HTMLElement>('[data-sheet]')?.dataset.sheet as LitigationSheetKind | undefined;
    if (sheetKind && LITIGATION_DEFAULT_SHEETS.some((sheet) => sheet.kind === sheetKind)) {
      listSheet = sheetKind;
      closeColumnFilterMenu();
      page.querySelectorAll<HTMLElement>('[data-sheet]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.sheet === listSheet);
      });
      page.querySelector('#llColumnPicker')?.classList.toggle('is-hidden', listSheet !== 'cases');
      renderTableHead();
      renderList(false);
      return;
    }
    if (action === 'insert-case-after') {
      const insertBtn = target.closest<HTMLButtonElement>('[data-action="insert-case-after"]');
      const afterId = insertBtn?.dataset.after || '';
      void createInlineCase(afterId).catch((error) => notice(error instanceof Error ? error.message : '新增失败', true));
      return;
    }
    if (target.id === 'llCreate') {
      startNewWorkbook();
      return;
    }
    if (action === 'add-case') {
      void createInlineCase().catch((error) => notice(error instanceof Error ? error.message : '新增失败', true));
      return;
    }
    if (action === 'clear-filters') {
      search.value = '';
      columnFilters.clear();
      syncColumnFilterButton();
      closeColumnFilterMenu();
      renderList();
      return;
    }
    if (action === 'retry') { void load(); return; }
    if (action === 'dismiss-detail' || action === 'back' || action === 'cancel-create') {
      dismissDetail();
      return;
    }
    if (action === 'add-party') {
      const host = detail.querySelector('#llParties');
      host?.insertAdjacentHTML('beforeend', partyRow({id: `party_${Date.now()}`, role: 'opponent', name: '', contact: ''}));
      scheduleSave();
      return;
    }
    if (action === 'remove-party') {
      target.closest('[data-party]')?.remove();
      scheduleSave();
      return;
    }
    if (action === 'add-progress') {
      const item = cases.find((entry) => entry.id === selected);
      if (!item) return;
      const date = (target.closest('form') ?? detail).querySelector<HTMLInputElement>('[data-progress-date]')?.value
        || localProgressDate();
      const content = (target.closest('form') ?? detail).querySelector<HTMLTextAreaElement>('[data-progress-content]')?.value.trim() || '';
      if (!content) {
        notice('请填写进展内容', true);
        return;
      }
      item.progressEntries = [...(item.progressEntries || []), {
        id: `progress_${Date.now()}`,
        date,
        content,
        createdAt: new Date().toISOString(),
      }];
      void persistProgress(item);
      return;
    }
    if (action === 'remove-progress') {
      const item = cases.find((entry) => entry.id === selected);
      const entryId = target.closest<HTMLElement>('[data-progress-id]')?.dataset.progressId;
      if (!item || !entryId) return;
      item.progressEntries = (item.progressEntries || []).filter((entry) => entry.id !== entryId);
      void persistProgress(item);
      return;
    }
    if (action === 'add-node') {
      const item = cases.find((entry) => entry.id === selected);
      if (!item) return;
      item.nodes.push({id: `node_${Date.now()}`, name: '自定义节点', plannedDate: '', completedDate: '', note: '', keyDeadline: true, deadlineKind: 'custom'});
      void persistNodes(item);
      return;
    }
    if (action === 'remove-node') {
      const item = cases.find((entry) => entry.id === selected);
      const nodeId = target.closest<HTMLElement>('[data-node]')?.dataset.node;
      if (!item || !nodeId) return;
      item.nodes = item.nodes.filter((node) => node.id !== nodeId);
      void persistNodes(item);
      return;
    }
    if (action === 'close' || action === 'reopen') {
      const item = cases.find((entry) => entry.id === selected);
      if (!item) return;
      void (async () => {
        const result = await patchCase(item.id, {closed: action === 'close', stageName: action === 'close' ? '已结案' : undefined});
        replaceCase(result.case);
        renderList();
        notice(action === 'close' ? '案件已结案' : '已恢复为进行中');
      })().catch((error) => notice(error instanceof Error ? error.message : '操作失败', true));
      return;
    }
    if (action === 'delete-case') {
      const item = cases.find((entry) => entry.id === selected);
      if (!item || !canDeleteCaseInList(item)) return;
      void (async () => {
        await deleteCaseRow(item.id);
        if (!cases.some(entry => entry.id === item.id)) { detailCaseId = ''; setDrawerOpen(false); }
      })().catch((error) => notice(error instanceof Error ? error.message : '删除失败', true));
      return;
    }
    if (action === 'link-contract') {
      const projectId = detail.querySelector<HTMLSelectElement>('#llContractSelect')?.value;
      const item = cases.find((entry) => entry.id === selected);
      const contract = contracts.find((entry) => entry.projectId === projectId);
      if (!item || !contract) return;
      if (item.linkedContracts.some((entry) => entry.projectId === contract.projectId)) return;
      item.linkedContracts = [...item.linkedContracts, contract];
      void persistLinks(item);
      return;
    }
    if (action === 'unlink-contract') {
      const projectId = target.closest<HTMLElement>('[data-link]')?.dataset.link;
      const item = cases.find((entry) => entry.id === selected);
      if (!item || !projectId) return;
      item.linkedContracts = item.linkedContracts.filter((entry) => entry.projectId !== projectId);
      void persistLinks(item);
      return;
    }
    if (action === 'open-contract') {
      const link = target.closest<HTMLElement>('[data-link]');
      if (!link) return;
      navigate(link.dataset.archived === '1' ? '/tax-assistant/legal-review/deliverables' : '/tax-assistant/legal-review');
      return;
    }
    if (action === 'remove-file') {
      const fileId = target.closest<HTMLElement>('[data-file]')?.dataset.file;
      const item = cases.find((entry) => entry.id === selected);
      if (!item || !fileId) return;
      void (async () => {
        const result = await api.delete<{case: LedgerCase}>(apiUrl(`${base}/cases/${encodeURIComponent(item.id)}/materials/${encodeURIComponent(fileId)}`));
        const current = cases.find((entry) => entry.id === item.id);
        if (current) replaceCase(mergeLitigationCaseResponse(current, result.case, {materials: true}, pendingPatches.get(item.id)));
        serverCasesById.set(item.id, cloneLedgerCase(result.case));
        renderList();
      })().catch((error) => notice(error instanceof Error ? error.message : '删除材料失败', true));
      return;
    }
    if (action === 'download-file') {
      const fileId = target.closest<HTMLElement>('[data-file]')?.dataset.file;
      if (fileId) window.open(apiUrl(`${base}/cases/${encodeURIComponent(selected)}/materials/${encodeURIComponent(fileId)}/content`), '_blank', 'noopener');
      return;
    }
    if (action === 'preview-file') {
      const fileEl = target.closest<HTMLElement>('[data-file]');
      const name = fileEl?.dataset.name || '';
      const fileId = fileEl?.dataset.file;
      if (!fileId) return;
      void previewFile(fileId, name);
      return;
    }
  });
  page.addEventListener('click', (event) => {
    const action = (event.target as HTMLElement).closest('[data-action]')?.getAttribute('data-action');
    if (action === 'dismiss-detail' || action === 'back' || action === 'cancel-create') {
      event.preventDefault();
      event.stopPropagation();
      dismissDetail();
    }
  }, true);
  page.querySelector('#llColumnPicker')?.addEventListener('click', () => openColumnPicker());
  page.querySelector('#llColumnFilter')?.addEventListener('click', () => {
    columnFilterArmed = !columnFilterArmed;
    if (!columnFilterArmed) closeColumnFilterMenu();
    syncColumnFilterButton();
    renderTableHead();
  });
  page.querySelector('#llSearchButton')?.addEventListener('click', () => renderList());
  page.querySelector('#llDeadlines')?.addEventListener('click', () => {
    void (async () => {
      const payload = await api.get<{items: Array<{internalNo: string; title: string; nextDeadline: string; tone: string; ownerName: string}>}>(apiUrl(`${base}/deadlines`));
      const host = page.querySelector<HTMLElement>('#llDialogHost')!;
      host.innerHTML = `<dialog class="la-dialog" open><header><h2>期限队列</h2><button class="la-dialog-close" type="button" data-close>×</button></header>
        <div class="la-dialog-body">${payload.items.length ? `<ul>${payload.items.map((item) => `<li>${escapeHtml(item.internalNo)} ${escapeHtml(item.title)} · ${escapeHtml(item.nextDeadline)} · ${item.tone === 'overdue' ? '已逾期' : '7日内'}</li>`).join('')}</ul>` : '<p>当前可见范围内没有逾期或临近期限。</p>'}</div>
        <footer><button type="button" class="la-secondary" data-close>关闭</button></footer></dialog>`;
      host.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => host.querySelector('dialog')?.close()));
    })().catch((error) => notice(error instanceof Error ? error.message : '期限队列加载失败', true));
  });
  page.querySelector('#llLoadWorkbook')?.addEventListener('click', () => {
    void (async () => {
      const host = page.querySelector<HTMLElement>('#llDialogHost')!;
      const payload = await api.get<{workbooks: LitigationWorkbookCard[]}>(apiUrl('/api/legal-litigation/workbooks'));
      const books = payload.workbooks;
      const rows = books.length
        ? books.map((book) => {
          const current = book.id === workbookId;
          return `<label class="ll-load-row${current ? ' is-current' : ''}"><input type="radio" name="llLoadBook" value="${escapeHtml(book.id)}" ${current ? 'checked' : ''}><span><b>${escapeHtml(book.name)}</b><small>${book.scope === 'team' ? '团队' : '个人'} · ${escapeHtml(book.updatedAt.slice(0, 16).replace('T', ' '))}${current ? ' · 当前台账' : ''}</small></span></label>`;
        }).join('')
        : '<p class="ll-load-empty">当前空间还没有可载入的诉讼台账。</p>';
      host.innerHTML = `<dialog class="la-dialog ll-load-dialog" open aria-label="载入诉讼台账"><header><h2>载入诉讼台账</h2><button class="la-dialog-close" type="button" data-close>×</button></header>
        <div class="la-dialog-body"><p class="ll-load-hint">选择要打开的诉讼管理台账。</p><div class="ll-load-list">${rows}</div></div>
        <footer><button type="button" class="la-secondary" data-close>取消</button><button type="button" class="la-primary" id="llConfirmLoad" ${books.length ? '' : 'disabled'}>载入</button></footer></dialog>`;
      const dialog = host.querySelector('dialog');
      host.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => dialog?.close()));
      host.querySelector('#llConfirmLoad')?.addEventListener('click', () => {
        const selectedId = host.querySelector<HTMLInputElement>('input[name="llLoadBook"]:checked')?.value || '';
        if (!selectedId || selectedId === workbookId) {
          dialog?.close();
          return;
        }
        if ((dirty || pendingPatches.size) && !window.confirm('当前台账有未保存的修改，载入其他台账后这些修改不会保留。仍要载入吗？')) return;
        dialog?.close();
        navigate(`/tax-assistant/legal-litigation/${encodeURIComponent(selectedId)}`);
      });
    })().catch((error) => notice(error instanceof Error ? error.message : '无法载入台账列表', true));
  });
  /**
   * 协作单位在下拉框里的名称。
   * @param unit 协作单位
   */
  const collaborationUnitLabel = (unit: LitigationPlanCollaborationUnit) => {
    const titled = unit.shortName ? `${unit.name}（${unit.shortName}）` : unit.name;
    return unit.code ? `${titled} ${unit.code}` : titled;
  };

  /**
   * 读取方案上的协作设定。非创建者看到的是台账副本，不能改。
   */
  const readCollaborationSettings = async (): Promise<{settings: NonNullable<LitigationListPayload['mapping']>; isCreator: boolean}> => {
    if (!planSettings?.id) throw new Error('当前台账还没有关联方案');
    let isCreator = !planSettings.createdBy || planSettings.createdBy === access.currentUserId;
    try {
      const payload = await api.get<{
        plan: NonNullable<LitigationListPayload['mapping']>;
        viewer?: {isCreator?: boolean};
      }>(apiUrl(`/api/legal-litigation/plans/${encodeURIComponent(planSettings.id)}`));
      isCreator = payload.viewer?.isCreator !== false;
      if (isCreator && payload.plan) {
        planSettings = {
          ...planSettings,
          ...payload.plan,
          id: payload.plan.id || planSettings.id,
          organizationLabelKind: parseLitigationOrganizationLabelKind(String(payload.plan.organizationLabelKind || planSettings.organizationLabelKind || 'name')),
          collaborationUnits: payload.plan.collaborationUnits || planSettings.collaborationUnits || [],
          teamEditors: payload.plan.teamEditors || planSettings.teamEditors || [],
        };
      }
    } catch { /* 方案接口读不到时，仍用台账副本里的设定。 */ }
    return {settings: planSettings, isCreator};
  };

  /**
   * 把协作设定写回方案，并同步到当前台账副本。
   * @param patch 本次修改的字段
   */
  const saveCollaborationSettings = async (patch: Record<string, unknown>) => {
    if (!planSettings?.id) throw new Error('当前台账还没有关联方案');
    await api.patch(apiUrl(`/api/legal-litigation/plans/${encodeURIComponent(planSettings.id)}`), patch);
    const saved = await api.patch<{mapping?: NonNullable<LitigationListPayload['mapping']>}>(apiUrl(`${base}/mapping`), patch);
    planSettings = {...planSettings, ...patch, ...(saved.mapping || {}), id: planSettings.id};
  };

  /**
   * 打开一项协作设定。归属设定含管理员单位和台账显示；协作设定含单位、人员与区段。
   * @param choice 要打开的弹窗
   */
  const openCollaborationChoice = async (choice: 'admin' | 'team') => {
    const host = page.querySelector<HTMLElement>('#llDialogHost')!;
    const {settings, isCreator} = await readCollaborationSettings();
    const locked = !isCreator;
    const lockAttr = locked ? 'disabled' : '';
    const titles = {
      admin: '台账管理员归属设定',
      team: '协作单位及用户设置',
    } as const;
    let units = (settings.collaborationUnits || []).map((item) => ({...item}));
    let editors = (settings.teamEditors || []).map((item) => ({...item}));
    const selectableUnits = () => [{
      id: LITIGATION_CREATOR_UNIT_ID,
      name: settings.creatorOrganizationName || auth.organization.name,
      shortName: settings.creatorOrganizationShortName || '',
      code: settings.creatorOrganizationCode || '',
    }, ...units.filter(item => item.name && item.shortName && item.code)];
    /**
     * 协作单位列表。
     */
    const unitRows = () => (units.length
      ? units.map((unit) => `<div class="ll-collab-unit" data-unit-id="${escapeHtml(unit.id)}">
          <input data-unit-name maxlength="200" placeholder="单位名称" aria-label="单位名称" value="${escapeHtml(unit.name)}" ${lockAttr}>
          <input data-unit-short maxlength="40" placeholder="简称" aria-label="简称" value="${escapeHtml(unit.shortName)}" ${lockAttr}>
          <input data-unit-code maxlength="40" placeholder="代码" aria-label="代码" value="${escapeHtml(unit.code)}" ${lockAttr}>
          <button type="button" class="ll-collab-remove" data-remove-unit ${lockAttr}>移除</button>
        </div>`).join('')
      : '<p class="ll-collab-empty">暂无协作单位</p>');
    /**
     * 从对话框读回协作单位。
     */
    const readUnitsFromDialog = () => [...host.querySelectorAll<HTMLElement>('[data-unit-id]')].map((row) => ({
      id: row.dataset.unitId || '',
      name: row.querySelector<HTMLInputElement>('[data-unit-name]')?.value.trim() || '',
      shortName: row.querySelector<HTMLInputElement>('[data-unit-short]')?.value.trim() || '',
      code: row.querySelector<HTMLInputElement>('[data-unit-code]')?.value.trim() || '',
    }));
    /**
     * 从对话框读回协作人员。
     */
    const readEditorsFromDialog = () => [...host.querySelectorAll<HTMLElement>('[data-editor-id]')].map((row) => {
      const userId = row.querySelector<HTMLSelectElement>('[data-editor-user]')?.value || row.dataset.editorId || '';
      const member = eligibleMembers.find((item) => item.userId === userId);
      const known = editors.find((item) => item.userId === userId);
      return {
        userId,
        displayName: member?.displayName || known?.displayName || '',
        unitId: row.querySelector<HTMLSelectElement>('[data-editor-unit]')?.value || '',
      };
    });
    const participantById = new Map(access.participants.map((item) => [item.userId, item]));
    const orderedMembers = [...eligibleMembers].sort((a, b) => {
      const left = participantById.get(a.userId)?.order ?? Number.MAX_SAFE_INTEGER;
      const right = participantById.get(b.userId)?.order ?? Number.MAX_SAFE_INTEGER;
      return left - right || a.displayName.localeCompare(b.displayName, 'zh-CN');
    });
    const participantRows = orderedMembers.map((member) => {
      const included = participantById.has(member.userId);
      const creator = member.userId === access.createdBy;
      const admin = access.adminUserIds.includes(member.userId);
      return `<div class="ll-collaboration-row" data-collaborator="${escapeHtml(member.userId)}">
        <label><input type="checkbox" data-participant ${included ? 'checked' : ''} ${creator ? 'disabled' : ''}><span>${escapeHtml(member.displayName)}</span>${creator ? '<em>创建者</em>' : ''}</label>
        <label class="ll-admin-check"><input type="checkbox" data-admin ${admin ? 'checked' : ''} ${creator || !included ? 'disabled' : ''}>管理员</label>
        <div><button type="button" data-move="-1" aria-label="上移">↑</button><button type="button" data-move="1" aria-label="下移">↓</button></div>
      </div>`;
    }).join('');
    /**
     * 协作人员列表。
     */
    const editorRows = () => {
      if (!editors.length) return '<p class="ll-collab-empty">暂无协作人员</p>';
      const memberOptions = (userId: string) => eligibleMembers
        .filter((item) => item.userId !== settings.createdBy)
        .map((item) => `<option value="${escapeHtml(item.userId)}" ${item.userId === userId ? 'selected' : ''}>${escapeHtml(item.displayName)}</option>`)
        .join('');
      const unitOptions = (unitId: string) => selectableUnits()
        .map((item) => `<option value="${escapeHtml(item.id)}" ${item.id === unitId ? 'selected' : ''}>${escapeHtml(collaborationUnitLabel(item))}</option>`)
        .join('');
      return editors.map((editor) => `<div class="ll-collab-person" data-editor-id="${escapeHtml(editor.userId)}">
        <select data-editor-user aria-label="协作人员" ${lockAttr}>${memberOptions(editor.userId) || `<option value="${escapeHtml(editor.userId)}">${escapeHtml(editor.displayName)}</option>`}</select>
        <select data-editor-unit aria-label="所属协作单位" ${lockAttr}>${unitOptions(editor.unitId) || '<option value="">请先添加协作单位</option>'}</select>
        <button type="button" class="ll-collab-remove" data-remove-editor ${lockAttr}>移除</button>
      </div>`).join('');
    };
    const body = choice === 'admin'
      ? `<section class="ll-collab-section"><h3>台账管理员所属单位显示内容</h3><div class="ll-collab-admin">
          <input id="llCollabOrgName" maxlength="200" placeholder="所属单位" aria-label="台账管理员所属单位显示内容" value="${escapeHtml(settings.creatorOrganizationName || '')}" ${lockAttr}>
          <input id="llCollabOrgShort" maxlength="40" placeholder="简称" aria-label="简称" value="${escapeHtml(settings.creatorOrganizationShortName || '')}" ${lockAttr}>
          <input id="llCollabOrgCode" maxlength="40" placeholder="代码" aria-label="代码" value="${escapeHtml(settings.creatorOrganizationCode || '')}" ${lockAttr}>
        </div></section>
        <section class="ll-collab-section"><h3>台账中的所属单位显示规则</h3>
          <div class="ll-collab-segment" role="radiogroup" aria-label="台账中的所属单位显示规则">
            ${(Object.keys(LITIGATION_ORGANIZATION_LABEL_KIND_LABELS) as LitigationOrganizationLabelKind[]).map((kind) => `<label><input type="radio" name="llOrgLabelKind" value="${kind}" ${parseLitigationOrganizationLabelKind(String(settings.organizationLabelKind || 'name')) === kind ? 'checked' : ''} ${lockAttr}><span>${escapeHtml(LITIGATION_ORGANIZATION_LABEL_KIND_LABELS[kind])}</span></label>`).join('')}
          </div>
          <p class="ll-collab-hint">所属单位按方案管理员选择的规则自动填入，团队成员不能改。</p>
        </section>`
      : `<section class="ll-collab-section"><h3>协作单位</h3>
          <div id="llCollabUnitList">${unitRows()}</div>
          <div class="ll-collab-unit">
            <input id="llCollabUnitName" maxlength="200" placeholder="单位名称" aria-label="单位名称" ${lockAttr}>
            <input id="llCollabUnitShort" maxlength="40" placeholder="简称" aria-label="简称" ${lockAttr}>
            <input id="llCollabUnitCode" maxlength="40" placeholder="代码" aria-label="代码" ${lockAttr}>
            <button type="button" class="secondary-button" id="llCollabAddUnit" ${lockAttr}>添加</button>
          </div>
        </section>
        <section class="ll-collab-section"><h3>协作人员</h3>
          <div id="llCollabEditorList">${editorRows()}</div>
          <div class="ll-collab-person">
            <select id="llCollabMemberPick" aria-label="选择团队成员" ${lockAttr}></select>
            <select id="llCollabMemberUnit" aria-label="所属协作单位" ${lockAttr}></select>
            <button type="button" class="secondary-button" id="llCollabAddEditor" ${lockAttr}>添加</button>
          </div>
          <p class="ll-collab-hint" id="llCollabEditorHint"></p>
        </section>
        <section class="ll-collab-section"><h3>区段设置</h3>
          <p class="ll-collab-hint">每位参与人的案件构成一个动态连续行区段；新增案件只追加在本人区段末尾。</p>
          <div class="ll-collaboration-list">${participantRows}</div>
          <p class="ll-hint">移除仍有案件的参与人会被拒绝；请先由其处理完本人区段。</p>
        </section>`;
    host.innerHTML = `<dialog class="la-dialog ll-collaboration-dialog" aria-label="${escapeHtml(titles[choice])}">
      <header><div><h2>${escapeHtml(titles[choice])}</h2>${choice === 'admin' && locked ? '<p>方案创建者选定后，团队成员不能改。</p>' : ''}</div><button class="la-dialog-close" type="button" data-close>×</button></header>
      <div class="la-dialog-body"><div class="ll-collab-form">${body}</div></div>
      <footer><button type="button" class="la-secondary" data-close>取消</button>${choice === 'admin' && locked ? '' : '<button type="button" class="la-primary" data-save-collab>保存</button>'}</footer>
    </dialog>`;
    const modal = host.querySelector<HTMLDialogElement>('dialog')!;
    modal.showModal();
    const close = () => modal.close();
    modal.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', close));
    /**
     * 刷新协作人员下拉框。
     */
    const paintEditors = () => {
      const list = modal.querySelector('#llCollabEditorList');
      if (list) list.innerHTML = editorRows();
      const pick = modal.querySelector<HTMLSelectElement>('#llCollabMemberPick');
      const unitPick = modal.querySelector<HTMLSelectElement>('#llCollabMemberUnit');
      const used = new Set(editors.map((item) => item.userId));
      const options = eligibleMembers.filter((item) => item.userId !== settings.createdBy && !used.has(item.userId));
      if (pick) {
        pick.innerHTML = options.length
          ? `<option value="">选择本团队成员</option>${options.map((item) => `<option value="${escapeHtml(item.userId)}">${escapeHtml(item.displayName)}</option>`).join('')}`
          : '<option value="">没有可添加的成员</option>';
        pick.disabled = locked || options.length === 0;
      }
      if (unitPick) {
        const ready = selectableUnits();
        unitPick.innerHTML = ready.length
          ? `<option value="">选择协作单位</option>${ready.map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(collaborationUnitLabel(item))}</option>`).join('')}`
          : '<option value="">请先添加协作单位</option>';
        unitPick.disabled = locked || ready.length === 0;
      }
    };
    if (choice === 'team') paintEditors();
    modal.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('#llCollabAddUnit')) {
        const name = modal.querySelector<HTMLInputElement>('#llCollabUnitName')?.value.trim() || '';
        const shortName = modal.querySelector<HTMLInputElement>('#llCollabUnitShort')?.value.trim() || '';
        const code = modal.querySelector<HTMLInputElement>('#llCollabUnitCode')?.value.trim() || '';
        if (!name || !shortName || !code) {
          notice('请填写协作单位名称、简称和代码', true);
          return;
        }
        units = readUnitsFromDialog();
        editors = readEditorsFromDialog();
        units.push({id: `unit_${crypto.randomUUID()}`, name, shortName, code});
        const list = modal.querySelector('#llCollabUnitList');
        if (list) list.innerHTML = unitRows();
        modal.querySelectorAll<HTMLInputElement>('#llCollabUnitName, #llCollabUnitShort, #llCollabUnitCode').forEach((input) => {
          input.value = '';
        });
        paintEditors();
        return;
      }
      const removeUnit = target.closest<HTMLButtonElement>('[data-remove-unit]');
      if (removeUnit) {
        const unitId = removeUnit.closest<HTMLElement>('[data-unit-id]')?.dataset.unitId || '';
        editors = readEditorsFromDialog();
        if (editors.some((item) => item.unitId === unitId)) {
          notice('请先移出该单位下的协作人员', true);
          return;
        }
        units = readUnitsFromDialog().filter((item) => item.id !== unitId);
        const list = modal.querySelector('#llCollabUnitList');
        if (list) list.innerHTML = unitRows();
        paintEditors();
        return;
      }
      if (target.closest('#llCollabAddEditor')) {
        const userId = modal.querySelector<HTMLSelectElement>('#llCollabMemberPick')?.value || '';
        const unitId = modal.querySelector<HTMLSelectElement>('#llCollabMemberUnit')?.value || '';
        const member = eligibleMembers.find((item) => item.userId === userId);
        if (!selectableUnits().some((item) => item.id === unitId)) {
          const hint = modal.querySelector('#llCollabEditorHint');
          if (hint) hint.textContent = '请先添加协作单位';
          return;
        }
        if (!member) {
          const hint = modal.querySelector('#llCollabEditorHint');
          if (hint) hint.textContent = eligibleMembers.some((item) => item.userId !== settings.createdBy) ? '请先选择协作人员' : '当前团队没有其他成员';
          return;
        }
        if (!unitId) {
          const hint = modal.querySelector('#llCollabEditorHint');
          if (hint) hint.textContent = '请选择所属协作单位';
          return;
        }
        const hint = modal.querySelector('#llCollabEditorHint');
        if (hint) hint.textContent = '';
        units = readUnitsFromDialog();
        editors = readEditorsFromDialog();
        editors.push({userId: member.userId, displayName: member.displayName, unitId});
        paintEditors();
        return;
      }
      const removeEditor = target.closest<HTMLButtonElement>('[data-remove-editor]');
      if (removeEditor) {
        const userId = removeEditor.closest<HTMLElement>('[data-editor-id]')?.dataset.editorId || '';
        editors = readEditorsFromDialog().filter((item) => item.userId !== userId);
        paintEditors();
        return;
      }
      const move = target.closest<HTMLButtonElement>('[data-move]');
      const row = move?.closest<HTMLElement>('[data-collaborator]');
      if (!move || !row) return;
      const direction = Number(move.dataset.move);
      const sibling = direction < 0 ? row.previousElementSibling : row.nextElementSibling;
      if (!(sibling instanceof HTMLElement)) return;
      if (direction < 0) sibling.before(row);
      else sibling.after(row);
    });
    modal.addEventListener('change', (event) => {
      const participant = (event.target as HTMLElement).closest<HTMLInputElement>('[data-participant]');
      if (!participant) return;
      const collaborator = participant.closest<HTMLElement>('[data-collaborator]');
      const admin = collaborator?.querySelector<HTMLInputElement>('[data-admin]');
      if (admin) {
        admin.disabled = !participant.checked;
        if (!participant.checked) admin.checked = false;
      }
    });
    modal.querySelector('[data-save-collab]')?.addEventListener('click', () => {
      void (async () => {
        if (choice === 'admin') {
          const name = modal.querySelector<HTMLInputElement>('#llCollabOrgName')?.value.trim() || '';
          const shortName = modal.querySelector<HTMLInputElement>('#llCollabOrgShort')?.value.trim() || '';
          const code = modal.querySelector<HTMLInputElement>('#llCollabOrgCode')?.value.trim() || '';
          const kind = modal.querySelector<HTMLInputElement>('input[name="llOrgLabelKind"]:checked')?.value || 'name';
          if (!name || !shortName || !code) throw new Error('请填写台账管理员所属单位的名称、简称和代码');
          await saveCollaborationSettings({
            creatorOrganizationName: name,
            creatorOrganizationShortName: shortName,
            creatorOrganizationCode: code,
            organizationLabelKind: parseLitigationOrganizationLabelKind(kind),
          });
          modal.close();
          notice('台账管理员归属设定已保存');
          return;
        }
        const editorUserIdsToKeep: string[] = [];
        if (!locked) {
          const draftName = modal.querySelector<HTMLInputElement>('#llCollabUnitName')?.value.trim() || '';
          const draftShort = modal.querySelector<HTMLInputElement>('#llCollabUnitShort')?.value.trim() || '';
          const draftCode = modal.querySelector<HTMLInputElement>('#llCollabUnitCode')?.value.trim() || '';
          const next = readUnitsFromDialog();
          if (draftName || draftShort || draftCode) {
            if (!draftName || !draftShort || !draftCode) throw new Error('请填写协作单位名称、简称和代码');
            next.push({id: `unit_${crypto.randomUUID()}`, name: draftName, shortName: draftShort, code: draftCode});
          }
          const nextEditors = readEditorsFromDialog();
          const draftUserId = modal.querySelector<HTMLSelectElement>('#llCollabMemberPick')?.value || '';
          const draftUnitId = modal.querySelector<HTMLSelectElement>('#llCollabMemberUnit')?.value || '';
          if (draftUserId || draftUnitId) {
            const member = eligibleMembers.find((item) => item.userId === draftUserId);
            if (!member) throw new Error('请先选择协作人员');
            if (!draftUnitId) throw new Error('请选择所属协作单位');
            if (!nextEditors.some((item) => item.userId === member.userId)) {
              nextEditors.push({userId: member.userId, displayName: member.displayName, unitId: draftUnitId});
            }
          }
          if (next.some((item) => !item.name || !item.shortName || !item.code)) throw new Error('请填写每个协作单位的名称、简称和代码');
          if (nextEditors.some((item) => !item.unitId)) throw new Error('请为每位协作人员选择所属协作单位');
          await saveCollaborationSettings({collaborationUnits: next, teamEditors: nextEditors});
          editorUserIdsToKeep.push(...nextEditors.map((item) => item.userId));
        }
        const participantUserIds: string[] = [];
        modal.querySelectorAll<HTMLElement>('[data-collaborator]').forEach((collaborator) => {
          const userId = collaborator.dataset.collaborator || '';
          const participant = collaborator.querySelector<HTMLInputElement>('[data-participant]');
          if (participant?.checked || userId === access.createdBy) participantUserIds.push(userId);
        });
        for (const userId of editorUserIdsToKeep) {
          if (userId && !participantUserIds.includes(userId)) participantUserIds.push(userId);
        }
        await api.patch(apiUrl(base), {editorUserIds: participantUserIds});
        modal.close();
        await load();
        notice('协作单位及用户设置已保存');
      })().catch((error) => notice(error instanceof Error ? error.message : '协作设置保存失败', true));
    });
  };

  page.querySelector('#llCollabAdmin')?.addEventListener('click', () => {
    void openCollaborationChoice('admin').catch((error) => notice(error instanceof Error ? error.message : '无法打开协作设置', true));
  });
  page.querySelector('#llCollabTeam')?.addEventListener('click', () => {
    void openCollaborationChoice('team').catch((error) => notice(error instanceof Error ? error.message : '无法打开协作设置', true));
  });
  page.querySelector('#llBatchDelete')?.addEventListener('click', () => {
    const ids = [...checked].filter((id) => {
      const item = cases.find((entry) => entry.id === id);
      return item && canDeleteCaseInList(item);
    });
    if (!ids.length) {
      notice('没有可删除的案件（仅能删除本人负责的行，或联系台账管理员）', true);
      return;
    }
    if (!window.confirm(`确定删除所选 ${ids.length} 件案件吗？案件登记及关联记录将移除，空间中的材料文件会保留。`)) return;
    void (async () => {
      if (batchDelete) batchDelete.disabled = true;
      const kept = await persistPendingPatches(new Set(ids));
      if (!kept) {
        if (batchDelete) batchDelete.disabled = false;
        return;
      }
      const removed = ids.flatMap((id) => {
        const item = cases.find((entry) => entry.id === id);
        if (!item) return [];
        const index = cases.findIndex((entry) => entry.id === id);
        return [{snapshot: cloneLedgerCase(item), insertAfterCaseId: index > 0 ? cases[index - 1].id : ''}];
      });
      const failures: string[] = [];
      for (const id of ids) {
        try {
          await api.delete(apiUrl(`${base}/cases/${encodeURIComponent(id)}`));
          pendingPatches.delete(id);
          checked.delete(id);
        } catch (error) {
          failures.push(error instanceof Error ? error.message : String(error));
        }
      }
      await load();
      for (const entry of removed.reverse()) {
        pushUndoEntry({kind: 'delete', caseId: entry.snapshot.id, snapshot: entry.snapshot, insertAfterCaseId: entry.insertAfterCaseId});
      }
      if (batchDelete) batchDelete.disabled = false;
      if (failures.length) {
        notice(failures.length === ids.length ? failures[0] : `部分删除失败：${failures[0]}`, true);
        return;
      }
      notice('已删除所选案件');
    })().catch((error) => {
      if (batchDelete) batchDelete.disabled = false;
      notice(error instanceof Error ? error.message : '删除失败', true);
    });
  });
  page.querySelector('#llSaveWorkbook')?.addEventListener('click', () => {
    void saveWorkbookToSpace();
  });
  page.querySelector('#llUndoEdit')?.addEventListener('click', () => {
    void undoLastListEdit();
  });
  page.querySelector('#llRedoEdit')?.addEventListener('click', () => {
    void redoLastListEdit();
  });

  /**
   * 收起台账文件、编辑、协作设置三个下拉菜单。
   */
  const closeToolbarMenus = () => {
    document.querySelectorAll<HTMLElement>('.ll-toolbar-menu-panel').forEach((panel) => {
      panel.hidden = true;
      const home = panel.dataset.menuHome ? page.querySelector<HTMLElement>(`#${panel.dataset.menuHome}`) : null;
      if (home && panel.parentElement !== home) home.appendChild(panel);
    });
    page.querySelectorAll<HTMLButtonElement>('.ll-toolbar-menu > button').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  };

  /**
   * 在按钮下方打开一个工具栏菜单。再点一次则收起。
   * 菜单挂到 body，避免工具栏的横向滚动把下拉裁掉。
   * @param button 菜单按钮
   * @param panel 菜单面板
   */
  const toggleToolbarMenu = (button: HTMLButtonElement, panel: HTMLElement) => {
    const opening = panel.hidden;
    const home = button.parentElement;
    if (home?.id) panel.dataset.menuHome = home.id;
    closeToolbarMenus();
    if (!opening) return;
    document.body.appendChild(panel);
    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    const rect = button.getBoundingClientRect();
    const width = Math.max(panel.offsetWidth, 148);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
    panel.style.left = `${left}px`;
    panel.style.top = `${rect.bottom + 4}px`;
  };

  page.querySelector('#llFileMenu')?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLButtonElement;
    const panel = document.getElementById('llFileMenuPanel');
    if (panel) toggleToolbarMenu(button, panel);
  });
  page.querySelector('#llEditMenu')?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLButtonElement;
    const panel = document.getElementById('llEditMenuPanel');
    syncWorkbookEditControls();
    if (panel) toggleToolbarMenu(button, panel);
  });
  page.querySelector('#llCollaboration')?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLButtonElement;
    const panel = document.getElementById('llCollaborationPanel');
    if (panel) toggleToolbarMenu(button, panel);
  });
  page.querySelectorAll('.ll-toolbar-menu-item').forEach((item) => {
    item.addEventListener('click', (event) => {
      event.stopPropagation();
      closeToolbarMenus();
    });
  });
  page.querySelector('#llCreate')?.addEventListener('click', () => startNewWorkbook());
  const onDocumentClick = (event: Event) => {
    if (!page.isConnected) {
      document.removeEventListener('click', onDocumentClick);
      return;
    }
    const hit = event.target as HTMLElement;
    if (hit.closest?.('.ll-insert-menu, [data-insert-corner]')) return;
    closeInsertMenu();
    if (hit.closest?.('.ll-toolbar-menu, .ll-toolbar-menu-panel')) return;
    closeToolbarMenus();
  };
  document.addEventListener('click', onDocumentClick);
  page.querySelector('.invoice-page-head')?.addEventListener('scroll', () => closeToolbarMenus());

  /**
   * 填写当前用户的台账标题。创建者还可以指定标题行。
   */
  const openExportTitleDialog = () => {
    const host = page.querySelector<HTMLElement>('#llDialogHost')!;
    const headerRow = Math.max(1, exportTitle.headerRow || 1);
    const rowEditable = exportTitle.canSetRow && headerRow > 1;
    const rowMarkup = rowEditable
      ? `<label class="ll-export-title-field">标题所在行<input class="form-input" id="llExportTitleRow" type="number" min="1" max="${headerRow - 1}" step="1" value="${Math.min(exportTitle.row, headerRow - 1)}"></label>
         <p class="ll-hint">表头在第 ${headerRow} 行。标题写在表头上方。平时不写入台账文件，导出 Excel 时才写上。</p>`
      : exportTitle.canSetRow
        ? '<p class="ll-hint">当前表头就在第 1 行，没有可放置标题的空行。标题可以先记下，但导出时不会盖住表头。</p>'
        : `<p class="ll-hint">标题将写在第 ${exportTitle.row} 行。这一行由创立台账的管理员设定。导出时才写入您自己的标题。</p>`;
    host.innerHTML = `<dialog class="la-dialog" open aria-label="台账标题"><header><h2>台账标题</h2><button class="la-dialog-close" type="button" data-close>×</button></header>
      <div class="la-dialog-body">
        <label class="ll-export-title-field">标题内容<input class="form-input" id="llExportTitleInput" type="text" maxlength="120" value="${escapeHtml(exportTitle.title)}" placeholder="例如：基础公司诉讼台账"></label>
        ${rowMarkup}
      </div>
      <footer><button type="button" class="la-secondary" data-close>取消</button><button type="button" class="la-primary" id="llConfirmExportTitle">确定</button></footer>
    </dialog>`;
    const dialog = host.querySelector('dialog');
    host.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => dialog?.close()));
    host.querySelector<HTMLInputElement>('#llExportTitleInput')?.focus();
    host.querySelector('#llConfirmExportTitle')?.addEventListener('click', () => {
      const title = host.querySelector<HTMLInputElement>('#llExportTitleInput')?.value ?? '';
      const rowInput = host.querySelector<HTMLInputElement>('#llExportTitleRow');
      const body: {title: string; row?: number} = {title};
      if (rowInput) {
        const row = Math.floor(Number(rowInput.value));
        if (!Number.isFinite(row) || row < 1 || row >= headerRow) {
          notice(`标题必须写在表头上方。当前表头在第 ${headerRow} 行`, true);
          return;
        }
        body.row = row;
      }
      void (async () => {
        exportTitle = await api.patch<typeof exportTitle>(apiUrl(`${base}/export-title`), body);
        paintLedgerTitle();
        dialog?.close();
        notice('台账标题已保存。导出 Excel 时会写上');
      })().catch((error) => notice(error instanceof Error ? error.message : '台账标题保存失败', true));
    });
  };
  /**
   * 打开内部案号规则。确定后写回方案，并同步到当前台账。
   */
  const openLedgerInternalNoRule = () => {
    if (!planSettings?.id) {
      notice('当前台账还没有关联方案', true);
      return;
    }
    const locked = Boolean(planSettings.createdBy && planSettings.createdBy !== access.currentUserId && !access.isAdmin);
    const orgCode = String(planSettings.creatorOrganizationCode || internalNoRule.orgCode || '').trim();
    document.getElementById('llInternalNoRuleDialog')?.remove();
    const snapshot = normalizeLitigationInternalNoRule(internalNoRule);
    /**
     * 规则示意用方案管理员的单位代码。
     * @param rule 当前规则
     */
    const previewText = (rule: ReturnType<typeof normalizeLitigationInternalNoRule>) => previewLitigationInternalNo(rule, {orgCode});
    /**
     * 组成段列表。
     * @param rule 当前规则
     */
    const partRows = (rule: ReturnType<typeof normalizeLitigationInternalNoRule>) => rule.parts.map((part, index) => {
      const meta = part.kind === 'year' ? '四位' : part.kind === 'month' || part.kind === 'day' ? '两位' : part.kind === 'caseType' ? '类型码' : part.kind === 'serial' ? '递增' : '';
      const extra = part.kind === 'orgCode'
        ? `<span class="ll-ino-part-meta">${escapeHtml(orgCode || '取方案里的单位代码')}</span>`
        : part.kind === 'other'
          ? `<input class="ll-ino-input" name="internalNoOther" maxlength="20" value="${escapeHtml(rule.otherText)}" placeholder="固定文字" ${locked ? 'disabled' : ''}>`
          : `<span class="ll-ino-part-meta">${meta}</span>`;
      return `<li class="ll-ino-part" data-no-part="${part.kind}">
        <label class="ll-ino-part-check"><input type="checkbox" name="internalNoPartEnabled" ${part.enabled ? 'checked' : ''} ${part.kind === 'serial' || locked ? 'disabled' : ''} ${part.kind === 'serial' ? 'checked' : ''}><span>${escapeHtml(LITIGATION_INTERNAL_NO_PART_LABELS[part.kind])}</span></label>
        <div class="ll-ino-part-extra">${extra}</div>
        <div class="ll-ino-part-order">
          <button type="button" class="ll-ino-move" data-move="up" ${index === 0 || locked ? 'disabled' : ''} aria-label="上移">↑</button>
          <button type="button" class="ll-ino-move" data-move="down" ${index === rule.parts.length - 1 || locked ? 'disabled' : ''} aria-label="下移">↓</button>
        </div>
      </li>`;
    }).join('');
    /**
     * 规则表单。
     * @param rule 当前规则
     */
    const section = (rule: ReturnType<typeof normalizeLitigationInternalNoRule>) => {
      const typeCodes = (Object.keys(LITIGATION_NUMBERING_KIND_LABELS) as LitigationNumberingKind[]).map((kind) =>
        `<label class="ll-ino-type"><span>${escapeHtml(LITIGATION_NUMBERING_KIND_LABELS[kind])}</span><input class="ll-ino-input" name="internalNoType_${kind}" maxlength="12" value="${escapeHtml(rule.caseTypeCodes[kind])}" ${locked ? 'disabled' : ''}></label>`,
      ).join('');
      return `<section class="ll-internal-no-rule" id="llInternalNoRule">
        <p class="ll-internal-no-lead">勾选组成段并调整顺序。所属单位代号使用台账方案里的单位代码：方案管理员用自己的代码，协作单位用该单位的代码。</p>
        <div class="ll-ino-preview-bar"><span class="ll-ino-preview-label">示例</span><code class="ll-ino-preview-value">${escapeHtml(previewText(rule))}</code></div>
        <ul class="ll-ino-parts">${partRows(rule)}</ul>
        <div class="ll-ino-settings">
          <label class="ll-ino-setting"><span>分隔符</span><input class="ll-ino-input ll-ino-input-sm" name="internalNoSeparator" maxlength="3" value="${escapeHtml(rule.separator)}" placeholder="-" ${locked ? 'disabled' : ''}></label>
          <label class="ll-ino-setting"><span>流水位数</span><input class="ll-ino-input ll-ino-input-sm" name="internalNoSeqWidth" type="number" min="1" max="6" value="${rule.serialWidth}" ${locked ? 'disabled' : ''}></label>
          <label class="ll-ino-setting ll-ino-setting-wide"><span>备注</span><input class="ll-ino-input" name="internalNoNote" maxlength="200" value="${escapeHtml(rule.note)}" placeholder="选填" ${locked ? 'disabled' : ''}></label>
        </div>
        <div class="ll-ino-types"><span class="ll-ino-types-title">类型代号</span><div class="ll-ino-types-grid">${typeCodes}</div></div>
      </section>`;
    };
    const overlay = document.createElement('dialog');
    overlay.id = 'llInternalNoRuleDialog';
    overlay.className = 'la-dialog ll-internal-no-dialog';
    overlay.innerHTML = `<header class="ll-ino-dialog-head"><h2>内部案号规则</h2><button class="la-dialog-close" type="button" data-close aria-label="关闭">×</button></header>
      <div class="la-dialog-body ll-ino-dialog-body">${section(snapshot)}</div>
      <footer class="ll-ino-dialog-foot">
        <button type="button" class="tp-btn-secondary" data-close>取消</button>
        ${locked ? '' : '<button type="button" class="tp-btn-primary" data-confirm>确定</button>'}
      </footer>`;
    document.body.appendChild(overlay);
    /**
     * 从弹窗读回规则。
     */
    const readRule = () => {
      const box = overlay.querySelector<HTMLElement>('#llInternalNoRule');
      const parts = [...(box?.querySelectorAll<HTMLElement>('[data-no-part]') || [])].map((row) => ({
        kind: String(row.dataset.noPart || ''),
        enabled: row.querySelector<HTMLInputElement>('input[name="internalNoPartEnabled"]')?.checked !== false,
      }));
      const typeCode = (kind: string) => String(box?.querySelector<HTMLInputElement>(`[name="internalNoType_${kind}"]`)?.value || '');
      return normalizeLitigationInternalNoRule({
        parts,
        separator: String(box?.querySelector<HTMLInputElement>('[name="internalNoSeparator"]')?.value ?? '-'),
        serialWidth: Number(box?.querySelector<HTMLInputElement>('[name="internalNoSeqWidth"]')?.value || 3),
        orgCode: '',
        otherText: String(box?.querySelector<HTMLInputElement>('[name="internalNoOther"]')?.value || ''),
        note: String(box?.querySelector<HTMLInputElement>('[name="internalNoNote"]')?.value || ''),
        caseTypeCodes: {ss: typeCode('ss'), zx: typeCode('zx'), zc: typeCode('zc'), ldzc: typeCode('ldzc')},
      });
    };
    const syncPreview = () => {
      const preview = overlay.querySelector('.ll-ino-preview-value');
      if (preview) preview.textContent = previewText(readRule());
      const rows = [...overlay.querySelectorAll<HTMLElement>('[data-no-part]')];
      rows.forEach((row, index) => {
        const up = row.querySelector<HTMLButtonElement>('[data-move="up"]');
        const down = row.querySelector<HTMLButtonElement>('[data-move="down"]');
        if (up) up.disabled = locked || index === 0;
        if (down) down.disabled = locked || index === rows.length - 1;
      });
    };
    overlay.addEventListener('input', syncPreview);
    overlay.addEventListener('change', syncPreview);
    overlay.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-move]');
      if (!button || button.disabled || locked) return;
      const row = button.closest<HTMLElement>('[data-no-part]');
      const list = row?.parentElement;
      if (!row || !list) return;
      if (button.dataset.move === 'up') {
        const previous = row.previousElementSibling;
        if (previous) list.insertBefore(row, previous);
      } else {
        const next = row.nextElementSibling;
        if (next) list.insertBefore(next, row);
      }
      syncPreview();
    });
    const close = () => {
      overlay.close();
      overlay.remove();
    };
    overlay.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', close));
    overlay.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    overlay.querySelector('[data-confirm]')?.addEventListener('click', () => {
      const next = readRule();
      void (async () => {
        const keptOrg = internalNoRule.orgCode;
        await saveCollaborationSettings({internalNoRule: next});
        internalNoRule = normalizeLitigationInternalNoRule({...next, orgCode: keptOrg});
        close();
        notice('内部案号规则已保存');
      })().catch((error) => notice(error instanceof Error ? error.message : '内部案号规则保存失败', true));
    });
    overlay.showModal();
  };
  page.querySelector('#llInternalNoRule')?.addEventListener('click', () => openLedgerInternalNoRule());
  page.querySelector('#llExportTitle')?.addEventListener('click', () => openExportTitleDialog());
  page.addEventListener('keydown', (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      if (dirty || pendingPatches.size) void saveWorkbookToSpace();
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      if (!undoStack.length) return;
      event.preventDefault();
      void undoLastListEdit();
    }
    const redoKey = (event.ctrlKey || event.metaKey) && (event.key.toLowerCase() === 'y' || (event.shiftKey && event.key.toLowerCase() === 'z'));
    if (redoKey) {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      if (!redoStack.length) return;
      event.preventDefault();
      void redoLastListEdit();
    }
  });
  page.querySelector('#llImport')?.addEventListener('click', () => {
    importBelowCaseId = '';
    if ((dirty || pendingPatches.size) && !window.confirm('当前台账有未保存的修改。导入 Excel 后这些修改不会保留。仍要导入吗？')) return;
    page.querySelector<HTMLInputElement>('#llImportFile')?.click();
  });
  page.querySelector('#llImportFile')?.addEventListener('change', (event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    void (async () => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = '';
      for (let index = 0; index < bytes.length; index += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
      }
      const belowId = importBelowCaseId;
      importBelowCaseId = '';
      const body: {base64: string; fileName: string; insertAfterCaseId?: string} = {base64: btoa(binary), fileName: file.name};
      if (belowId) body.insertAfterCaseId = belowId;
      const result = await api.post<{updated: number; added: number; kept: number}>(apiUrl(`${base}/import`), body);
      pendingPatches.clear();
      dirty = false;
      await load();
      notice(`已导入 Excel：更新 ${result.updated} 行，新增 ${result.added} 行，原有未对上的 ${result.kept} 行仍保留`);
    })().catch((error) => notice(error instanceof Error ? error.message : '导入失败', true));
  });
  page.querySelector('#llExport')?.addEventListener('click', async () => {
    const ids = checked.size ? [...checked] : filtered().map((item) => item.id);
    if (!ids.length) { notice('没有可导出的案件', true); return; }
    try {
      const response = await fetch(apiUrl(`${base}/export`), {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({caseIds: ids}),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || '导出失败');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `诉讼台账-${auth.organization.name}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      notice('已开始下载 Excel');
    } catch (error) {
      notice(error instanceof Error ? error.message : '导出失败', true);
    }
  });
  detail.addEventListener('change', (event) => {
    const nodeRow = (event.target as HTMLElement).closest<HTMLElement>('[data-node]');
    const item = cases.find((entry) => entry.id === selected);
    if (nodeRow && item) {
      const node = item.nodes.find((entry) => entry.id === nodeRow.dataset.node);
      if (!node) return;
      const name = nodeRow.querySelector<HTMLInputElement>('[data-node-name]')?.value.trim();
      if (name) node.name = name;
      node.plannedDate = nodeRow.querySelector<HTMLInputElement>('[data-node-plan]')?.value || '';
      node.completedDate = nodeRow.querySelector<HTMLInputElement>('[data-node-done]')?.value || '';
      node.note = nodeRow.querySelector<HTMLInputElement>('[data-node-note]')?.value.trim() || '';
      node.keyDeadline = nodeRow.querySelector<HTMLInputElement>('[data-node-key]')?.checked === true;
      void persistNodes(item);
    }
  });

  /**
   * 把案件背景写回该案。内容没变时不请求。
   * @param item 案件
   * @param text 介绍正文
   */
  const persistBackground = async (item: LedgerCase, text: string) => {
    const next = text.trim();
    if ((item.caseBackground || '').trim() === next) return;
    try {
      const result = await patchCase(item.id, {
        caseBackground: next,
      });
      replaceCase(result.case);
      if (listSheet === 'parties') renderList(false);
    } catch (error) {
      notice(error instanceof Error ? error.message : '案件背景保存失败', true);
    }
  };

  rows.addEventListener('input', (event) => {
    const area = event.target;
    if (area instanceof HTMLTextAreaElement && area.classList.contains('ll-background-sheet-text')) fitBackgroundSheetTextarea(area);
  });
  rows.addEventListener('focusout', (event) => {
    const area = event.target;
    if (!(area instanceof HTMLTextAreaElement) || !area.classList.contains('ll-background-sheet-text')) return;
    const item = cases.find((entry) => entry.id === area.dataset.backgroundSheet);
    if (item) void persistBackground(item, area.value);
  });

  /**
   * 打开案件背景窗口。标题下写案件名称和内部编号。
   * @param caseId 案件
   */
  const openBackgroundDialog = (caseId: string) => {
    const item = cases.find((entry) => entry.id === caseId);
    if (!item) return;
    const host = page.querySelector<HTMLElement>('#llDialogHost')!;
    const editable = canEditCaseInList(item);
    host.innerHTML = backgroundDialogMarkup(item, editable);
    const dialog = host.querySelector<HTMLDialogElement>('#llBackgroundDialog')!;
    const area = dialog.querySelector<HTMLTextAreaElement>('.ll-background-text');
    dialog.querySelector('[data-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => {
      if (!area || !editable) return;
      void persistBackground(item, area.value);
    });
    dialog.addEventListener('click', (event) => event.stopPropagation());
    dialog.showModal();
    area?.focus();
  };

  const persistProgress = async (item: LedgerCase) => {
    try {
      const result = await patchCase(item.id, {
        progressEntries: item.progressEntries,
      });
      replaceCase(result.case);
      renderList(false);
      const dialog = page.querySelector<HTMLDialogElement>('#llProgressDialog');
      if (dialog?.open) {
        const drawerDialog = detail.contains(dialog);
        if (drawerDialog && detailCaseId && detailCaseId !== item.id) return;
        dialog.close();
        if (!drawerDialog || (detailCaseId === item.id && !detail.hidden)) openProgressDialog(item.id);
      } else if (tab === 'progress' && detailCaseId === item.id && !detail.hidden) {
        renderDetail(result.case);
      }
    } catch (error) {
      notice(error instanceof Error ? error.message : '进展保存失败', true);
    }
  };

  /**
   * 让进展记录窗口从四边拖动改变宽高，表格区域跟着伸缩。
   * @param dialog 进展记录窗口
   */
  const enableProgressDialogResize = (dialog: HTMLDialogElement) => {
    const minWidth = 420;
    const minHeight = 240;
    const width = Math.min(720, window.innerWidth - 48);
    const wrap = dialog.querySelector<HTMLElement>('.ll-progress-sheet-wrap');
    if (wrap) wrap.style.flex = '0 0 auto';
    dialog.style.width = `${width}px`;
    dialog.style.height = 'auto';
    dialog.style.maxHeight = 'none';
    const natural = dialog.getBoundingClientRect().height;
    if (wrap) wrap.style.flex = '';
    const height = Math.min(Math.max(natural, minHeight), window.innerHeight - 48);
    dialog.style.left = `${Math.max(8, (window.innerWidth - width) / 2)}px`;
    dialog.style.top = `${Math.max(8, (window.innerHeight - height) / 2)}px`;
    dialog.style.right = 'auto';
    dialog.style.bottom = 'auto';
    dialog.style.height = `${height}px`;
    dialog.style.margin = '0';
    dialog.style.maxHeight = `${window.innerHeight - 16}px`;
    /**
     * 判断指针是否落在窗口边框上。
     * @param event 指针事件
     */
    const edgeOf = (event: PointerEvent) => {
      const rect = dialog.getBoundingClientRect();
      const margin = 8;
      const leftEdge = event.clientX - rect.left <= margin;
      const rightEdge = rect.right - event.clientX <= margin;
      const topEdge = event.clientY - rect.top <= margin;
      const bottomEdge = rect.bottom - event.clientY <= margin;
      return {leftEdge, rightEdge, topEdge, bottomEdge, active: leftEdge || rightEdge || topEdge || bottomEdge};
    };
    /**
     * 边框上的光标。
     * @param edge 命中的边
     */
    const cursorFor = (edge: ReturnType<typeof edgeOf>) => {
      if ((edge.leftEdge && edge.topEdge) || (edge.rightEdge && edge.bottomEdge)) return 'nwse-resize';
      if ((edge.rightEdge && edge.topEdge) || (edge.leftEdge && edge.bottomEdge)) return 'nesw-resize';
      if (edge.leftEdge || edge.rightEdge) return 'ew-resize';
      if (edge.topEdge || edge.bottomEdge) return 'ns-resize';
      return '';
    };
    const onMove = (event: PointerEvent) => {
      if ((event.target as HTMLElement).closest('button, input, textarea, select, a')) {
        dialog.style.cursor = '';
        return;
      }
      dialog.style.cursor = cursorFor(edgeOf(event));
    };
    const onDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      if ((event.target as HTMLElement).closest('button, input, textarea, select, a')) return;
      const edge = edgeOf(event);
      if (!edge.active) return;
      event.preventDefault();
      const startX = event.clientX;
      const startY = event.clientY;
      const rect = dialog.getBoundingClientRect();
      const start = {left: rect.left, top: rect.top, width: rect.width, height: rect.height};
      const drag = (moveEvent: PointerEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        let nextWidth = start.width;
        let nextHeight = start.height;
        if (edge.rightEdge) nextWidth = start.width + dx;
        if (edge.leftEdge) nextWidth = start.width - dx;
        if (edge.bottomEdge) nextHeight = start.height + dy;
        if (edge.topEdge) nextHeight = start.height - dy;
        nextWidth = Math.max(minWidth, Math.min(nextWidth, window.innerWidth - 16));
        nextHeight = Math.max(minHeight, Math.min(nextHeight, window.innerHeight - 16));
        const nextLeft = Math.max(8, Math.min(edge.leftEdge ? start.left + (start.width - nextWidth) : start.left, window.innerWidth - nextWidth - 8));
        const nextTop = Math.max(8, Math.min(edge.topEdge ? start.top + (start.height - nextHeight) : start.top, window.innerHeight - nextHeight - 8));
        dialog.style.left = `${nextLeft}px`;
        dialog.style.top = `${nextTop}px`;
        dialog.style.width = `${nextWidth}px`;
        dialog.style.height = `${nextHeight}px`;
      };
      const stop = () => {
        window.removeEventListener('pointermove', drag);
        window.removeEventListener('pointerup', stop);
      };
      window.addEventListener('pointermove', drag);
      window.addEventListener('pointerup', stop);
    };
    dialog.addEventListener('pointermove', onMove);
    dialog.addEventListener('pointerdown', onDown);
    dialog.addEventListener('close', () => {
      dialog.removeEventListener('pointermove', onMove);
      dialog.removeEventListener('pointerdown', onDown);
    }, {once: true});
  };

  /**
   * 打开进展记录。查看更多只读，角标仍按填写权限打开编辑。
   * @param caseId 案件
   * @param viewOnly 为真时不提供添加、修改和删除
   */
  const openProgressDialog = (caseId: string, viewOnly = false) => {
    const item = cases.find((entry) => entry.id === caseId);
    if (!item) return;
    const host = page.querySelector<HTMLElement>('#llDialogHost')!;
    const editable = !viewOnly && canEditCaseInList(item);
    host.innerHTML = progressSheetDialogMarkup(item, editable);
    const dialog = host.querySelector<HTMLDialogElement>('#llProgressDialog')!;
    /**
     * 关掉进展记录弹窗。
     */
    const closeProgressDialog = () => {
      host.querySelector('.ll-progress-backdrop')?.remove();
      if (dialog.open) dialog.close();
    };
    dialog.querySelector<HTMLButtonElement>('[data-close]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeProgressDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeProgressDialog();
    });
    if (viewOnly) {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeProgressDialog();
      });
    } else {
      host.querySelector('.ll-progress-backdrop')?.addEventListener('click', closeProgressDialog);
    }
    /**
     * 读取这一行上的记录日期。
     * @param row 进展行
     */
    const progressDateOf = (row: HTMLElement) => row.querySelector<HTMLButtonElement>('[data-progress-edit="date"]')?.dataset.dateValue || '';
    /**
     * 把这一行写回案件。新行要有内容才保存。
     * @param savedRow 进展行
     */
    const saveProgressFromRow = (savedRow: HTMLElement) => {
      const content = savedRow.querySelector<HTMLTextAreaElement>('[data-progress-edit="content"]')?.value.trim() || '';
      if (savedRow.dataset.progressDraft === '1') {
        if (!content) return;
        item.progressEntries = [...(item.progressEntries || []), {
          id: savedRow.dataset.progressId || `progress_${Date.now()}`,
          date: progressDateOf(savedRow),
          content,
          createdAt: new Date().toISOString(),
        }];
        void persistProgress(item);
        return;
      }
      const entry = (item.progressEntries || []).find((row) => row.id === savedRow.dataset.progressId);
      if (!entry) return;
      entry.date = progressDateOf(savedRow);
      entry.content = content;
      void persistProgress(item);
    };
    dialog.addEventListener('close', () => {
      closeChoiceMenu();
      host.querySelector('.ll-progress-backdrop')?.remove();
      if (host.querySelector('#llProgressDialog') === dialog) host.innerHTML = '';
    });
    dialog.addEventListener('click', (event) => {
      event.stopPropagation();
      const dateButton = (event.target as HTMLElement).closest<HTMLButtonElement>('.ll-progress-date');
      if (!dateButton) return;
      event.preventDefault();
      if (dateButton.classList.contains('is-open')) {
        closeChoiceMenu();
        return;
      }
      openDateMenu(dateButton, (value) => {
        dateButton.dataset.dateValue = value;
        dateButton.textContent = value || '请选择';
        dateButton.classList.toggle('is-empty', !value);
        const savedRow = dateButton.closest<HTMLElement>('[data-progress-id]');
        if (savedRow) saveProgressFromRow(savedRow);
      });
    });
    dialog.addEventListener('input', (event) => {
      const area = event.target;
      if (area instanceof HTMLTextAreaElement && area.dataset.progressEdit === 'content') fitProgressContentHeight(area);
    });
    dialog.addEventListener('change', (event) => {
      const control = event.target;
      if (!(control instanceof HTMLTextAreaElement) || control.dataset.progressEdit !== 'content') return;
      const savedRow = control.closest<HTMLElement>('[data-progress-id]');
      if (savedRow) saveProgressFromRow(savedRow);
    });
    /**
     * 删掉一条已保存的进展，或撤掉还没填写内容的新行。
     * @param button 删除按钮
     */
    const removeProgressRow = (button: Element) => {
      const row = button.closest<HTMLElement>('[data-progress-id]');
      const entryId = row?.dataset.progressId;
      if (!row || !entryId) return;
      if (row.dataset.progressDraft === '1') {
        row.remove();
        const tbody = dialog.querySelector('tbody');
        if (tbody && !tbody.querySelector('[data-progress-id]')) {
          tbody.innerHTML = '<tr><td colspan="4" class="ll-progress-empty">暂无进展记录</td></tr>';
        }
        dialog.querySelectorAll<HTMLElement>('.ll-progress-seq').forEach((cell, index) => {
          cell.textContent = String(index + 1);
        });
        return;
      }
      item.progressEntries = (item.progressEntries || []).filter((entry) => entry.id !== entryId);
      void persistProgress(item);
    };
    /**
     * 在表尾加一行。序号顺延，日期是当地今天。写了内容才保存。
     */
    const addProgressRow = () => {
      const tbody = dialog.querySelector('tbody');
      if (!tbody) return;
      tbody.querySelector('.ll-progress-empty')?.closest('tr')?.remove();
      const id = `progress_${Date.now()}`;
      const index = tbody.querySelectorAll('[data-progress-id]').length + 1;
      tbody.insertAdjacentHTML('beforeend', `<tr data-progress-id="${escapeHtml(id)}" data-progress-draft="1">
        <td class="ll-progress-seq">${index}</td>
        <td>${progressDateControl(localProgressDate())}</td>
        <td class="ll-progress-text"><textarea data-progress-edit="content" rows="1" aria-label="记录的内容" placeholder="填写进展内容" data-progress-focus></textarea></td>
        <td class="ll-progress-op"><button type="button" class="ll-progress-icon-btn" data-dialog-remove-progress title="删除" aria-label="删除">${progressMinusIcon()}</button></td>
      </tr>`);
      const row = tbody.querySelector<HTMLElement>(`[data-progress-id="${id}"]`);
      row?.querySelector('[data-dialog-remove-progress]')?.addEventListener('click', (event) => {
        removeProgressRow(event.currentTarget as Element);
      });
      const area = row?.querySelector<HTMLTextAreaElement>('[data-progress-focus]');
      if (area) fitProgressContentHeight(area);
      area?.focus();
    };
    if (viewOnly) {
      dialog.showModal();
    } else {
      dialog.show();
      enableProgressDialogResize(dialog);
    }
    dialog.querySelector('[data-progress-add]')?.addEventListener('click', () => addProgressRow());
    dialog.querySelectorAll('[data-dialog-remove-progress]').forEach((button) => {
      button.addEventListener('click', () => removeProgressRow(button));
    });
    dialog.querySelectorAll<HTMLTextAreaElement>('[data-progress-edit="content"]').forEach(fitProgressContentHeight);
    dialog.querySelector<HTMLTextAreaElement>('[data-progress-focus]')?.focus();
  };

  const persistNodes = async (item: LedgerCase) => {
    try {
      const result = await patchCase(item.id, {nodes: item.nodes});
      replaceCase(result.case);
      renderList();
    } catch (error) {
      notice(error instanceof Error ? error.message : '程序节点保存失败', true);
    }
  };
  const persistLinks = async (item: LedgerCase) => {
    try {
      const result = await patchCase(item.id, {linkedContracts: item.linkedContracts});
      replaceCase(result.case);
      renderList();
    } catch (error) {
      notice(error instanceof Error ? error.message : '关联合同保存失败', true);
    }
  };

  const previewFile = async (fileId: string, name: string) => {
    const host = page.querySelector('#llDialogHost')!;
    host.innerHTML = `<dialog class="la-dialog la-preview-dialog" open><header><h2>${escapeHtml(name)}</h2><button class="la-dialog-close" type="button" data-close>×</button></header><div class="la-dialog-body"><div class="la-preview-state">正在加载预览…</div></div></dialog>`;
    const modal = host.querySelector('dialog')!;
    modal.querySelector('[data-close]')?.addEventListener('click', () => modal.remove());
    try {
      if (/\.(pdf|png|jpe?g|webp|gif)$/i.test(name)) {
        const response = await fetch(apiUrl(`${base}/cases/${encodeURIComponent(selected)}/materials/${encodeURIComponent(fileId)}/content`), {credentials: 'include'});
        if (!response.ok) throw new Error('无法读取文件');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const body = modal.querySelector('.la-dialog-body')!;
        body.innerHTML = /\.pdf$/i.test(name)
          ? `<iframe class="la-pdf-preview" title="PDF预览" src="${escapeHtml(url)}"></iframe>`
          : `<img class="la-image-preview" src="${escapeHtml(url)}" alt="${escapeHtml(name)}">`;
        modal.addEventListener('close', () => URL.revokeObjectURL(url));
        return;
      }
      modal.querySelector('.la-dialog-body')!.innerHTML = '<div class="la-preview-state">该格式请下载后查看。</div>';
    } catch (error) {
      modal.querySelector('.la-dialog-body')!.innerHTML = `<div class="la-preview-state">${escapeHtml(error instanceof Error ? error.message : '预览失败')}</div>`;
    }
  };

  detail.addEventListener('change', (event) => {
    const input = event.target as HTMLInputElement;
    if (input.id !== 'llMaterialFile' || !input.files?.length) return;
    const item = cases.find((entry) => entry.id === selected);
    const category = detail.querySelector<HTMLSelectElement>('#llMaterialCategory')?.value as LitigationMaterialCategory;
    const file = input.files[0];
    if (!item || !file) return;
    void (async () => {
      const signed = await api.post<{file: {id: string}}>(apiUrl('/api/files/upload-url'), {
        moduleId: 'legal-compliance-assistant',
        kind: 'legal-litigation',
        fileName: file.name,
        sizeBytes: file.size,
        mimeType: file.type || 'application/octet-stream',
      });
      await api.upload(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/content`), file, file.type || 'application/octet-stream');
      await api.post(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/complete`));
      const result = await api.post<{case: LedgerCase}>(apiUrl(`${base}/cases/${encodeURIComponent(item.id)}/materials`), {fileId: signed.file.id, category});
      const current = cases.find((entry) => entry.id === item.id);
      if (current) replaceCase(mergeLitigationCaseResponse(current, result.case, {materials: true}, pendingPatches.get(item.id)));
      serverCasesById.set(item.id, cloneLedgerCase(result.case));
      renderList();
      notice('材料已上传');
    })().catch((error) => notice(error instanceof Error ? error.message : '上传失败', true));
    input.value = '';
  });

  const tableScroll = page.querySelector<HTMLElement>('#llTableScroll');
  const sheetHScroll = page.querySelector<HTMLElement>('#llSheetHScroll');
  const sheetHScrollInner = page.querySelector<HTMLElement>('#llSheetHScrollInner');
  /**
   * 表格底部留出将近一屏空白，最后一行可以滚到表头下方。
   * @param host 表格滚动区
   */
  const fitLedgerScrollTail = (host: HTMLElement) => {
    let tail = host.querySelector<HTMLElement>(':scope > .ll-scroll-tail');
    if (!tail) {
      tail = document.createElement('div');
      tail.className = 'll-scroll-tail';
      tail.setAttribute('aria-hidden', 'true');
      host.appendChild(tail);
    }
    const headHeight = host.querySelector('thead')?.getBoundingClientRect().height || 54;
    const lastRow = host.querySelector('tbody tr:last-child');
    const rowHeight = lastRow?.getBoundingClientRect().height || 54;
    const lift = Math.max(0, Math.round(host.clientHeight - headHeight - rowHeight - 12));
    tail.style.height = `${lift}px`;
  };
  syncSheetHScroll = () => {
    const table = tableScroll?.querySelector('table');
    if (!tableScroll || !sheetHScroll || !sheetHScrollInner || !table) return;
    const range = Math.max(0, table.scrollWidth - tableScroll.clientWidth);
    sheetHScrollInner.style.width = `${range + sheetHScroll.clientWidth}px`;
    fitLedgerScrollTail(tableScroll);
  };
  if (tableScroll && sheetHScroll) {
    let scrollLock = false;
    tableScroll.addEventListener('scroll', () => {
      if (scrollLock) return;
      scrollLock = true;
      sheetHScroll.scrollLeft = tableScroll.scrollLeft;
      scrollLock = false;
    });
    sheetHScroll.addEventListener('scroll', () => {
      if (scrollLock) return;
      scrollLock = true;
      tableScroll.scrollLeft = sheetHScroll.scrollLeft;
      scrollLock = false;
    });
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => syncSheetHScroll());
      const table = tableScroll.querySelector('table');
      if (table) observer.observe(table);
      observer.observe(tableScroll);
    }
    syncSheetHScroll();
  }

  page.querySelector('.ll-case-table')?.setAttribute('hidden', '');
  await load();
}

type LitigationCauseHit = {code: number; name: string; part: string; level2: string};

/**
 * 案由手填控件（须从资料库点选）。
 * @param value 当前案由
 * @param extraAttrs 额外 HTML 属性
 */
function litigationCauseFieldMarkup(value: string, extraAttrs = ''): string {
  const locked = value.trim();
  return `<div class="enterprise-picker" data-litigation-cause-picker>
    <input class="form-input" name="title" maxlength="200" value="${escapeHtml(locked)}" placeholder="输入至少 2 个字检索第三级案由" autocomplete="off" required${extraAttrs}>
    <input type="hidden" name="litigationCauseLocked" value="${escapeHtml(locked)}">
    <div class="enterprise-suggestions" data-litigation-cause-suggestions hidden></div>
    <div class="enterprise-selection-hint" data-litigation-cause-hint">${locked ? `已选定：${escapeHtml(locked)}` : '请从下拉结果中选择案由库中的标准案由'}</div>
  </div>`;
}

/**
 * 读取表单中的案由是否已从库中选定。
 * 案件总表单元格内案由检索（无弹窗）。
 * @param caseId 案件 ID
 * @param value 当前案由
 */
function litigationCauseCellMarkup(caseId: string, value: string): string {
  const locked = value.trim();
  return `<div class="ll-cell-cause" data-litigation-cause-cell data-inplace-case="${escapeHtml(caseId)}" data-inplace-field="title">
    <div class="enterprise-picker" data-litigation-cause-picker>
      <textarea class="ll-cell-cause-input" name="title" maxlength="200" rows="1" autocomplete="off" aria-label="案由">${escapeHtml(locked)}</textarea>
      <input type="hidden" name="litigationCauseLocked" value="${escapeHtml(locked)}">
      <div class="enterprise-suggestions ll-cell-cause-suggestions" data-litigation-cause-suggestions hidden></div>
    </div>
  </div>`;
}

/**
 * @param form 表单
 */
function readLitigationCauseSelection(form: HTMLFormElement): {ok: true; title: string} | {ok: false; message: string} {
  const input = form.querySelector<HTMLInputElement>('input[name="title"]');
  const locked = form.querySelector<HTMLInputElement>('input[name="litigationCauseLocked"]');
  const title = input?.value.trim() || '';
  const canonical = locked?.value.trim() || '';
  if (!title) return {ok: false, message: '请填写案由'};
  if (!canonical || canonical !== title) return {ok: false, message: '请从案由库检索并点选标准案由，不能自行输入'};
  return {ok: true, title: canonical};
}

/**
 * 绑定案由模糊检索与点选。
 * @param root 包含控件的容器
 */
function bindLitigationCausePicker(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-litigation-cause-picker]').forEach((picker) => {
    if (picker.dataset.bound === '1') return;
    picker.dataset.bound = '1';
    const input = picker.querySelector<HTMLInputElement | HTMLTextAreaElement>('input[name="title"], textarea[name="title"]');
    const locked = picker.querySelector<HTMLInputElement>('input[name="litigationCauseLocked"]');
    const list = picker.querySelector<HTMLElement>('[data-litigation-cause-suggestions]');
    const hint = picker.querySelector<HTMLElement>('[data-litigation-cause-hint]');
    if (!input || !locked || !list) return;
    let fittingCause = false;
    /**
     * 案由按当前列宽换行，格子跟着变高。
     */
    const fitCauseField = () => {
      if (!(input instanceof HTMLTextAreaElement) || fittingCause) return;
      fittingCause = true;
      input.style.height = 'auto';
      input.style.height = `${input.scrollHeight}px`;
      fittingCause = false;
    };
    fitCauseField();
    if (input instanceof HTMLTextAreaElement && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => fitCauseField());
      observer.observe(input);
    }
    let timer: number | undefined;
    let requestId = 0;
    const hide = () => {
      list.hidden = true;
      list.innerHTML = '';
    };
    const renderResults = (results: LitigationCauseHit[]) => {
      if (!results.length) {
        list.innerHTML = '<div class="enterprise-suggestion-empty">未找到匹配的第三级案由</div>';
        list.hidden = false;
        return;
      }
      list.innerHTML = results.map((item, index) =>
        `<button type="button" class="enterprise-suggestion" data-cause-index="${index}"><strong>${escapeHtml(item.name)}</strong><span>${escapeHtml(item.part)} · ${escapeHtml(item.level2)}</span></button>`,
      ).join('');
      list.hidden = false;
      list.querySelectorAll<HTMLButtonElement>('[data-cause-index]').forEach((button) => {
        button.addEventListener('click', () => {
          const hit = results[Number(button.dataset.causeIndex)];
          if (!hit) return;
          input.value = hit.name;
          locked.value = hit.name;
          fitCauseField();
          if (hint) hint.textContent = `已选定：${hit.name}`;
          hide();
          locked.dispatchEvent(new Event('change', {bubbles: true}));
        });
      });
    };
    input.addEventListener('input', () => {
      fitCauseField();
      if (locked.value !== input.value.trim()) locked.value = '';
      if (hint) hint.textContent = '请从下拉结果中选择案由库中的标准案由';
      if (timer) window.clearTimeout(timer);
      const keyword = input.value.trim();
      if (keyword.length < 2) {
        hide();
        return;
      }
      timer = window.setTimeout(async () => {
        const current = ++requestId;
        list.innerHTML = '<div class="enterprise-suggestion-loading">正在检索案由…</div>';
        list.hidden = false;
        try {
          const result = await api.get<{results: LitigationCauseHit[]}>(
            apiUrl(`/api/legal-litigation/causes/search?q=${encodeURIComponent(keyword)}`),
            {timeoutMs: 10000},
          );
          if (current !== requestId) return;
          renderResults(result.results || []);
        } catch {
          if (current !== requestId) return;
          list.innerHTML = '<div class="enterprise-suggestion-empty">案由检索失败，请稍后重试</div>';
          list.hidden = false;
        }
      }, 350);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && !locked.value) input.dispatchEvent(new Event('input'));
    });
    input.addEventListener('blur', () => {
      window.setTimeout(() => {
        if (!picker.contains(document.activeElement)) hide();
      }, 150);
    });
    document.addEventListener('click', (event) => {
      if (!picker.contains(event.target as Node)) hide();
    });
  });
}

type LitigationCourtHit = {name: string; level: string; province: string};

/**
 * 把法院检索结果挂到输入框下方，避免被表格滚动区裁切。
 * @param input 法院名称输入
 * @param list 候选列表
 */
function placeLitigationCourtSuggestions(input: HTMLInputElement, list: HTMLElement): void {
  const rect = input.getBoundingClientRect();
  const width = Math.max(rect.width, 280);
  const spaceBelow = window.innerHeight - rect.bottom;
  list.style.position = 'fixed';
  list.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`;
  list.style.width = `${width}px`;
  list.style.right = 'auto';
  list.style.zIndex = '80';
  if (spaceBelow < 180 && rect.top > spaceBelow) {
    list.style.top = 'auto';
    list.style.bottom = `${window.innerHeight - rect.top + 2}px`;
  } else {
    list.style.bottom = 'auto';
    list.style.top = `${rect.bottom + 2}px`;
  }
}

/**
 * 绑定法院名称模糊检索。点选或完整输入名录中的名称后才写入。
 * @param root 包含控件的容器
 * @param notify 未命中名录时的提示
 */
function bindLitigationCourtPicker(root: ParentNode, notify: (message: string, error?: boolean) => void): void {
  root.querySelectorAll<HTMLElement>('[data-litigation-court-picker]').forEach((picker) => {
    if (picker.dataset.bound === '1') return;
    picker.dataset.bound = '1';
    const input = picker.querySelector<HTMLInputElement>('[data-venue-court]');
    const list = picker.querySelector<HTMLElement>('[data-litigation-court-suggestions]');
    if (!input || !list || input.readOnly) return;
    let timer: number | undefined;
    let requestId = 0;
    let hits: LitigationCourtHit[] = [];
    let scrollBound = false;
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && (target === list || list.contains(target))) return;
      hide();
    };
    const hide = () => {
      list.hidden = true;
      list.innerHTML = '';
      hits = [];
      if (!scrollBound) return;
      scrollBound = false;
      document.removeEventListener('scroll', onScroll, true);
    };
    const show = () => {
      placeLitigationCourtSuggestions(input, list);
      list.hidden = false;
      if (scrollBound) return;
      scrollBound = true;
      document.addEventListener('scroll', onScroll, true);
    };
    const accept = (name: string) => {
      input.value = name;
      input.dataset.venueSaved = name;
      hide();
      input.dispatchEvent(new Event('change', {bubbles: true}));
    };
    const renderResults = (results: LitigationCourtHit[]) => {
      hits = results;
      if (!results.length) {
        list.innerHTML = '<div class="enterprise-suggestion-empty">名录中没有这个法院，请换个关键字</div>';
        show();
        return;
      }
      list.innerHTML = results.map((item, index) =>
        `<button type="button" class="enterprise-suggestion" data-court-index="${index}"><strong>${escapeHtml(item.name)}</strong><span>${escapeHtml(item.level)} · ${escapeHtml(item.province)}</span></button>`,
      ).join('');
      show();
      list.querySelectorAll<HTMLButtonElement>('[data-court-index]').forEach((button) => {
        button.addEventListener('mousedown', (event) => {
          event.preventDefault();
          const hit = hits[Number(button.dataset.courtIndex)];
          if (hit) accept(hit.name);
        });
      });
    };
    const search = (keyword: string) => {
      if (timer) window.clearTimeout(timer);
      if (keyword.length < 2) {
        hide();
        return;
      }
      timer = window.setTimeout(async () => {
        const current = ++requestId;
        list.innerHTML = '<div class="enterprise-suggestion-loading">正在检索法院名录…</div>';
        show();
        try {
          const result = await api.get<{results: LitigationCourtHit[]}>(
            apiUrl(`/api/legal-litigation/courts/search?q=${encodeURIComponent(keyword)}`),
            {timeoutMs: 10000},
          );
          if (current !== requestId || input.value.trim() !== keyword) return;
          renderResults(result.results || []);
        } catch {
          if (current !== requestId) return;
          list.innerHTML = '<div class="enterprise-suggestion-empty">法院名录检索失败，请稍后重试</div>';
          show();
        }
      }, 200);
    };
    input.addEventListener('input', () => {
      search(input.value.trim());
    });
    input.addEventListener('focus', () => {
      const keyword = input.value.trim();
      if (keyword.length >= 2 && keyword !== (input.dataset.venueSaved || '')) search(keyword);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        input.value = input.dataset.venueSaved || '';
        hide();
        return;
      }
      if (event.key !== 'Enter' || list.hidden) return;
      const exact = hits.find((item) => item.name === input.value.trim());
      if (!exact) return;
      event.preventDefault();
      accept(exact.name);
    });
    input.addEventListener('change', (event) => {
      const current = input.value.trim();
      const saved = input.dataset.venueSaved || '';
      if (current === saved) return;
      event.stopPropagation();
      if (!current) {
        input.dataset.venueSaved = '';
        input.dispatchEvent(new Event('change', {bubbles: true}));
        return;
      }
      const currentRequest = ++requestId;
      void (async () => {
        try {
          const result = await api.get<{results: LitigationCourtHit[]}>(
            apiUrl(`/api/legal-litigation/courts/search?q=${encodeURIComponent(current)}`),
            {timeoutMs: 10000},
          );
          if (currentRequest !== requestId || input.value.trim() !== current) return;
          const exact = (result.results || []).find((item) => item.name === current);
          if (!exact) {
            input.value = saved;
            hide();
            notify('请从法院名录中点选法院名称', true);
            return;
          }
          accept(exact.name);
        } catch {
          if (currentRequest !== requestId) return;
          input.value = saved;
          hide();
          notify('法院名录暂时无法核对，请稍后再选', true);
        }
      })();
    });
    input.addEventListener('blur', () => {
      window.setTimeout(() => {
        if (!picker.contains(document.activeElement)) hide();
      }, 150);
    });
  });
}

function partyRow(party: LitigationParty): string {
  return `<div class="ll-party-row" data-party="${escapeHtml(party.id)}">
    <select data-party-role>${Object.entries(PARTY_ROLE_LABELS).map(([id, label]) => `<option value="${id}" ${party.role === id ? 'selected' : ''}>${label}</option>`).join('')}</select>
    <input data-party-name maxlength="200" value="${escapeHtml(party.name)}" placeholder="名称">
    <input data-party-contact maxlength="120" value="${escapeHtml(party.contact)}" placeholder="联系人">
    <button type="button" class="la-text-button" data-action="remove-party">移除</button>
  </div>`;
}

/**
 * 当前台账方案的字段填写规则覆盖（后台「诉讼台账 · 四表模板」下发）。
 * 由 list 接口的 activePlan 带入，供 fieldFillAttrs 决定提示文案与是否只读。
 */
let activeFieldRuleOverrides: LitigationFieldRuleOverrides | undefined;

/** 各明细工作表（当事人/程序与期限/进展记录）的列标题，取自后台模板表头原文。 */
let activePlanSheetLabels: Partial<Record<LitigationSheetKind, string[]>> | undefined;

/**
 * 生成表单字段的 title / placeholder / 只读属性。
 *
 * 规则来源优先级：平台下发的覆盖 > 代码内置默认。
 * ruleKind 为 system / computed / reference 时字段只读（系统写入或跨表取数）。
 *
 * @param id 标准字段 ID
 * @param internalNoRule 方案中的内部案号规则
 */
function fieldFillAttrs(id: string, internalNoRule?: Parameters<typeof litigationFieldFillGuide>[1]): string {
  const resolved = resolveLitigationFieldRule(id, activeFieldRuleOverrides);
  // 内部案号有按方案动态生成的说明，优先用它
  const guide = id === 'internalNo' ? litigationFieldFillGuide(id, internalNoRule) : resolved;
  if (!guide) return '';
  const title = [guide.rule, guide.allowed ? `可选值：${guide.allowed}` : '', guide.example ? `示例：${guide.example}` : '']
    .filter(Boolean)
    .join(' ');
  const placeholder = guide.example && !guide.example.startsWith('（') ? ` placeholder="${escapeHtml(guide.example)}"` : '';
  const kindInfo = litigationFieldRuleKindInfo(resolved.ruleKind);
  // 系统写入 / 系统计算 / 跨表引用 → 只读，并说明原因
  const readOnly = kindInfo?.readOnly === true;
  const readOnlyAttr = readOnly ? ` readonly title="${escapeHtml(`${title}${title ? ' ' : ''}（${kindInfo?.label ?? ''}，不可修改）`)}"` : '';
  return readOnlyAttr || ` title="${escapeHtml(title)}"${placeholder}`;
}

/**
 * 从案号编辑区读出每一行。
 * @param host 案号容器或包含它的表单
 */
function readCourtNumberRows(host: ParentNode): LitigationCourtNumberRow[] {
  return [...host.querySelectorAll<HTMLElement>('.ll-court-row')].map((row) => {
    const control = row.querySelector<HTMLElement>('[data-court-kind]');
    const layer = control instanceof HTMLSelectElement ? control.value : control?.getAttribute('data-court-kind') || 'first';
    const known = LITIGATION_COURT_NUMBER_LAYERS.some((item) => item.id === layer) ? layer : 'first';
    const value = row.querySelector<HTMLInputElement>('[data-court-value]')?.value.trim().slice(0, 80) || '';
    return {layer: known as LitigationCourtNumberLayer, value};
  }).slice(0, 12);
}

/**
 * 一行案号。第一行只有加号，后面的行可以删掉。
 * @param layer 层次
 * @param value 案号
 * @param editable 是否可改
 * @param withAdd 是否显示加号
 * @param withRemove 是否显示减号
 */
/**
 * 列表里的固定选项。不用原生下拉，避免表格裁切弹层，选项也可以水平居中。
 * @param fieldId 字段
 * @param caseId 案件
 * @param label 无障碍名称
 * @param value 当前值
 * @param options 可选项
 */
/**
 * 这些日期默认用年月日弹窗。后台若改成其他规则种类，则不再强制弹窗。
 * @param fieldId 字段
 */
function ledgerDateField(fieldId: string): boolean {
  return fieldId === 'filingDate' || fieldId === 'closedAt' || fieldId === 'plannedDate' || fieldId === 'completedDate' || fieldId === 'progressDate';
}

/**
 * 台账日期格。点开后选择年月日并确认。
 * @param fieldId 字段
 * @param caseId 案件
 * @param value 已保存的年-月-日
 */
function dateCellMarkup(fieldId: string, caseId: string, value: string): string {
  const shown = value || '请选择';
  return `<button type="button" class="ll-cell-date${value ? '' : ' is-empty'}" data-inplace-field="${escapeHtml(fieldId)}" data-inplace-case="${escapeHtml(caseId)}" data-date-value="${escapeHtml(value)}" aria-haspopup="dialog" aria-expanded="false">${escapeHtml(shown)}</button>`;
}

function choiceCellMarkup(fieldId: string, caseId: string, label: string, value: string, options: string[]): string {
  const shown = value || '请选择';
  return `<button type="button" class="ll-cell-choice${value ? '' : ' is-empty'}" data-inplace-field="${escapeHtml(fieldId)}" data-inplace-case="${escapeHtml(caseId)}" data-choice-value="${escapeHtml(value)}" data-choice-options="${escapeHtml(JSON.stringify(options))}" aria-haspopup="listbox" aria-expanded="false" aria-label="${escapeHtml(label)}">${escapeHtml(shown)}</button>`;
}

/**
 * 详情表单里的案件类型。列表行优先用案件本身的审级，才能认出执行案件。
 * @param host 案号或管辖容器
 */
function layerContextFromForm(host: HTMLElement): {disputeType: LedgerCase['disputeType']; instance: LedgerCase['instance']; enforcementStatus: string} {
  const form = host.closest('#llOverviewForm');
  const disputeType = form?.querySelector<HTMLSelectElement>('[name="disputeType"]')?.value;
  const instance = form?.querySelector<HTMLInputElement>('[name="instance"]')?.value;
  return {
    disputeType: disputeType === 'arbitration' || disputeType === 'labor' ? disputeType : 'litigation',
    instance: instance === 'second' || instance === 'retrial' || instance === 'enforcement' ? instance : 'first',
    enforcementStatus: form?.querySelector<HTMLSelectElement>('[name="enforcementStatus"]')?.value || 'none',
  };
}

/**
 * 不可改的格子只展示文字。系统生成的内容点击后不出现输入框。
 * @param value 已有内容
 * @param label 无障碍名称
 */
function ledgerStaticValue(value: string, label: string): string {
  return `<span class="ll-court-value is-static" aria-label="${escapeHtml(label)}">${escapeHtml(value)}</span>`;
}

function courtNumberRowMarkup(layer: LitigationCourtNumberLayer, value: string, editable: boolean, withAdd: boolean, withRemove: boolean, disputeType: {disputeType: LedgerCase['disputeType']; instance?: LedgerCase['instance']} | LedgerCase['disputeType'] = 'litigation'): string {
  const allowed = litigationCourtNumberLayersForDispute(disputeType);
  const current = allowed.some((item) => item.id === layer) ? layer : (allowed[0]?.id || 'first');
  const label = allowed.find((item) => item.id === current)?.label || '';
  const disabled = editable ? '' : ' disabled';
  const kind = !editable || allowed.length < 2
    ? `<span class="ll-court-kind" data-court-kind="${current}">${escapeHtml(label)}</span>`
    : `<select class="ll-court-kind" data-court-kind aria-label="案号层次"${disabled}>${allowed.map((item) => `<option value="${item.id}" ${item.id === current ? 'selected' : ''}>${escapeHtml(item.label)}</option>`).join('')}</select>`;
  const add = withAdd && editable ? `<button type="button" class="ll-court-add" data-court-add aria-label="新增案号">${ledgerPlusIcon()}</button>` : '';
  const remove = withRemove && editable ? `<button type="button" class="ll-court-remove" data-court-remove aria-label="删除案号">${ledgerMinusIcon()}</button>` : '';
  const valueControl = editable
    ? `<input class="ll-court-value" data-court-value value="${escapeHtml(value)}" aria-label="案号">`
    : ledgerStaticValue(value, '案号');
  return `<div class="ll-court-row">${kind}${valueControl}${add}${remove}</div>`;
}

/**
 * 案号按行填写，行与行之间用表格线隔开。
 * @param item 案件
 * @param editable 是否可改
 */
/**
 * 关联案号空格的填写提示。执行案件填生效裁判文书案号，其余填执行案号。
 * @param item 案件
 */
function relatedCourtNoHint(item: LedgerCase): string {
  const kind = deriveLitigationNumberingKind({
    disputeType: item.disputeType,
    instance: item.instance,
    enforcementStatus: item.enforcementStatus,
  });
  return kind === 'zx' ? '生效裁判文书案号' : '执行案号';
}

/**
 * 按内容把诉讼请求输入框撑开，换行后能看见下一行。
 * @param area 请求输入框
 */
function fitClaimTextarea(area: HTMLTextAreaElement): void {
  area.style.height = '0px';
  area.style.height = `${Math.max(22, area.scrollHeight)}px`;
}

/**
 * 调整一块区域内全部诉讼请求输入框的高度。
 * @param root 列表或详情
 */
function fitClaimTextareas(root: ParentNode): void {
  root.querySelectorAll<HTMLTextAreaElement>('textarea.ll-claim-text, textarea.ll-result-text').forEach(fitClaimTextarea);
}

/**
 * 案件背景表的内容介绍随文字增高，过长时在格内滚动。
 * @param area 内容介绍
 */
function fitBackgroundSheetTextarea(area: HTMLTextAreaElement): void {
  area.style.height = '0px';
  area.style.height = `${Math.min(160, Math.max(44, area.scrollHeight))}px`;
}

/**
 * 调整案件背景表里全部内容介绍的高度。
 * @param root 表体
 */
function fitBackgroundSheetTextareas(root: ParentNode): void {
  root.querySelectorAll<HTMLTextAreaElement>('textarea.ll-background-sheet-text').forEach(fitBackgroundSheetTextarea);
}

/**
 * 案件背景表单元格 class。内容介绍单独留白。
 * @param fieldId 列字段
 */
/**
 * 明细表列宽。案件背景的名称、编号和介绍要能完整放下。
 * @param fieldId 列字段
 */
function detailSheetColWidth(fieldId: string): number {
  if (fieldId === 'caseName' || fieldId === 'partyName') return 240;
  if (fieldId === 'internalNo') return 168;
  if (fieldId === 'caseBackground' || fieldId === 'progressContent' || fieldId === 'nodeNote') return 480;
  if (fieldId === 'claimSummary' || fieldId === 'resultSummary') return 280;
  return 128;
}

function backgroundSheetCellClass(fieldId: string): string {
  if (fieldId === 'caseBackground') return 'll-background-sheet-cell';
  if (fieldId === 'internalNo') return 'll-background-sheet-no';
  if (fieldId === 'caseName') return 'll-background-sheet-name';
  return '';
}

/**
 * 案件背景表一案一行，与背景窗口同一段介绍。
 * @param item 案件
 * @param fieldId 列字段
 * @param editable 是否可改介绍
 */
function backgroundSheetCell(item: {id: string; internalNo?: string; caseName?: string; caseBackground?: string}, fieldId: string, editable: boolean): string {
  if (fieldId === 'internalNo') return escapeHtml(item.internalNo || '');
  if (fieldId === 'caseName') return escapeHtml(item.caseName || '');
  if (fieldId === 'caseBackground') {
    const text = item.caseBackground || '';
    if (!editable) return `<div class="ll-background-sheet-read">${text ? escapeHtml(text) : ''}</div>`;
    return `<textarea class="ll-background-sheet-text" data-background-sheet="${escapeHtml(item.id)}" maxlength="4000" placeholder="填写该案件的内容介绍" aria-label="案件内容介绍">${escapeHtml(text)}</textarea>`;
  }
  return '';
}

/** 本页已点开、但反请求内容还是空的案件。刷新后只保留写过内容的反请求。 */
const openCounterclaimRows = new Set<string>();

/** 本页已点开、但反诉结果还是空的案件。刷新后只保留写过内容的反诉结果。 */
const openCounterResultRows = new Set<string>();

/** 本页已点开、但反诉金额还是空的案件。刷新后只保留写过金额的反诉行。 */
const openCounterAmountRows = new Set<string>();

/** 本页已点开、但反诉获支持金额还是空的案件。刷新后只保留写过金额的反诉行。 */
const openCounterAwardRows = new Set<string>();

/**
 * 诉讼请求的一行：左侧固定名称，中间填写，右侧是加号或减号。
 * @param label 左侧名称
 * @param part 原告请求或反请求
 * @param value 已填内容
 * @param editable 是否可改
 * @param wide 名称较长时加宽
 * @param withAdd 是否显示添加反请求
 * @param withRemove 是否显示删除反请求
 */
function claimRowMarkup(label: string, part: 'claim' | 'counter', value: string, editable: boolean, wide: boolean, withAdd: boolean, withRemove: boolean): string {
  const add = withAdd && editable ? `<button type="button" class="ll-court-add" data-claim-add title="添加反请求" aria-label="添加反请求">${ledgerPlusIcon()}</button>` : '';
  const remove = withRemove && editable ? `<button type="button" class="ll-court-remove" data-claim-remove aria-label="删除反请求">${ledgerMinusIcon()}</button>` : '';
  const valueControl = editable
    ? `<textarea class="ll-claim-text" data-claim-part="${part}" rows="1" aria-label="${escapeHtml(label)}" placeholder="回车换行，可写多条">${escapeHtml(value)}</textarea>`
    : ledgerStaticValue(value, label);
  return `<div class="ll-court-row ll-claim-row"><span class="ll-court-kind ll-claim-kind${wide ? ' is-wide' : ''}">${escapeHtml(label)}</span>${valueControl}${add}${remove}</div>`;
}

/**
 * 诉讼请求格。执行案件只有申请执行标的。其余案件先显示原告请求，点加号后再出现反请求。
 * @param item 案件
 * @param editable 是否可改
 */
function claimFieldsMarkup(item: LedgerCase, editable: boolean): string {
  const kind = deriveLitigationNumberingKind({
    disputeType: item.disputeType,
    instance: item.instance,
    enforcementStatus: item.enforcementStatus,
  });
  if (kind === 'zx') {
    return `<div class="ll-claim-layers" data-claim-case="${escapeHtml(item.id)}">${claimRowMarkup('申请执行标的', 'claim', item.claimSummary || '', editable, true, false, false)}</div>`;
  }
  const showCounter = openCounterclaimRows.has(item.id) || Boolean(String(item.counterclaimSummary || '').trim());
  const primary = claimRowMarkup('原告请求', 'claim', item.claimSummary || '', editable, false, !showCounter, false);
  const counter = showCounter ? claimRowMarkup('反请求', 'counter', item.counterclaimSummary || '', editable, false, false, true) : '';
  return `<div class="ll-claim-layers" data-claim-case="${escapeHtml(item.id)}">${primary}${counter}</div>`;
}

/**
 * 判决/裁决结果的一行。
 * @param label 左侧名称
 * @param part 本诉结果或反诉结果
 * @param value 已填内容
 * @param editable 是否可改
 * @param withAdd 是否显示加号
 * @param withRemove 是否显示减号
 * @param removeTitle 删除按钮提示
 */
function resultRowMarkup(label: string, part: 'primary' | 'counter', value: string, editable: boolean, withAdd: boolean, withRemove: boolean, removeTitle = '删除反诉结果', addTitle = '添加反诉判决/裁决结果'): string {
  const add = withAdd && editable ? `<button type="button" class="ll-court-add" data-result-add title="${escapeHtml(addTitle)}" aria-label="${escapeHtml(addTitle)}">${ledgerPlusIcon()}</button>` : '';
  const remove = withRemove && editable ? `<button type="button" class="ll-court-remove" data-result-remove title="${escapeHtml(removeTitle)}" aria-label="${escapeHtml(removeTitle)}">${ledgerMinusIcon()}</button>` : '';
  const valueControl = editable
    ? `<textarea class="ll-result-text" data-result-part="${part}" rows="1" aria-label="${escapeHtml(label)}" placeholder="回车换行，可写多条">${escapeHtml(value)}</textarea>`
    : ledgerStaticValue(value, label);
  return `<div class="ll-court-row ll-claim-row"><span class="ll-court-kind ll-claim-kind${label.length > 4 ? ' is-wide' : ''}">${escapeHtml(label)}</span>${valueControl}${add}${remove}</div>`;
}

/**
 * 判决/裁决结果格。执行案件只有执行结果。其余案件先显示本诉结果，点加号后再出现反诉结果。
 * @param item 案件
 * @param editable 是否可改
 */
function resultFieldsMarkup(item: LedgerCase, editable: boolean): string {
  const labels = litigationJudgmentResultLabels(item);
  if (labels.single) {
    return `<div class="ll-result-layers is-single" data-result-case="${escapeHtml(item.id)}">${resultRowMarkup(labels.primary, 'primary', item.resultSummary || '', editable, false, false)}</div>`;
  }
  const showCounter = openCounterResultRows.has(item.id) || Boolean(String(item.counterclaimResult || '').trim());
  const primary = resultRowMarkup(labels.primary, 'primary', item.resultSummary || '', editable, !showCounter, false, labels.removeTitle, labels.addTitle);
  const counter = showCounter ? resultRowMarkup(labels.counter, 'counter', item.counterclaimResult || '', editable, false, true, labels.removeTitle, labels.addTitle) : '';
  return `<div class="ll-result-layers" data-result-case="${escapeHtml(item.id)}">${primary}${counter}</div>`;
}

/**
 * 金额显示成输入框里的数字。0 显示为空。
 * @param value 金额
 */
function amountInputText(value: number | undefined): string {
  return value && value > 0 ? String(value) : '';
}

/**
 * 案涉金额的一行：左侧固定名称，中间填写，右侧是加号或减号。
 * @param label 左侧名称
 * @param part 本诉金额或反诉金额
 * @param value 已填金额
 * @param editable 是否可改
 * @param withAdd 是否显示加号
 * @param withRemove 是否显示减号
 * @param addTitle 加号悬停提示
 */
function amountRowMarkup(label: string, part: 'principal' | 'counter', value: string, editable: boolean, withAdd: boolean, withRemove: boolean, addTitle = ''): string {
  const wide = label.length > 5 ? ' is-wide' : '';
  const add = withAdd && editable ? `<button type="button" class="ll-court-add" data-amount-add title="${escapeHtml(addTitle)}" aria-label="${escapeHtml(addTitle)}">${ledgerPlusIcon()}</button>` : '';
  const remove = withRemove && editable ? `<button type="button" class="ll-court-remove" data-amount-remove aria-label="删除${escapeHtml(label)}">${ledgerMinusIcon()}</button>` : '';
  const valueControl = editable
    ? `<input class="ll-court-value" data-amount-part="${part}" inputmode="decimal" value="${escapeHtml(value)}" aria-label="${escapeHtml(label)}">`
    : ledgerStaticValue(value, label);
  return `<div class="ll-court-row"><span class="ll-court-kind ll-amount-kind${wide}">${escapeHtml(label)}</span>${valueControl}${add}${remove}</div>`;
}

/**
 * 案涉金额格。执行案件只有申请执行标的金额。其余案件先显示本诉或本请求金额，点加号后再出现反诉或反请求金额。
 * @param item 案件
 * @param editable 是否可改
 */
/**
 * 从支持金额编辑区读出本诉获支持金额和反诉获支持金额。格式不对时返回 null。
 * @param host 表单或金额容器
 */
function readAwardedAmountPatch(host: ParentNode): {awardedAmount: number; counterclaimAwardedAmount: number} | null {
  const layers = host instanceof Element && host.classList.contains('ll-award-layers')
    ? host
    : host.querySelector<HTMLElement>('.ll-award-layers');
  if (!layers) return {awardedAmount: 0, counterclaimAwardedAmount: 0};
  const primary = parseLitigationMoney(layers.querySelector<HTMLInputElement>('[data-award-part="primary"]')?.value || '');
  const counterInput = layers.querySelector<HTMLInputElement>('[data-award-part="counter"]');
  const counter = counterInput ? parseLitigationMoney(counterInput.value) : 0;
  if (primary === null || counter === null) return null;
  return {
    awardedAmount: primary,
    counterclaimAwardedAmount: layers.classList.contains('is-single') ? 0 : counter,
  };
}

/**
 * 支持金额的一行：左侧固定名称，中间填写金额，右侧是加号或减号。
 * @param label 左侧名称
 * @param part 本诉获支持金额或反诉获支持金额
 * @param value 已填金额
 * @param editable 是否可改
 * @param withAdd 是否显示加号
 * @param withRemove 是否显示减号
 * @param actionTitle 加号或减号的悬停提示
 */
function awardRowMarkup(label: string, part: 'primary' | 'counter', value: string, editable: boolean, withAdd: boolean, withRemove: boolean, actionTitle: string): string {
  const add = withAdd && editable ? `<button type="button" class="ll-court-add" data-award-add title="${escapeHtml(actionTitle)}" aria-label="${escapeHtml(actionTitle)}">${ledgerPlusIcon()}</button>` : '';
  const remove = withRemove && editable ? `<button type="button" class="ll-court-remove" data-award-remove title="${escapeHtml(actionTitle)}" aria-label="${escapeHtml(actionTitle)}">${ledgerMinusIcon()}</button>` : '';
  const valueControl = editable
    ? `<input class="ll-court-value" data-award-part="${part}" inputmode="decimal" value="${escapeHtml(value)}" aria-label="${escapeHtml(label)}">`
    : ledgerStaticValue(value, label);
  return `<div class="ll-court-row"><span class="ll-court-kind ll-award-kind">${escapeHtml(label)}</span>${valueControl}${add}${remove}</div>`;
}

/**
 * 判决（裁决）支持金额格。执行案件只有执行到位的金额。诉讼先显示本诉获支持金额，仲裁先显示本请求获支持金额，点加号后再出现对应的反诉或反请求金额。
 * @param item 案件
 * @param editable 是否可改
 */
function awardedAmountFieldsMarkup(item: LedgerCase, editable: boolean): string {
  const labels = litigationAwardedAmountLabels(item, activeFieldRuleOverrides?.awardedAmount?.rowLabels);
  if (labels.single) {
    return `<div class="ll-award-layers is-single" data-award-case="${escapeHtml(item.id)}">${awardRowMarkup(labels.primary, 'primary', amountInputText(item.awardedAmount), editable, false, false, '')}</div>`;
  }
  const showCounter = openCounterAwardRows.has(item.id) || (item.counterclaimAwardedAmount || 0) > 0;
  const primary = awardRowMarkup(labels.primary, 'primary', amountInputText(item.awardedAmount), editable, !showCounter, false, labels.addTitle);
  const counter = showCounter ? awardRowMarkup(labels.counter, 'counter', amountInputText(item.counterclaimAwardedAmount), editable, false, true, labels.removeTitle) : '';
  return `<div class="ll-award-layers" data-award-case="${escapeHtml(item.id)}">${primary}${counter}</div>`;
}

function involvedAmountFieldsMarkup(item: LedgerCase, editable: boolean): string {
  const labels = litigationInvolvedAmountLabels(item);
  if (labels.single) {
    return `<div class="ll-amount-layers" data-amount-case="${escapeHtml(item.id)}">${amountRowMarkup(labels.primary, 'principal', amountInputText(item.principalAmount), editable, false, false)}</div>`;
  }
  const showCounter = openCounterAmountRows.has(item.id) || (item.counterclaimAmount || 0) > 0;
  const primary = amountRowMarkup(labels.primary, 'principal', amountInputText(item.principalAmount), editable, !showCounter, false, labels.addTitle);
  const counter = showCounter ? amountRowMarkup(labels.counter, 'counter', amountInputText(item.counterclaimAmount), editable, false, true) : '';
  return `<div class="ll-amount-layers" data-amount-case="${escapeHtml(item.id)}">${primary}${counter}</div>`;
}

function courtNumberFieldsMarkup(item: LedgerCase, editable: boolean): string {
  const context = {disputeType: item.disputeType, instance: item.instance, enforcementStatus: item.enforcementStatus};
  const stored = resolveLitigationCourtNumberRows(item);
  const fallback = litigationCourtNumberLayersForDispute(context)[0]?.id || 'first';
  const rows = stored.length ? stored : [{layer: fallback, value: ''}];
  const singleEnforcement = fallback === 'enforcement' && litigationCourtNumberLayersForDispute(context).length === 1;
  const fields = rows.map((row, index) => courtNumberRowMarkup(row.layer, row.value, editable, editable && !singleEnforcement, editable && index > 0, context)).join('');
  return `<div class="ll-court-layers">${fields}</div>`;
}

/**
 * 从管辖编辑区读出每一行。
 * @param host 管辖容器
 */
function readVenueRows(host: ParentNode): LitigationVenueRow[] {
  const layers = host instanceof Element && host.classList.contains('ll-venue-layers')
    ? host
    : host.querySelector<HTMLElement>('.ll-venue-layers');
  if (!layers) return [];
  return [...layers.querySelectorAll<HTMLElement>('.ll-court-row')].map((row) => {
    const layer = row.querySelector<HTMLElement>('[data-venue-kind]')?.getAttribute('data-venue-kind') || 'first';
    const known = LITIGATION_VENUE_LAYERS.some((item) => item.id === layer) ? layer : 'first';
    const input = row.querySelector<HTMLInputElement>('[data-venue-value]');
    const live = input?.value.trim().slice(0, 80) || '';
    const value = input?.hasAttribute('data-venue-court') ? (input.dataset.venueSaved || '').trim().slice(0, 80) : live;
    return {layer: known as LitigationVenueLayer, value};
  }).slice(0, 12);
}

/**
 * 管辖层次控件。只有一个层次时直接显示名称；诉讼有两级法院时用完整边框的下拉。
 * @param layer 当前层次
 * @param disputeType 案件类型
 * @param editable 是否可改
 */
function venueKindControl(layer: LitigationVenueLayer, disputeType: {disputeType: LedgerCase['disputeType']; instance?: LedgerCase['instance']} | LedgerCase['disputeType'], editable: boolean): string {
  const allowed = litigationVenueLayersForDispute(disputeType);
  const current = allowed.some((item) => item.id === layer) ? layer : (allowed[0]?.id || 'first');
  const label = allowed.find((item) => item.id === current)?.label || '';
  if (!editable || allowed.length < 2) {
    return `<span class="ll-court-kind ll-venue-kind" data-venue-kind="${current}">${escapeHtml(label)}</span>`;
  }
  return `<button type="button" class="ll-court-kind ll-venue-kind ll-layer-choice" data-venue-kind="${current}" data-layer-options="${escapeHtml(JSON.stringify(allowed))}" aria-haspopup="listbox" aria-expanded="false" aria-label="管辖层次">${escapeHtml(label)}</button>`;
}

/**
 * 原告侧、被告侧当前要分成的层次。
 * @param item 案件
 */
function partyLayersForListItem(item: LedgerCase): LitigationPartyLayer[] {
  return litigationPartyLayersForCase({
    disputeType: item.unfilledFields?.includes('disputeType') ? undefined : item.disputeType,
    instance: litigationShownInstance(item),
  });
}

/**
 * 原告或被告格子。左边是身份，右边填写名称。二审、再审按审级自动加行。
 * @param item 案件
 * @param role 原告侧或被告侧
 * @param editable 是否可改
 */
function partySideFieldsMarkup(item: LedgerCase, role: 'plaintiff' | 'opponent', editable: boolean): string {
  const layers = partyLayersForListItem(item);
  const rows = partyLayerRows(item.parties, role, layers);
  if (role === 'plaintiff' && rows[0] && !rows[0].name) {
    const carried = ourPartyName(item).trim();
    if (carried) rows[0] = {...rows[0], name: carried};
  }
  const fields = rows.map((row) => {
    const label = partyLayerLabel(role, row.layer);
    const valueControl = editable
      ? `<input class="ll-court-value" data-party-value value="${escapeHtml(row.name)}" aria-label="${escapeHtml(label)}">`
      : ledgerStaticValue(row.name, label);
    return `<div class="ll-court-row"><span class="ll-party-kind" data-party-layer="${row.layer}">${escapeHtml(label)}</span>${valueControl}</div>`;
  }).join('');
  return `<div class="ll-party-layers" data-party-role="${role}">${fields}</div>`;
}

/**
 * 当前台账的管辖字段是否要求法院名称来自名录。
 */
function venueCourtDirectoryEnabled(): boolean {
  return resolveLitigationFieldRule('venue', activeFieldRuleOverrides).ruleKind === 'court-library';
}

/**
 * 管辖机构输入。法院层次接名录检索，仲裁机构保持手填。
 * @param layer 层次
 * @param value 已保存的名称
 * @param editable 是否可改
 */
function venueValueControl(layer: LitigationVenueLayer, value: string, editable: boolean): string {
  const text = value.trim();
  if (!editable) return ledgerStaticValue(text, '管辖机构');
  if (!venueCourtDirectoryEnabled() || !litigationVenueLayerUsesCourtDirectory(layer)) {
    return `<input class="ll-court-value" data-venue-value value="${escapeHtml(text)}" aria-label="管辖机构">`;
  }
  return `<div class="ll-venue-court enterprise-picker" data-litigation-court-picker>
    <input class="ll-court-value" data-venue-value data-venue-court="1" data-venue-saved="${escapeHtml(text)}" value="${escapeHtml(text)}" placeholder="输入法院名称" autocomplete="off" aria-autocomplete="list" aria-label="管辖法院">
    <div class="enterprise-suggestions ll-venue-court-suggestions" data-litigation-court-suggestions hidden></div>
  </div>`;
}

/**
 * 一行管辖。第一行只有加号，后面的行可以删掉。
 * @param layer 层次
 * @param value 机构名称
 * @param editable 是否可改
 * @param withAdd 是否显示加号
 * @param withRemove 是否显示减号
 */
function venueRowMarkup(layer: LitigationVenueLayer, value: string, editable: boolean, withAdd: boolean, withRemove: boolean, disputeType: {disputeType: LedgerCase['disputeType']; instance?: LedgerCase['instance']} | LedgerCase['disputeType'] = 'litigation'): string {
  const add = withAdd && editable ? `<button type="button" class="ll-court-add" data-venue-add aria-label="新增管辖">${ledgerPlusIcon()}</button>` : '';
  const remove = withRemove && editable ? `<button type="button" class="ll-court-remove" data-venue-remove aria-label="删除管辖">${ledgerMinusIcon()}</button>` : '';
  return `<div class="ll-court-row">${venueKindControl(layer, disputeType, editable)}${venueValueControl(layer, value, editable)}${add}${remove}</div>`;
}

/**
 * 管辖按行填写，行与行之间用表格线隔开。
 * @param item 案件
 * @param editable 是否可改
 */
function venueFieldsMarkup(item: LedgerCase, editable: boolean): string {
  const context = {disputeType: item.disputeType, instance: item.instance, enforcementStatus: item.enforcementStatus};
  const stored = resolveLitigationVenueRows(item);
  const fallback = litigationVenueLayersForDispute(context)[0]?.id || 'first';
  const rows = stored.length ? stored : [{layer: fallback, value: ''}];
  const singleEnforcement = fallback === 'enforcement';
  const fields = rows.map((row, index) => venueRowMarkup(row.layer, row.value, editable, editable && !singleEnforcement, editable && index > 0, context)).join('');
  return `<div class="ll-venue-layers">${fields}</div>`;
}

function overviewForm(item: LedgerCase, editable: boolean, internalNoRule?: Parameters<typeof litigationFieldFillGuide>[1]): string {
  const shownInstance = litigationShownInstance(item);
  const closureOpen = litigationDisplayedStageIsClosed(item, item.currentStage || currentStageName(item));
  const select = (name: string, value: string, labels: Record<string, string>, fieldId: string) =>
    `<select class="form-input" name="${name}"${fieldFillAttrs(fieldId)}>${item.unfilledFields?.includes(fieldId) ? '<option value="" selected>请选择</option>' : ''}${Object.entries(labels).map(([id, label]) => `<option value="${id}" ${!item.unfilledFields?.includes(fieldId) && value === id ? 'selected' : ''}>${label}</option>`).join('')}</select>`;
  const closedAtField = closureOpen
    ? `<label>诉讼结案日<input class="form-input" type="date" name="litigationClosedAt" value="${escapeHtml(item.litigationClosedAt || '')}"${fieldFillAttrs('closedAt')}></label>`
    : '<label>诉讼结案日</label>';
  const closeModeField = closureOpen
    ? `<label>结案方式<select class="form-input" name="closeMode"${fieldFillAttrs('closeMode')}><option value="">未结</option>${closeModeSelectOptions(item).map(({id, label}) => `<option value="${id}" ${item.closeMode === id ? 'selected' : ''}>${label}</option>`).join('')}</select></label>`
    : '<label>结案方式</label>';
  const awardedField = closureOpen ? awardedAmountFieldsMarkup(item, editable) : '';
  const resultField = closureOpen ? resultFieldsMarkup(item, editable) : '';
  return `<form id="llOverviewForm" class="ll-form"><fieldset ${editable ? '' : 'disabled'}>
    <div class="ll-grid">
      <label>内部案号<input class="form-input" name="internalNo" value="${escapeHtml(item.internalNo)}" readonly${fieldFillAttrs('internalNo', internalNoRule)}></label>
      <div class="ll-span-2">${courtNumberFieldsMarkup(item, editable)}</div>
      <label class="ll-span-2">案由${litigationCauseFieldMarkup(item.title, fieldFillAttrs('title'))}</label>
      <label>争议类型${select('disputeType', item.disputeType, DISPUTE_TYPE_LABELS, 'disputeType')}</label>
      <label>审级<input class="form-input" value="${escapeHtml(shownInstance ? INSTANCE_LABELS[shownInstance] : '')}" readonly${fieldFillAttrs('instance')}><input type="hidden" name="instance" value="${escapeHtml(shownInstance || item.instance)}"></label>
      <label>我方地位${select('position', item.position, POSITION_LABELS, 'position')}</label>
      <label>我方主体<input class="form-input" name="ourEntity" value="${escapeHtml(item.ourEntity)}"${fieldFillAttrs('ourParty')}></label>
      <div class="ll-span-2">${venueFieldsMarkup(item, editable)}</div>
      <label>立案日期<input class="form-input" type="date" name="filingDate" value="${escapeHtml(item.filingDate)}"${fieldFillAttrs('filingDate')}></label>
      ${closedAtField}
      ${closeModeField}
      <label>执行状态${select('enforcementStatus', item.enforcementStatus, ENFORCEMENT_STATUS_LABELS, 'enforcementStatus')}</label>
      <label>执行结案日<input class="form-input" type="date" name="enforcementClosedAt" value="${escapeHtml(item.enforcementClosedAt || '')}"></label>
      <label>标的额<input class="form-input" type="number" min="0" step="0.01" name="amount" value="${item.amount || ''}"${fieldFillAttrs('amount')}></label>
      <div class="ll-span-2">${involvedAmountFieldsMarkup(item, editable)}</div>
      <label>币种<input class="form-input" name="currency" value="${escapeHtml(item.unfilledFields?.includes('currency') ? '' : item.currency || 'CNY')}"></label>
      <label class="ll-check"><input type="checkbox" name="preservation" ${item.preservation ? 'checked' : ''}>财产保全</label>
      <label>承办人<input class="form-input" name="handler" value="${escapeHtml(item.handler)}"${fieldFillAttrs('handler')}></label>
      <label>代理律师<input class="form-input" name="counsel" value="${escapeHtml(item.counsel)}"${fieldFillAttrs('counsel')}></label>
      <label>代理机构<input class="form-input" name="counselOrg" value="${escapeHtml(item.counselOrg)}"${fieldFillAttrs('counselOrg')}></label>
      <label>生效日期<input class="form-input" type="date" name="effectiveDate" value="${escapeHtml(item.effectiveDate)}"></label>
      <div class="ll-span-2">${claimFieldsMarkup(item, editable)}</div>
      <div class="ll-span-2">${resultField}</div>
      <div class="ll-span-2">${awardedField}</div>
    </div>
    <h3>当事人</h3>
    <div id="llParties">${item.parties.map(partyRow).join('') || partyRow({id: 'party_new', role: 'opponent', name: '', contact: ''})}</div>
    ${editable ? '<button type="button" class="la-text-button" data-action="add-party">添加当事人</button>' : ''}
  </fieldset></form>`;
}

/**
 * 当地日历上的今天，格式年-月-日。
 * @param date 取值时刻
 */
function localProgressDate(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * 按已输入文字把进展内容框撑开或收回，避免换行后被裁切。
 * @param area 内容输入框
 */
/**
 * 按内容把进展文本框撑开。空内容与日期框同高。
 * @param area 记录内容
 */
function fitProgressContentHeight(area: HTMLTextAreaElement): void {
  const base = 34;
  area.style.height = `${base}px`;
  const border = area.offsetHeight - area.clientHeight;
  area.style.height = `${Math.max(base, area.scrollHeight + border)}px`;
}

/**
 * 案件进展格子：中间是最新一条，右下角角标打开这张进展记录表。
 * @param item 案件
 */
/**
 * 案件名称格。右下角角标打开案件背景。
 * @param item 案件
 * @param value 当前名称
 * @param editable 是否可改名称
 */
function caseNameCellMarkup(item: LedgerCase, value: string, editable: boolean): string {
  const name = value ? escapeHtml(value) : '';
  const body = editable
    ? `<span class="ll-cell-inplace ll-cell-wrap" data-inplace-case="${escapeHtml(item.id)}" data-inplace-field="caseName" tabindex="0">${name}</span>`
    : `<span class="ll-case-name-text">${name}</span>`;
  return `<div class="ll-case-name-cell">${body}<button type="button" class="ll-progress-corner" data-background="${escapeHtml(item.id)}" title="案件背景" aria-label="打开案件背景"></button></div>`;
}

/**
 * 案件背景窗口。标题下是案件名称和内部编号，下面是介绍正文。
 * @param item 案件
 * @param editable 是否可改
 */
function backgroundDialogMarkup(item: LedgerCase, editable: boolean): string {
  const caseName = (item.caseName || '').trim();
  const internalNo = (item.internalNo || '').trim();
  const meta = [caseName, internalNo].filter(Boolean).map((part) => `<span>${escapeHtml(part)}</span>`).join('');
  return `<dialog class="la-dialog ll-progress-dialog ll-background-dialog" id="llBackgroundDialog">
    <header><div><h3>案件背景</h3>${meta ? `<p>${meta}</p>` : ''}</div><button type="button" class="ll-progress-close" data-close aria-label="关闭">×</button></header>
    <div class="ll-background-body"><textarea class="ll-background-text" maxlength="4000" placeholder="填写该案件的内容介绍" aria-label="案件内容介绍"${editable ? '' : ' readonly'}>${escapeHtml(item.caseBackground || '')}</textarea></div>
  </dialog>`;
}

/**
 * 最新进展格子。完整展示最新一条，其余用只读入口查看。
 * @param item 案件
 */
function progressCellMarkup(item: LedgerCase): string {
  const entries = sortedLitigationProgress(item.progressEntries).filter((entry) => entry.content.trim());
  const latest = entries[entries.length - 1];
  const preview = !latest
    ? `<span class="ll-progress-link is-empty">登记进展</span>`
    : (() => {
      const date = formatProgressLongDate(latest.date || latest.createdAt.slice(0, 10));
      const content = latest.content.trim();
      const more = entries.length > 1
        ? `<button type="button" class="ll-progress-more" data-progress-view="${escapeHtml(item.id)}">查看更多案件进展</button>`
        : '';
      return `<div class="ll-progress-latest"><p>${date ? `<time>${escapeHtml(date)}</time>` : ''}<span>${escapeHtml(content)}</span></p>${more}</div>`;
    })();
  return `<div class="ll-progress-cell">${preview}<button type="button" class="ll-progress-corner" data-progress="${escapeHtml(item.id)}" title="进展记录" aria-label="打开进展记录"></button></div>`;
}

/**
 * 台账加号。图形取自资料库中的加号图标。
 * @param className 图标 class
 */
function ledgerPlusIcon(className = 'll-glyph'): string {
  return `<svg class="${className}" viewBox="0 0 1024 1024" aria-hidden="true"><path fill="#8a2524" d="M511.3 64.2c-247.1 0-447.4 200.3-447.4 447.4S264.2 959 511.3 959s447.4-200.3 447.4-447.4S758.4 64.2 511.3 64.2z m244.1 488.1H552v203.4c0 22.5-18.2 40.7-40.7 40.7-22.5 0-40.7-18.2-40.7-40.7V552.3H267.3c-22.5 0-40.7-18.2-40.7-40.7 0-22.5 18.2-40.7 40.7-40.7h203.4V267.6c0-22.5 18.2-40.7 40.7-40.7 22.5 0 40.7 18.2 40.7 40.7V471h203.4c22.5 0 40.7 18.2 40.7 40.7-0.1 22.4-18.4 40.6-40.8 40.6z"/></svg>`;
}

/**
 * 台账减号。图形取自资料库中的减号图标。
 * @param className 图标 class
 */
function ledgerMinusIcon(className = 'll-glyph'): string {
  return `<svg class="${className}" viewBox="0 0 1024 1024" aria-hidden="true"><path fill="#8a2524" d="M507.904 52.224q95.232 0 179.2 36.352t145.92 98.304 98.304 145.408 36.352 178.688-36.352 179.2-98.304 145.92-145.92 98.304-179.2 36.352-178.688-36.352-145.408-98.304-98.304-145.92-36.352-179.2 36.352-178.688 98.304-145.408 145.408-98.304 178.688-36.352zM736.256 573.44q30.72 0 55.296-15.872t24.576-47.616q0-30.72-24.576-45.568t-55.296-14.848l-452.608 0q-30.72 0-56.32 14.848t-25.6 45.568q0 31.744 25.6 47.616t56.32 15.872l452.608 0z"/></svg>`;
}

/**
 * 进展表表头右侧的加号。
 */
function progressPlusIcon(): string {
  return '<span class="ll-progress-glyph ll-progress-plus" aria-hidden="true"></span>';
}

/**
 * 进展表里用于删除一条记录的垃圾桶图标。
 */
function progressMinusIcon(): string {
  return '<span class="ll-progress-glyph ll-progress-trash" aria-hidden="true"></span>';
}

/**
 * 进展记录里的日期按钮。点开后使用与结案日相同的年月日弹层。
 * @param value 年-月-日，空则显示请选择
 */
function progressDateControl(value: string): string {
  const shown = value || '请选择';
  return `<button type="button" class="ll-progress-date${value ? '' : ' is-empty'}" data-progress-edit="date" data-date-value="${escapeHtml(value)}" aria-haspopup="dialog" aria-expanded="false" aria-label="记录日期">${escapeHtml(shown)}</button>`;
}

/**
 * 这个案子的进展记录工作表。标题下只写案件名称。序号按当前排序自动编排。
 * @param item 案件
 * @param editable 是否可改
 */
function progressSheetDialogMarkup(item: LedgerCase, editable: boolean): string {
  const entries = sortedLitigationProgress(item.progressEntries);
  const openedOn = localProgressDate();
  const caseName = (item.caseName || '').trim();
  const blankId = [...entries].reverse().find((entry) => !entry.content.trim())?.id;
  const rows = entries.map((entry, index) => `<tr data-progress-id="${escapeHtml(entry.id)}">
    <td class="ll-progress-seq">${index + 1}</td>
    <td>${editable ? progressDateControl(entry.date || openedOn) : escapeHtml(formatProgressLongDate(entry.date || ''))}</td>
    <td class="ll-progress-text">${editable ? `<textarea data-progress-edit="content" rows="1" aria-label="记录的内容" placeholder="填写进展内容"${entry.id === blankId ? ' data-progress-focus' : ''}>${escapeHtml(entry.content)}</textarea>` : `<p class="ll-progress-readonly">${escapeHtml(entry.content)}</p>`}</td>
    ${editable ? `<td class="ll-progress-op"><button type="button" class="ll-progress-icon-btn" data-dialog-remove-progress title="删除" aria-label="删除">${progressMinusIcon()}</button></td>` : ''}
  </tr>`).join('');
  const columnCount = editable ? 4 : 3;
  const addAction = editable
    ? `<button type="button" class="ll-progress-add-record" data-progress-add>${progressPlusIcon()}<span>添加记录</span></button>`
    : '';
  const dialogClass = editable ? 'la-dialog ll-progress-dialog ll-progress-table-dialog' : 'la-dialog ll-progress-dialog ll-progress-view-dialog';
  return `<div class="ll-progress-backdrop"></div><dialog class="${dialogClass}" id="llProgressDialog" aria-label="进展记录">
    <header><div class="ll-progress-heading"><h3>进展记录</h3>${caseName ? `<p>${escapeHtml(caseName)}</p>` : ''}</div><div class="ll-progress-header-actions">${addAction}<button type="button" class="ll-progress-close" data-close aria-label="关闭">×</button></div></header>
    <div class="ll-progress-sheet-wrap"><table class="ll-progress-sheet">
      <colgroup><col class="ll-progress-col-seq"><col class="ll-progress-col-date"><col>${editable ? '<col class="ll-progress-col-op">' : ''}</colgroup>
      <thead><tr><th scope="col">序号</th><th scope="col">记录日期</th><th scope="col">记录内容</th>${editable ? '<th scope="col"><span class="ll-progress-sr-only">操作</span></th>' : ''}</tr></thead>
      <tbody>${rows || `<tr><td colspan="${columnCount}" class="ll-progress-empty">暂无进展记录</td></tr>`}</tbody>
    </table>
    </div>
  </dialog>`;
}

function progressEntriesHtml(item: LedgerCase, editable: boolean, removeAction = 'data-dialog-remove-progress'): string {
  const entries = sortedLitigationProgress(item.progressEntries);
  if (!entries.length) return '<div class="la-empty compact">暂无进展。一条案件只出现一次，进展按时间追加。</div>';
  return `<ol class="ll-progress-list">${entries.map((entry) => `<li data-progress-id="${escapeHtml(entry.id)}">
    <time>${escapeHtml(entry.date || entry.createdAt.slice(0, 10))}</time>
    <p>${escapeHtml(entry.content)}</p>
    ${editable ? `<button type="button" class="la-text-button" ${removeAction}>删除</button>` : ''}
  </li>`).join('')}</ol>`;
}

function progressView(item: LedgerCase, editable: boolean): string {
  return `${progressEntriesHtml(item, editable, 'data-action="remove-progress"')}
    ${editable ? `<form class="ll-progress-add">
      <input type="date" data-progress-date value="${localProgressDate()}">
      <textarea data-progress-content rows="3" placeholder="记录开庭、保全、审批或对方动作"></textarea>
      <button type="button" class="la-primary" data-action="add-progress">添加进展</button>
    </form>` : ''}`;
}

function procedureView(item: LedgerCase, editable: boolean): string {
  return `<div class="ll-timeline">${item.nodes.map((node) => `<div class="ll-node" data-node="${escapeHtml(node.id)}">
    <input data-node-name value="${escapeHtml(node.name)}" ${editable ? '' : 'disabled'}>
    <label>计划日<input type="date" data-node-plan value="${escapeHtml(node.plannedDate)}" ${editable ? '' : 'disabled'}></label>
    <label>完成日<input type="date" data-node-done value="${escapeHtml(node.completedDate)}" ${editable ? '' : 'disabled'}></label>
    <label class="ll-check"><input type="checkbox" data-node-key ${node.keyDeadline ? 'checked' : ''} ${editable ? '' : 'disabled'}>关键期限</label>
    <input data-node-note value="${escapeHtml(node.note)}" placeholder="结果备注" ${editable ? '' : 'disabled'}>
    ${editable ? '<button type="button" class="la-text-button" data-action="remove-node">删除</button>' : ''}
  </div>`).join('')}</div>
  ${editable ? '<button type="button" class="la-secondary" data-action="add-node">添加节点</button>' : ''}`;
}

function deadlineView(item: LedgerCase, _tone: string): string {
  const rows = item.nodes.filter((node) => node.keyDeadline).map((node) => {
    const tone = node.completedDate ? 'done' : deadlineTone(node.plannedDate, item.closed);
    const label = tone === 'overdue' ? '逾期' : tone === 'upcoming' ? '临近' : tone === 'done' ? '已完成' : '未到';
    return `<tr><td>${escapeHtml(node.name)}</td><td>${formatDate(node.plannedDate)}</td><td><span class="ll-deadline ll-deadline-${tone}">${label}</span></td><td>${escapeHtml(node.note || '—')}</td></tr>`;
  }).join('');
  return `<table class="la-file-table"><thead><tr><th>期限</th><th>日期</th><th>状态</th><th>备注</th></tr></thead><tbody>${rows || '<tr><td colspan="4"><div class="la-empty compact">暂无关键期限，请在程序节点中勾选。</div></td></tr>'}</tbody></table>`;
}

function materialsView(item: LedgerCase, editable: boolean): string {
  return `${editable ? `<div class="ll-upload-bar"><select id="llMaterialCategory">${Object.entries(MATERIAL_CATEGORY_LABELS).map(([id, label]) => `<option value="${id}">${label}</option>`).join('')}</select><label class="la-primary">上传材料<input id="llMaterialFile" type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" hidden></label></div>` : ''}
    <p class="ll-hint">支持 PDF、Word、TXT、图片。可不关联合同，直接把争议材料放在这里。</p>
    <table class="la-file-table"><thead><tr><th>文件</th><th>分类</th><th>操作</th></tr></thead>
    <tbody>${item.materials.map((file) => `<tr data-file="${escapeHtml(file.fileId)}" data-name="${escapeHtml(file.originalName)}"><td><div class="la-filename"><img class="la-file-icon" src="${fileIconUrl(file.originalName)}" alt=""><span>${escapeHtml(file.originalName)}</span></div></td><td>${escapeHtml(MATERIAL_CATEGORY_LABELS[file.category])}</td><td><div class="la-file-actions"><button type="button" data-action="preview-file">${icon('eye')}预览</button><span class="la-action-divider"></span><button type="button" data-action="download-file">${icon('download')}下载</button>${editable ? '<span class="la-action-divider"></span><button type="button" data-action="remove-file">删除</button>' : ''}</div></td></tr>`).join('') || '<tr><td colspan="3"><div class="la-empty compact">暂无材料</div></td></tr>'}</tbody></table>`;
}

function contractsView(item: LedgerCase, contracts: LinkableContract[], editable: boolean): string {
  const unused = contracts.filter((entry) => !item.linkedContracts.some((link) => link.projectId === entry.projectId));
  return `<p class="ll-hint">可不关联。争议合同也可以直接上传到材料。</p>
    ${editable ? `<div class="ll-upload-bar"><select id="llContractSelect">${unused.map((entry) => `<option value="${escapeHtml(entry.projectId)}">${escapeHtml(entry.name)}${entry.archived ? '（已归档）' : ''}</option>`).join('') || '<option value="">暂无可选合同项目</option>'}</select><button type="button" class="la-secondary" data-action="link-contract" ${unused.length ? '' : 'disabled'}>关联</button></div>` : ''}
    <table class="la-file-table"><thead><tr><th>项目</th><th>状态</th><th>操作</th></tr></thead>
    <tbody>${item.linkedContracts.map((link) => `<tr data-link="${escapeHtml(link.projectId)}" data-archived="${link.archived ? '1' : '0'}"><td>${escapeHtml(link.name)}</td><td>${link.archived ? '已归档' : '工作台'}</td><td><div class="la-file-actions"><button type="button" data-action="open-contract">打开</button>${editable ? '<span class="la-action-divider"></span><button type="button" data-action="unlink-contract">取消关联</button>' : ''}</div></td></tr>`).join('') || '<tr><td colspan="3"><div class="la-empty compact">未关联合同审核项目</div></td></tr>'}</tbody></table>`;
}
